import { useState, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import "../css/ClaimStatusV2.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass, faPrint, faShieldHalved, faAnglesDown, faAnglesUp,
  faXmark, faPaperPlane, faPaperclip, faLock, faBars, faHouse, faList,
  faGear, faBell, faBolt, faSackDollar, faListCheck, faCircleUser,
  faEye, faPenToSquare, faEllipsisVertical, faCheck, faFilter, faNotesMedical,
  faRotate, faFileInvoiceDollar, faBoxArchive, faTriangleExclamation,
  faSpinner, faIdBadge, faClockRotateLeft,
  faTrash, faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { faFileExcel, faFilePdf, faFile, faCreditCard } from "@fortawesome/free-regular-svg-icons";

import { APIConstants, ClaimStatusConstants, CommonAPIConstants, IntelliBillConstants, ClaimApprovalConstants, PostingpaymentsConstants, FilesAPIConstants, formatDOSDate } from "../utilities/Constants";
import { exportExcel } from "../utilities/exportToExcel";
import PrintColumnModal from "../components/PrintColumnModal";
const BASE_URL = APIConstants.BASE_URL ?? "";
const BASE_URL_BILLING = APIConstants.BASE_URL_BILLING ?? BASE_URL;
const BASE_URL_SEARCH = APIConstants.BASE_URL_SEARCH ?? BASE_URL;
const COMMON_BASE_URL = APIConstants.COMMON_BASE_URL ?? BASE_URL;
const PATIENT_PORTAL_URL = APIConstants.PATIENT_PORTAL_URL ?? BASE_URL;
const SERVICES_URL = APIConstants.SERVICES_URL ?? BASE_URL;

/* Paths verified against ../utilities/Constants.js — do not invent paths here. */
const API = {
  GetClaimDropDowns: ClaimApprovalConstants.GetClaimDropDowns,
  GetClaimStatusData: ClaimStatusConstants.GetClaimStatusData,
  GetPracticeBillingNPIs: ClaimStatusConstants.GetPracticeBillingNPIs,
  GetClaimServices: ClaimStatusConstants.GetClaimServices,
  GetAllPatientNames: CommonAPIConstants.GetAllPatientNames,
  GetSuggestionDropDowns: ClaimApprovalConstants.GetSuggestionDropDowns,
  GetIntelliProvidersNames: IntelliBillConstants.GetIntelliProvidersNames,
  GetInsuCompanyNames: CommonAPIConstants.GetInsuCompanyNames,
  GetClearingHouse: CommonAPIConstants.GetClearingHouse,
  GetAllCPTList: CommonAPIConstants.GetAllCPTList,
  GetTransactionCode: ClaimStatusConstants.GetTransactionCode,
  SaveVerifiedData: ClaimStatusConstants.SaveVerifiedData,
  UpdateCopayData: ClaimStatusConstants.UpdateCopayData,
  CloseClaimWithCPT: ClaimStatusConstants.CloseClaimWithCPT,
  SaveClaimCPTAdminWriteOffCharges: ClaimStatusConstants.SaveClaimCPTAdminWriteOffCharges,
  SaveAdminWriteOff: ClaimStatusConstants.SaveClaimCPTAdminWriteOffCharges,
  SaveClaimNotes: PostingpaymentsConstants.UpdateClaimNotes,
  CloseClaimsData: ClaimStatusConstants.CloseClaimWithCPT,
  ResubmitClaims: IntelliBillConstants.GetIntelliUpdatedClaims,
  SavePaymentData: PostingpaymentsConstants.CPTReceviedCharge,
  CheckEligibility: CommonAPIConstants.CheckPatientEligibility,
  SaveSecPendingCharges: CommonAPIConstants.SaveSecPendingCharges,
  UpdateCheckNumber: PostingpaymentsConstants.UpdateCheckNumber,
  UpdateCheckDate: PostingpaymentsConstants.UpdateCheckDate,
  SaveAdminWriteOff: ClaimStatusConstants.SaveClaimCPTAdminWriteOffCharges,
  SaveClaimNotes: PostingpaymentsConstants.UpdateClaimNotes,
  CloseClaimsData: ClaimStatusConstants.CloseClaimWithCPT,
  // ResubmitClaims removed — V1 has no resubmit API. Resubmission is
  // selection-only on the Claim Status page (V1 ClaimStatus.js 217);
  // the actual submit happens in IntelliBill / Workable Claims.
  SavePaymentData: PostingpaymentsConstants.CPTReceviedCharge,
  CheckEligibility: CommonAPIConstants.CheckPatientEligibility,
  SaveSecPendingCharges: CommonAPIConstants.SaveSecPendingCharges,
  UpdateCheckNumber: PostingpaymentsConstants.UpdateCheckNumber,
  UpdateCheckDate: PostingpaymentsConstants.UpdateCheckDate,
  UpdateClaimReferenceNumber: PostingpaymentsConstants.UpdateClaimReferencenumber,
  UpdateClaimNotes: PostingpaymentsConstants.UpdateClaimNotes,
  GetPatientEligibility: CommonAPIConstants.GetPatientEligibility,
  CheckPatientEligibility: CommonAPIConstants.CheckPatientEligibility,
  ExportPdfFile: FilesAPIConstants.ExportPdfFile,
  GetClaimAllEvents: CommonAPIConstants.GetClaimAllEvents,
  GetPDFCMS1500: CommonAPIConstants.GetPDFCMS1500,
  SaveRegeneratePDFCMS1500: CommonAPIConstants.SaveRegeneratePDFCMS1500,
  CPTDocument: CommonAPIConstants.CPTDcoument,
  GetClaimSubmitHx: CommonAPIConstants.GetClaimSubmitHx,
  ApplyCopay: CommonAPIConstants.ApplyCopay,
  SaveAdjustmentCharges: CommonAPIConstants.SaveAdjustmentCharges,
  CPTReceviedCharge: PostingpaymentsConstants.CPTReceviedCharge,
  UpdateClaimDeductible: PostingpaymentsConstants.UpdateClaimDeductible,
  UpdateClaimPatientResponsibility: PostingpaymentsConstants.UpdateClaimPatientResponsibility,
  UpdateWriteoff: PostingpaymentsConstants.UpdateWriteoff,
  SaveOtherAdjustment: PostingpaymentsConstants.savOtherAdjustment,
  SaveFederalReduction: PostingpaymentsConstants.SaveFedReduction,
  SaveSelfPay: PostingpaymentsConstants.SaveSelfPay,
  GetClaimAttachments: CommonAPIConstants.GetClaimAttachments,
};

/* ─── HELPERS ─── */
const removeHtmlTags = (s) => s?.replace(/<[^>]*>/g, "").trim() ?? "";
const toNum = (v) => parseFloat(String(v ?? "0")) || 0;
const parseBoolish = (v) =>
  v === true || v === 1 || v === "1" || v === "true" || v === "True";

const parseApiDate = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value)) {
    const [month, day, year] = value.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  // WCF/.NET JSON sends dates as "/Date(1234567890000)/" or
  // "/Date(1234567890000-0500)/". new Date() can't parse this so we
  // extract the ms epoch ourselves. Matches V1 convertTimestampToDate
  // (ClaimStatus.js line 2154) which the old UI relied on.
  if (typeof value === "string" && /^\/Date\(/.test(value)) {
    const m = value.match(/\/Date\((-?\d+)/);
    if (m) {
      const d = new Date(parseInt(m[1], 10));
      if (!Number.isNaN(d.getTime())) return format(d, "yyyy-MM-dd");
    }
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return format(parsed, "yyyy-MM-dd");
};

const formatDateForApi = (value) => {
  const normalized = parseApiDate(value);
  if (!normalized) return "";
  const [year, month, day] = normalized.split("-");
  return `${month}/${day}/${year}`;
};

const formatDateForInput = (value) => parseApiDate(value);

function getClaimStatus(item) {
  const claimStatusInt = Number(item.ClaimStatusInt ?? 0);
  const isDenied = parseBoolish(item.isDenied);
  const isPaymentReceived = parseBoolish(item.isPaymentReceived);
  const isPaymentPosted = parseBoolish(item.isPaymentPosted);
  const recvdCharge = toNum(item.recvdCharge);
  const secRecvdCharge = toNum(item.SecrecvdCharge);
}

function mapApiClaim(item, index) {
  const patient = [item.LastName, item.FirstName].filter(Boolean).join(", ");
  const cpt = item.CPTOld ?? item.EncCPT ?? item.CPTCode1 ?? item.ClaimCPT ?? "";
  const statusMeta = getClaimStatus(item);
  const rowKey = [
    item.ClaimId ?? "",
    cpt,
    item.ClaimDOS ?? item.AppointmentDate ?? "",
    index,
  ].join("::");

  return {
    rowKey,
    index: index + 1,
    raw: item,
    id: item.ClaimId ?? "",
    patient,
    patientId: String(item.PatientID ?? ""),
    dob: item.DOB ?? "",
    mrn: item.MRN ?? "",
    conType: item.ClaimConType ?? "BH",
    dos: formatDOSDate(item.ClaimDOS) ?? "",
    provider: item.providername ?? item.RenderingProvider ?? "",
    payer: item.InsCompNickName ?? item.Payer ?? "",
    payerId: item.PayerID ?? item.InsPayerID ?? "",
    policyNumber: item.PolicyNumber ?? "",
    secPayer: item.SecInsCompNickName ?? item.SecPayer ?? "",
    secPayerId: item.SecPayerID ?? "",
    secPolicyNumber: item.SecInsPolicyNumber ?? "",
    cpt,
    dx: "",
    dxDesc: "",
    location: item.LocationNickname ?? "",
    locationColor: item.LocationColor ?? "",
    billed: toNum(item.totalcharge),
    allowed: 0,
    paid: toNum(item.recvdCharge),
    secPaid: toNum(item.SecrecvdCharge),
    copay: toNum(item.Copay),
    copayAmount: toNum(item.CopayAmount),
    deductible: toNum(item.Deductible),
    patRes: toNum(item.PatientAmount),
    contractual: toNum(item.Adjustment),
    otherAdj: toNum(item.OtherAdjustment),
    writeOff: toNum(item.WriteOffAmount),
    adminWriteOff: toNum(item.AdminWriteOff),
    units: toNum(item.Units),
    clearingHouse: item.ClearingHouseName ?? "",
    secSubmitDate: item.SecClaimSubmiDate ?? "",
    federalPayment: toNum(item.FederalPayment),
    selfPay: toNum(item.SELFPAY),
    secPendingAmt: toNum(item.SecPendingAmt),
    balance: toNum(item.claimbln),
    checkNumPri: item.PaymentChqueNo ?? "",
    checkDatePri: item.PaymentChequeDate ?? "",
    payerRefPri: item.PayerRefNumber ?? "",
    checkNumSec: item.SecCPTChequeNumber ?? "",
    checkDateSec: item.SecCPTChequeDate ?? "",
    payerRefSec: item.SecPayerclaimRefNumber ?? "",
    status: item.SecClaimStatus && item.SecClaimStatus !== ""
      ? item.SecClaimStatus
      : item.PrimaryClaimStatus || "",
    statusFlag: item.SecClaimStatus ?? item.PrimaryClaimStatus,
    submittedOn: item.SubmitDate ?? "",
    updatedOn: item.UpdatedDate ?? "",
    submittedBy: item.SubmitterName ?? "",
    updatedBy: item.UpdaterName ?? "",
    verifiedBy: typeof item.verifiedBy === "string" ? item.verifiedBy : "",
    verifiedOn: item.verifiedOn ?? "",
    verificationComment: item.VerificationComment ?? "",
    isVerified: parseBoolish(item.isVerified),
    denialCode: item.DenialCode ?? item.ClaimAdjustmentCode ?? "",
    remarks: item.Remarks ?? "",
    notes: item.ClaimNotes ?? "",
    // Clinical extras for Patient 360 — surface whatever the API returns
    // even if these fields are sparsely populated.
    diagnosisCodes: item.DiagnosisCodes ?? item.ICDCodes ?? item.DxCodes ?? item.ICD ?? "",
    diagnosisDesc: item.DiagnosisDescription ?? item.DxDescription ?? "",
    modifier: item.Modifier ?? item.CPTModifier ?? "",
    medications: item.Medications ?? item.MedicationList ?? item.RxList ?? "",
    appointmentNotes: item.AppointmentNotes ?? item.SessionNotes ?? "",
    attachments: parseInt(String(item.Attachments ?? "0"), 10) || 0,
    eob: item.EOBRef,
    denialReason: item.ErrorDetails,
    isPaymentReceived: parseBoolish(item.isPaymentReceived),
    isPaymentPosted: parseBoolish(item.isPaymentPosted),
    isDenied: parseBoolish(item.isDenied),
    isClosed: Number(item.iClosed ?? 0) === 1,
    isPrimarySkipped: !!item.IsPrimarySkipped,
    priorSecClaim: Number(item.PriorSecClaim ?? 0),
    claimHx: item.ClaimHx ?? "",
    copayAppliedClaim: String(item.CopayAppliedClaim ?? ""),
    copayAppliedByShortName: item.CopayApliedByShortName ?? "",
    copayAppliedOn: item.CopayApliedOn ?? "",
  };
}

/* ─── LOCATION COLOUR PALETTE + DEMO DATA (module-level) ─── */
const LOC_COLORS = [
  { bg: "#1565c0", text: "#fff" },
  { bg: "#00838f", text: "#fff" },
  { bg: "#00695c", text: "#fff" },
  { bg: "#0288d1", text: "#fff" },
  { bg: "#ad1457", text: "#fff" },
  { bg: "#2e7d32", text: "#fff" },
  { bg: "#c62828", text: "#fff" },
  { bg: "#f57f17", text: "#fff" },
  { bg: "#6a1b9a", text: "#fff" },
  { bg: "#558b2f", text: "#fff" },
];

const DEMO_LOCATIONS = [
  { LocationId: "1", LocationNickname: "Bellefontaine Office" },
  { LocationId: "2", LocationNickname: "2980" },
  { LocationId: "3", LocationNickname: "Whitehall Medical Center" },
  { LocationId: "4", LocationNickname: "Eb" },
  { LocationId: "5", LocationNickname: "Akron" },
  { LocationId: "6", LocationNickname: "Lon" },
  { LocationId: "7", LocationNickname: "Wb" },
  { LocationId: "8", LocationNickname: "NCHC" },
  { LocationId: "9", LocationNickname: "Sawbury" },
  { LocationId: "10", LocationNickname: "Buckeye Clinic" },
];


const SEARCH_FILTER_OPTIONS = [
  { value: "0", label: "" },
  { value: "1", label: "Patient Name" },
  { value: "2", label: "Claim ID" },
  { value: "3", label: "Insurance" },
  { value: "4", label: "Verified By" },
  { value: "5", label: "Rendering Provider" },
  { value: "6", label: "Verified By all" },
  { value: "7", label: "Billing NPI" },
  { value: "8", label: "Submitted By" },
  { value: "9", label: "Updated By" },
  { value: "10", label: "Clearing House" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All Claims" },
  { value: "sub", label: "Submitted" },
  { value: "nonsub", label: "Non-Submitted" },
  { value: "5", label: "Forwarded" },
  { value: "1", label: "Paid" },
  { value: "2", label: "Rejected" },
  { value: "deni", label: "Denied" },
  { value: "sec", label: "Sec Claims" },
  { value: "priskip", label: "Primary Skipped" },
];

const getSearchFilterType = (value) => {
  switch (value) {
    case "1":
      return "pat";
    case "2":
      return "claimId";
    case "3":
      return "ins";
    case "4":
      return "verifiedby";
    case "5":
      return "renderingprovider";
    case "6":
      return "verifiedbyall";
    case "7":
      return "billingnpi";
    case "8":
      return "submittedby";
    case "9":
      return "updatedby";
    case "10":
      return "ch";
    default:
      return " ";
  }
};


const STATUS_CFG = {
  Submitted: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd", dot: "#3b82f6" },
  Sent: { bg: "#e0f2fe", color: "#0369a1", border: "#7dd3fc", dot: "#0ea5e9" },
  Forwarded: { bg: "#ede9fe", color: "#6d28d9", border: "#c4b5fd", dot: "#8b5cf6" },
  Received: { bg: "#cffafe", color: "#0e7490", border: "#67e8f9", dot: "red" },
  Posted: { bg: "#e6f6f4", color: "#0e6b60", border: "#c8e5e0", dot: "red" },
  Paid: { bg: "#dcfce7", color: "#15803d", border: "#86efac", dot: "#22c55e" },
  Denied: { bg: "#fee2e2", color: "#dc2626", border: "#fca5a5", dot: "#ef4444" },
  Rejected: { bg: "#ffedd5", color: "#c2410c", border: "#fdba74", dot: "#f97316" },
  Pending: { bg: "#fef9c3", color: "#a16207", border: "#fde047", dot: "#eab308" },
};

// Column order matches the old UI:
// # | Location | DOS | Claim # | Patient Name | BH/SUD | DOB | ...
const VIEW_COLUMNS = {
  claimInfo: ["#", "location", "dos", "claimId", "patient", "bhsud", "dob", "provider", "cpt", "units", "status", "totalCharge", "balance", "actions"],
  insurance: ["#", "location", "dos", "claimId", "patient", "insPri", "insSec", "clearingHouse", "submitDatePri", "secSubmitDate", "status", "actions"],
  payments: ["#", "location", "dos", "claimId", "patient", "totalCharge", "recvdPri", "recvdSec", "contractual", "secPendingAmt", "deductible", "patRes", "copay", "otherAdj", "federalPayment", "selfPay", "writeOff", "adminWriteOff", "balance", "chequePri", "chequeDatePri", "payerRefPri", "chequeSec", "chequeDateSec", "payerRefSec"],
  notesWorkflow: ["#", "location", "dos", "claimId", "patient", "provider", "updatedBy", "submittedBy", "denialCode", "remarks", "errorDetails", "attachments", "verifiedBy", "resubmit", "notes", "actions"],
};

const VIEW_TITLES = {
  claimInfo: "Claim Info",
  insurance: "Insurance",
  payments: "Payments",
  notesWorkflow: "Notes & Workflow",
};

const COL_LABELS = {
  "#": "", actions: "", patient: "Patient Name", claimId: "Claim #", dos: "DOS", bhsud: "BH/SUD",
  dob: "DOB",
  location: "Location", insurance: "Insurance", cpt: "CPT", status: "Status", charge: "Charge",
  balance: "Balance", updated: "Updated",
  units: "Units", totalCharge: "Total Charge", recvdPri: "Recvd (Pri)", recvdSec: "Recvd (Sec)",
  contractual: "Contract. Adj", deductible: "Deductible", patRes: "Pat. Resp", copay: "Copay",
  otherAdj: "Other Adj", secPendingAmt: "Sec Pending", federalPayment: "Fed. Reduction", selfPay:"Self Pay",
  insPri: "Insurance(Pri)", submitDatePri: "Submit Date(Pri)", chequePri: "Cheque#(Pri)", chequeDatePri: "Cheque Date",
  payerRefPri: "Payer Ref#", insSec: "Insurance(Sec)", chequeSec: "Cheque#(Sec)",
  chequeDateSec: "Cheque Date(Sec)", payerRefSec: "Payer Ref#(Sec)",
  writeOff: "Write-Off", adminWriteOff: "Admin W/O",
  clearingHouse: "Clearing House", provider: "Provider", submittedBy: "Submitted By",
  secSubmitDate: "Submit Date(Sec)", updatedBy: "Updated By",
  verifiedBy: "Verified By", denialCode: "Adj./Denial Code", remarks: "Remarks",
  errorDetails: "Error Details", attachments: "Attach.", notes: "Notes", resubmit: "Resubmit",
};

const NUM_COLS = new Set(["charge", "balance", "units", "totalCharge", "recvdPri", "recvdSec", "contractual", "deductible", "patRes", "copay", "otherAdj", "secPendingAmt", "federalPayment","selfPay", "writeOff", "adminWriteOff"]);

const SORT_KEY_MAP = {
  patient: "patient",
  claimId: "id",

  dob: "dob",
  dos: "dos",
  bhsud: "conType",
  location: "location",
  insurance: "payer",
  insPri: "payer",
  submitDatePri: "submittedOn",
  insSec: "secPayer",
  secSubmitDate: "secSubmitDate",
  provider: "provider",
  cpt: "cpt",
  units: "units",
  status: "status",
  charge: "billed",
  totalCharge: "billed",
  balance: "balance",
  updated: "updatedOn",
  recvdPri: "paid",
  recvdSec: "secPaid",
  contractual: "contractual",
  deductible: "deductible",
  patRes: "patRes",
  copay: "copay",
  otherAdj: "otherAdj",
  secPendingAmt: "secPendingAmt",
  federalPayment: "federalPayment",
  selfPay :"selfPay",
  chequePri: "checkNumPri",
  chequeDatePri: "checkDatePri",
  payerRefPri: "payerRefPri",
  chequeSec: "checkNumSec",
  chequeDateSec: "checkDateSec",
  payerRefSec: "payerRefSec",
  writeOff: "writeOff",
  adminWriteOff: "adminWriteOff",
  clearingHouse: "clearingHouse",
  submittedBy: "submittedBy",
  updatedBy: "updatedBy",
  verifiedBy: "verifiedBy",
  denialCode: "denialCode",
  remarks: "remarks",
  attachments: "attachments",
  notes: "notes",
};


const fmtDate = (d) => {
  // Handle null, undefined, empty
  if (!d || d === "" || d === "null" || d === "undefined") {
    return "";
  }

  // Remove time if present
  const cleanDate = String(d).split("T")[0].split(" ")[0];

  // If format is MM/DD/YYYY
  if (cleanDate.includes("/")) {
    const [mm, dd, yyyy] = cleanDate.split("/");

    return `${mm.padStart(2, "0")}/${dd.padStart(2, "0")}/${yyyy}`;
  }

  // If format is YYYY-MM-DD
  if (cleanDate.includes("-")) {
    const [yyyy, mm, dd] = cleanDate.split("-");

    return `${mm.padStart(2, "0")}/${dd.padStart(2, "0")}/${yyyy}`;
  }

  return "";
};

const initials = (name) => (name || "").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const escapeHtml = (value) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

function getTooltip(html) {
  const match = html.match(/data-title=['"]([^'"]+)['"]/);
  return match ? match[1] : "";
}

function StatusBadge({ status }) {
  const cfg =
    STATUS_CFG[status] ?? {
      bg: "#f1f5f9",
      color: "#64748b",
      border: "#e2e8f0",
      dot: "#94a3b8",
    };

  return (
    <span
      className="cs-status-badge"

      title={getTooltip(status)}
    >
      <span

      />

      <span
        dangerouslySetInnerHTML={{
          __html: status || "",
        }}
      />
    </span>
  );
}

const normalizeStatusTab = (tab) => {
  switch (String(tab || "").toLowerCase()) {
    case "submit":
    case "sub":
    case "submitted":
      return "submitted";
    case "nonsubmit":
    case "non-submitted":
    case "nonsub":
      return "nonsub";
    case "fwd":
    case "forwarded":
      return "forwarded";
    case "recvd":
    case "received":
      return "received";
    default:
      return String(tab || "all").toLowerCase();
  }
};

const claimMatchesStatusTab = (claim, tab) => {
  const filterType = normalizeStatusTab(tab);
  const raw = claim.raw || {};
  const claimStatusInt = Number(raw.ClaimStatusInt ?? claim.raw?.ClaimStatusInt ?? 0);
  const submitDate = parseApiDate(raw.SubmitDate ?? claim.submittedOn);
  const secClaimSubmitDate = parseApiDate(raw.SecClaimSubmiDate ?? claim.secSubmitDate);
  const recvdCharge = toNum(raw.recvdCharge ?? claim.paid);
  const isDenied = parseBoolish(raw.isDenied ?? claim.isDenied);

  switch (filterType) {
    case "all":
      return true;
    case "submitted":
      return submitDate !== "" || secClaimSubmitDate !== "";
    case "nonsub":
      return submitDate === "" && secClaimSubmitDate === "";
    case "sent":
      return claimStatusInt == 3;
    case "forwarded":
      return claimStatusInt == 5;
    case "paid":
      return claimStatusInt == 1 && !isDenied && recvdCharge > 0;
    case "rejected":
      return claimStatusInt == 2;
    case "denied":
      return isDenied;
    case "received":
      return parseBoolish(raw.isPaymentReceived ?? claim.isPaymentReceived);
    case "posted":
      return parseBoolish(raw.isPaymentPosted ?? claim.isPaymentPosted);
    default:
      return false;
  }
};

export default function ClaimStatus({ onHome }) {
  /* ─── AUTH / USER ─── */
  const userData = useSelector((state) => state.user.user);
  const sAuthToken = useSelector((state) => state.user.token) ?? "";
  const userPracticeID = userData?.practiceID ?? "";
  const userId = userData?.userID ?? "";
  const userFullName = userData ? `${userData.firstName ?? ""} ${userData.lastName ?? ""}`.trim() : "User";

  // TEMP DIAGNOSTIC: log auth token presence so we can diagnose 401s on REST endpoints.
  // Remove this once auth issues are resolved.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[ClaimStatusV2] sAuthToken length:", sAuthToken?.length || 0, "| first 10 chars:", (sAuthToken || "").slice(0, 10));
    // eslint-disable-next-line no-console
    console.log("[ClaimStatusV2] userData present?", !!userData, "| keys:", userData ? Object.keys(userData) : "(none)");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);





  /* ─── FILTER STATE ─── */
  const [startDate, setStartDate] = useState(() => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 7);
    return dt;
  });
  const [endDate, setEndDate] = useState(new Date());
  // On page open, fetch claims using the date range shown in the UI
  // (default: last 7 days → today). User can tick "All dates" to widen.
  const [allDates, setAllDates] = useState(false);
  const [dateType, setDateType] = useState("DOS");
  // V1 parity (ClaimStatus.js 853-862, 1632-1638): location + status radio
  // are persisted to localStorage so the user's last selection survives a
  // page reload. Falls back to defaults when nothing is stored.
  const [locationFilter, setLocationFilter] = useState(
    () => localStorage.getItem("selectedLocation") || "-1",
  );
  // Match the old UI exactly: default Search By to "9" (Updated By) and
  // insOrPatFilter to "updatedby" (see ClaimStatus.js line 82 / line 194).
  const [searchBy, setSearchBy] = useState("9");
  const [searchText, setSearchText] = useState("");
  const [navSearchBy, setNavSearchBy] = useState("name");
  const [navSearchText, setNavSearchText] = useState("");
  const [cptInput, setCptInput] = useState("");
  const [selectedStatusRadioButton, setSelectedStatusRadioButton] = useState(
    () => localStorage.getItem("selectedStatusRadioButton") || "",
  );
  const [selectedBillingNPI, setSelectedBillingNPI] = useState("");
  const viewType = "0";
  const [insOrPatFilter, setInsOrPatFilter] = useState("updatedby");
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [activeStatusTab, setActiveStatusTab] = useState("all");
  const [activeView, setActiveView] = useState("claimInfo");
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [drawerTab, setDrawerTab] = useState("overview");
  const [selectedRows, setSelectedRows] = useState([]);
  // Rows checked in the Insurance view for batch eligibility — primary vs secondary
  // mirror the old UI's selectedPolicyRows / selectedSecInsuranceRows split.
  const [eligPriRows, setEligPriRows] = useState([]);
  const [eligSecRows, setEligSecRows] = useState([]);
  // Per-column selections in the Payments view that mirror the old UI's
  // selectedWriteOffRows / selectedAdminWriteRows. The Write-Off column's
  // checkboxes drive the "Close" batch action; the Admin Write-Off column's
  // checkboxes drive the "Write-off" modal batch action.
  const [closeRows, setCloseRows] = useState([]);
  const [adminWriteOffRows, setAdminWriteOffRows] = useState([]);
  const [activeCounts, setActiveCounts] = useState(null);
  const [dosFilter, setDosFilter] = useState(""); // active drill-down: filter main table by this DOS
  const [dosSearch, setDosSearch] = useState("");
  const [perPage, setPerPage] = useState("all");
  const [pageNumber, setPageNumber] = useState(1);
  const [toast, setToast] = useState(null);
  const [toastType, setToastType] = useState("ok");
  const [sortKey, setSortKey] = useState("dos");
  const [sortDir, setSortDir] = useState("desc");
  const [colFilters, setColFilters] = useState({});
  const [openRowMenu, setOpenRowMenu] = useState(null);
  const [paymentsEdited, setPaymentsEdited] = useState(false);
  const [draftInputs, setDraftInputs] = useState({});

  /* ─── PRINT COLUMN MODAL ───
     Mirrors V1's print-column picker (ClaimStatus.js line 178 +). Users
     toggle which of the 42 columns appear in the printed/exported file.
     Without this, V2 prints whatever the current view exposes (limited). */
  const [isOpenPrintFilter, setIsOpenPrintFilter] = useState(false);

  /* ─── STATUS FLAG MINI-FILTER ───
     V1 status column header has clickable N/D/P/F/R/P0/S0 labels that
     filter by the raw ClaimStatusFlag value (separate from the tab pills
     which filter by ClaimStatusInt). See ClaimStatus.js line 3660. */
  const [statusFlagFilter, setStatusFlagFilter] = useState(null);

  /* ─── INFINITE SCROLL ───
     V1 loads 200 rows at a time and shows "X of Y loaded" popup when the
     user scrolls to bottom. See ClaimStatus.js line 525-602. */
  const ROWS_PER_PAGE = 200;
  const [visibleRecords, setVisibleRecords] = useState(ROWS_PER_PAGE);
  const [scrollPopupMsg, setScrollPopupMsg] = useState("");
  const [showScrollPopup, setShowScrollPopup] = useState(false);

  /* ─── DELETE CLAIMS FLOW ───
     V1 (ClaimStatus.js 2453-2484): user selects rows in the main table,
     clicks Delete in the batch bar, confirms in a modal. handleDelete then
     filters those rows out of the displayed data and shows a success
     modal that auto-dismisses after 5s. NOTE: V1 only removes locally — no
     API call. We mirror that exactly so behaviour is unchanged. */
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deleteRowCount, setDeleteRowCount] = useState(0);

  /* ─── SEARCH AUTOCOMPLETE ───
     The filter bar has `overflow-x: auto` (needed so it can scroll on
     narrow viewports), which means an absolutely-positioned dropdown
     inside it gets clipped at the bar's edge — that's why the
     suggestions panel "didn't appear" earlier. We render the dropdown as
     `position: fixed` with coordinates computed from the input's
     getBoundingClientRect() so it floats above everything. Same trick the
     LOCATION dropdown above already uses (`showLocDropdown`). */
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSearchID, setSelectedSearchID] = useState("");
  const [suggestPos, setSuggestPos] = useState({ top: 0, left: 0, width: 0 });
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const pageTopRef = useRef(null);
  // Helper: re-measure the input each time the dropdown opens or the
  // user types so the floating panel stays glued to the bottom edge.
  function updateSuggestPos() {
    if (!searchInputRef.current) return;
    const r = searchInputRef.current.getBoundingClientRect();
    setSuggestPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 200) });
  }

  /* ─── LOCATION DROPDOWN ─── */
  const [showLocDropdown, setShowLocDropdown] = useState(false);
  const [locDropdownPos, setLocDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const locRef = useRef(null);
  const locBtnRef = useRef(null);

  /* ─── CPT AUTOCOMPLETE ───
     Same fix as the search suggestions above — render the dropdown as
     position:fixed so it floats over the overflow-clipped filter bar. */
  const [cptValueList, setCptValueList] = useState([]);
  const [showCptList, setShowCptList] = useState(false);
  const [cptListPos, setCptListPos] = useState({ top: 0, left: 0, width: 0 });
  const cptRef = useRef(null);
  const cptInputRef = useRef(null);
  function updateCptListPos() {
    if (!cptInputRef.current) return;
    const r = cptInputRef.current.getBoundingClientRect();
    setCptListPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 240) });
  }

  /* ─── STATUS FILTERING ───
     V2 uses the tab pills (handleStatusTab) and the Status column
     mini-buttons (statusFlagFilter) to filter by status — the per-status
     checkbox UI from V1 would be redundant and was removed at user request.
     The booleans below remain as constants so any downstream filter logic
     that depends on them keeps working (currently no-op since all true). */
  const isSubmitChecked = true;
  const isNonSubmitChecked = true;
  const isSentChecked = true;
  const isForwardChecked = true;
  const isRecvdChecked = true;
  const isPostedChecked = true;
  const isPaidChecked = true;
  const isDeniedChecked = true;
  const isRejectedChecked = true;
  const [isClaimClosed, setIsClaimClosed] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [checkedServices, setCheckedServices] = useState([]);
  const [searchFilterOptions, setSearchFilterOptions] = useState(SEARCH_FILTER_OPTIONS);
  const [statusFilterOptions, setStatusFilterOptions] = useState(STATUS_FILTER_OPTIONS);

  /* ─── DATA STATE ─── */
  const [claims, setClaims] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [claimservices, setClaimServices] = useState([]);
  const [practiceBillingNPIs, setPracticeBillingNPIs] = useState([]);
  const [transactionCodes, setTransactionCodes] = useState([]);
  const [selectedTransactionCode, setSelectedTransactionCode] = useState("93");
  const [selectedFilterProvider, setSelectedFilterProvider] = useState("0");
  const [sPatID, setSPatID] = useState("");
  const [sInsID, setSInsID] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [adjustmentCodeData, setAdjustmentCodeData] = useState([]);
  const [apiTC, setApiTC] = useState({
    allClaims: 0, totalSubmitClaim: 0, totalNonSubmitted: 0,
    totalSent: 0, totalForwarded: 0, totalReceivedClaim: 0,
    totalPosted: 0, totalPaid: 0, totalRejected: 0, totalDenied: 0,
  });
  const [apiTotals, setApiTotals] = useState({ totalBilled: 0, totalReceived: 0, totalBalance: 0 });
  const [showAdminWriteModal, setShowAdminWriteModal] = useState(false);
  // Patient 360 — full history modal showing all claims, services, payments
  // and insurance info for a single patient (filtered from the current claims set).
  const [patient360Claim, setPatient360Claim] = useState(null);
  const [attachmentData, setAttachmentData] = useState([]);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentClaimId, setAttachmentClaimId] = useState("");
  const [eventData, setEventData] = useState([]);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [eventClaimId, setEventClaimId] = useState("");

  /* ─── PORTED FLOWS: state ─── */
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyClaim, setVerifyClaim] = useState(null);
  const [verifyComment, setVerifyComment] = useState("");
  const [verifySaving, setVerifySaving] = useState(false);

  const [showCopayModal, setShowCopayModal] = useState(false);
  const [copayClaim, setCopayClaim] = useState(null);
  const [copayInput, setCopayInput] = useState("");
  const [copaySaving, setCopaySaving] = useState(false);

  const [showSubmitHxModal, setShowSubmitHxModal] = useState(false);
  const [submitHxClaim, setSubmitHxClaim] = useState(null);
  const [submitHxData, setSubmitHxData] = useState([]);

  // Inline history state for the drawer's History tab (separate from the modal versions).
  const [drawerHistoryClaimId, setDrawerHistoryClaimId] = useState("");
  const [drawerHistoryEvents, setDrawerHistoryEvents] = useState([]);
  const [drawerHistorySubmitHx, setDrawerHistorySubmitHx] = useState([]);
  const [drawerHistoryLoading, setDrawerHistoryLoading] = useState(false);

  const [regenLoadingKey, setRegenLoadingKey] = useState("");
  const [providerList, setProviderList] = useState([]);

  const tableRef = useRef(null);
  const selectedServiceIDs = useMemo(
    () => checkedServices.filter((serviceId) => serviceId !== "0"),
    [checkedServices],
  );
  const selectedClaims = useMemo(
    () => claims.filter((claim) => selectedRows.includes(claim.rowKey)),
    [claims, selectedRows],
  );

  /* ─── EFFECTS ─── */
  useEffect(() => {
    document.title = "Claim Status";

    fetchTransactionCodes(); // no user-dependent fields, safe at mount
  }, []);

  // Page sizing is handled entirely in CSS now:
  //  - .cs-app uses height: auto so it shrinks to fit content (no empty space
  //    below when the table has few rows).
  //  - .cs-table-wrap has max-height: calc(100vh - 440px) so when there are
  //    many rows it caps at the viewport and scrolls internally — keeping the
  //    top section visible without forcing a fixed app height.
  // Match body and html background to the page bg so when the table is short
  // and the page is shorter than the viewport, the area below the data
  // doesn't show as a dark grey strip — it blends into the page. Restored on
  // unmount so other pages aren't affected.
  useEffect(() => {
    const previousBodyBg = document.body.style.background;
    const previousHtmlBg = document.documentElement.style.background;
    const previousBodyWidth = document.body.style.width;
    const previousBodyMaxWidth = document.body.style.maxWidth;
    const previousBodyMargin = document.body.style.margin;
    document.body.style.background = "#f0f5f4";
    document.documentElement.style.background = "#f0f5f4";
    // Force body to span the full viewport width so wide monitors don't
    // show a grey strip on the right when the body's parent (or default
    // user-agent styles) constrain its width.
    document.body.style.width = "100vw";
    document.body.style.maxWidth = "100vw";
    document.body.style.margin = "0";
    return () => {
      document.body.style.background = previousBodyBg;
      document.documentElement.style.background = previousHtmlBg;
      document.body.style.width = previousBodyWidth;
      document.body.style.maxWidth = previousBodyMaxWidth;
      document.body.style.margin = previousBodyMargin;
    };
  }, []);

  // Fire user-dependent fetches once userData is present. Matches original
  // ClaimStatus.js which fires unconditionally; here we just wait for userData
  // so we don't send empty sPracticeID before Redux is hydrated.
  useEffect(() => {
    if (!userData) return;
    fetchDropdowns();
    fetchClaimServices();
    fetchPracticeBillingNPIs();
    fetchProvidersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // V1 parity (ClaimStatus.js 1846-1862): on first mount seed sPatID with
  // the current user's userID AND searchText with their userFullName so the
  // default "Updated By" filter pre-fills with the logged-in biller — matches
  // the workflow billers expect on page open. URL deep-links can still
  // override this in the URLSearchParams effect below.
  useEffect(() => {
    if (!userData) return;
    if (userData.userID && !sPatID) setSPatID(String(userData.userID));
    if (userData.userFullName && !searchText && searchBy === "9") {
      setSearchText(userData.userFullName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // Initial data fetch on page open. Fires as soon as userData is hydrated
  // AND claimservices has loaded — that way the fetch payload has both a
  // real iPracticeID and a non-empty sService array, so the server returns
  // claims for the date range visible in the UI. If services don't load
  // within 2.5s (e.g. API down), fire anyway with an empty service list as
  // a fallback so the user isn't stuck on an empty page.
  // Fire the initial fetch once BOTH userData and claimservices are ready —
  // the server returns 500 if sService is empty, so we must have a non-empty
  // services list before firing. If services don't load within 3s (API down),
  // we still fire so the user isn't stuck on an empty page.
  useEffect(() => {
    console.log("[ClaimStatusV2] init fetch effect", {
      hasLoaded,
      userData: !!userData,
      claimservicesLen: claimservices?.length ?? 0,
    });
    if (hasLoaded) { console.log("  → skip: already loaded"); return; }
    if (!userData) { console.log("  → skip: no userData"); return; }
    if (claimservices && claimservices.length > 0) {
      console.log("  → firing fetchClaimData with", claimservices.length, "services");
      fetchClaimData();
      return;
    }
    console.log("  → no services yet, setting 3s fallback timer");
    const t = setTimeout(() => {
      if (!hasLoaded) {
        console.log("[ClaimStatusV2] 3s timer fired, calling fetchClaimData with empty services");
        fetchClaimData();
      }
    }, 3000);
    return () => { console.log("  → cleanup: clearing timer"); clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, claimservices, hasLoaded]);

  // If the initial fetch fired BEFORE claimservices loaded (the 3s timeout
  // path), we ended up sending sService:[] and the server returned 500 with
  // an empty claims array. Once services finally arrive, refetch ONCE so
  // the user gets the 7-day data they expect on page open. The
  // initialRefetchDone ref guarantees this fires at most once.
  const initialRefetchDoneRef = useRef(false);
  useEffect(() => {
    if (initialRefetchDoneRef.current) return;
    if (!hasLoaded) return;
    if (!userData) return;
    if (!claimservices || claimservices.length === 0) return;
    // Only refetch if the user hasn't done any manual filtering yet.
    if (selectedServiceIDs.length > 0) return;
    if (claims.length > 0) {
      initialRefetchDoneRef.current = true;
      return;
    }
    initialRefetchDoneRef.current = true;
    fetchClaimData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimservices, hasLoaded, userData]);

  /* URL query-param deep-link: ?InsID=&startDate=&endDate=&InsName=&selectedSearchFilter= */
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const insId = p.get("InsID");
    const from = p.get("startDate");
    const to = p.get("endDate");
    const insName = p.get("InsName");
    const sbf = p.get("selectedSearchFilter");
    if (insId && from && to) {
      setSInsID(insId);
      setSPatID(insId);
      try { setStartDate(new Date(from)); } catch { }
      try { setEndDate(new Date(to)); } catch { }
      if (insName) setSearchText(insName);
      if (sbf) setInsOrPatFilter(sbf);
      setSearchBy("3");
      fetchClaimData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") { setSelectedClaim(null); setOpenRowMenu(null); setShowSuggestions(false); setShowCptList(false); return; }
      // V1 parity: window-level Enter triggers a search (ClaimStatus.js 1977).
      // Skip when typing in a textarea (so notes editor isn't hijacked) or
      // a contentEditable. Inputs/selects DO fire — matches V1 behaviour.
      if (e.key === "Enter") {
        const tag = (e.target?.tagName || "").toLowerCase();
        if (tag === "textarea" || e.target?.isContentEditable) return;
        // Don't trigger while the user is inside a Bootstrap modal/dialog
        // that has its own submit button (e.g. PrintColumnModal).
        if (document.querySelector(".modal.show")) return;
        fetchClaimData();
      }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, searchBy, searchText, locationFilter, cptInput, selectedStatusRadioButton, selectedServiceIDs.length]);

  useEffect(() => {
    const h = (e) => {
      if (!e.target.closest?.(".cs-row-menu-wrap")) setOpenRowMenu(null);
      // Suggestions + CPT dropdowns are now rendered as fixed-position
      // portals outside searchRef/cptRef, so we have to also exempt the
      // floating panel itself. Otherwise the document mousedown closes
      // the panel before the option's onMouseDown can fire.
      if (
        searchRef.current &&
        !searchRef.current.contains(e.target) &&
        !e.target.closest?.(".cs-floating-suggest")
      ) setShowSuggestions(false);
      if (
        cptRef.current &&
        !cptRef.current.contains(e.target) &&
        !e.target.closest?.(".cs-floating-cpt")
      ) setShowCptList(false);
      if (locRef.current && !locRef.current.contains(e.target)) setShowLocDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Keep the floating suggestions/CPT panels glued to their input on
  // window resize and page scroll. Only attached while a panel is open
  // to avoid pointless re-renders during normal table scrolling.
  useEffect(() => {
    if (!showSuggestions && !showCptList) return;
    const onUpdate = () => {
      if (showSuggestions) updateSuggestPos();
      if (showCptList) updateCptListPos();
    };
    window.addEventListener("scroll", onUpdate, true);
    window.addEventListener("resize", onUpdate);
    return () => {
      window.removeEventListener("scroll", onUpdate, true);
      window.removeEventListener("resize", onUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuggestions, showCptList]);

  /* ─── URL DEEP-LINK: hash params from dashboard (e.g. #?FromDate=...&ToDate=...&FilterValue=...) ─── */
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.includes("?")) return;
    const p = new URLSearchParams(hash.split("?")[1]);
    const from = p.get("FromDate");
    const to = p.get("ToDate");
    const fval = p.get("FilterValue");
    if (from && to) {
      try {
        setStartDate(new Date(from));
        setEndDate(new Date(to));
      } catch { }
    }
    if (fval) {
      // Match original ClaimStatus.js: hash FilterValue sets both the server-side
      // status filter (sClaimStatus in API request) AND the client-side pill tab.
      setSelectedStatusRadioButton(fval);
      setActiveStatusTab(normalizeStatusTab(fval));
    }
  }, []);

  /* ─── SEARCH AUTOCOMPLETE ─── */
  useEffect(() => {
    if (!searchText.trim()) { setSuggestions([]); return; }
    const t = setTimeout(() => fetchSuggestions(searchBy, searchText), 300);
    return () => clearTimeout(t);
  }, [searchText, searchBy]);



  async function fetchSuggestions(by, key) {
    try {
      if (by === "1") {
        // Patient names - GET endpoint
        const inp = JSON.stringify({ PracticeID: userPracticeID, SearchKey: key, SearchBy: 0, isFromLogin: 0 });
        const r = await fetch(`${BASE_URL_SEARCH}${API.GetAllPatientNames}?sJSONInput=${inp}`,
          { headers: { "Accept": "application/json" } });
        const data = JSON.parse(await r.json());
        const patientData = data.Data;
        setSuggestions(
          (Array.isArray(patientData) ? patientData : []).map((x) => ({
            label: `${x.LastName}, ${x.FirstName}`,
            val: x.PatientID
          }))
        );
      } else if (by === "3") {
        // Insurance companies - GET endpoint
        const inp = JSON.stringify({ SearchKey: key });
        const r = await fetch(`${BASE_URL_SEARCH}${API.GetInsuCompanyNames}?sJSONInput=${inp}`,
          { headers: { "Accept": "application/json" } });
        const data = JSON.parse(await r.json());
        setSuggestions((Array.isArray(data?.Data) ? data.Data : []).map((x) => ({
          label: x.Insurance, val: String(x.InsuID)
        })));
      } else if (by === "5") {
        // Providers
        const data = await postJSON(API.GetIntelliProvidersNames,
          { sProviderName: key, sPracticeID: userPracticeID, LoggedInUserDetails: userData }, BASE_URL, false);
        const list = JSON.parse(data.d)?.Data ?? [];
        setSuggestions((Array.isArray(list) ? list : []).map((x) => ({
          label: x.split("$")[0].split("|")[1]?.trim() ?? "", val: x.split("$")[1]?.trim() ?? ""
        })));
      } else if (by === "10") {
        // Clearing house - GET endpoint
        const inp = JSON.stringify({ SearchKey: key });
        const r = await fetch(`${COMMON_BASE_URL}${API.GetClearingHouse}?sJSONInput=${inp}`,
          { headers: { "Accept": "application/json" } });
        const data = JSON.parse(await r.json());
        setSuggestions((Array.isArray(data) ? data : []).map((x) => ({
          label: x.ClearingHouseName, val: String(x.MCHID)
        })));
      } else if (by === "4" || by === "6" || by === "8" || by === "9") {
        // Submitted by / Updated by
        const data = await postJSON(API.GetSuggestionDropDowns,
          { sPracticeID: userPracticeID, sUserID: userId, srchKey: key });
        const list = JSON.parse(data.d)?.Data ?? [];
        setSuggestions((Array.isArray(list) ? list : []).map((x) => ({
          label: x.split("$")[1] ?? "", val: x.split("$")[0] ?? ""
        })));
      } else {
        setSuggestions([]);
      }
      setShowSuggestions(true);
    } catch { setSuggestions([]); }
  }



  /* ─── CPT AUTOCOMPLETE ─── */
  useEffect(() => {
    if (!cptInput.trim()) { setCptValueList([]); setShowCptList(false); return; }
    const t = setTimeout(() => fetchCPTList(cptInput), 300);
    return () => clearTimeout(t);
  }, [cptInput]);

  async function fetchCPTList(key) {
    try {
      const inp = JSON.stringify({ PracticeID: userPracticeID, SearchKey: key });
      const r = await fetch(`${BASE_URL_SEARCH}${API.GetAllCPTList}?sInputJSON=${inp}`,
        { headers: { "Accept": "application/json", "Authorization": `Bearer ${sAuthToken}` } });
      const data = JSON.parse(await r.json());
      setCptValueList(Array.isArray(data) ? data.filter((x) => String(x.CPTCode) !== cptInput) : []);
      setShowCptList(true);
    } catch { setCptValueList([]); }
  }

  /* ─── HELPERS ─── */
  function showToast(msg, type = "ok") {
    setToast(msg); setToastType(type);
    setTimeout(() => setToast(null), 2800);
  }
  function requireSelection(action) {
    if (selectedRows.length === 0) { showToast("Please select at least one claim", "warn"); return; }
    action();
  }

  // V1 helpers ported in so PrintColumnModal + row UI can use them
  // exactly the way ClaimStatus.js does.
  // - getVerifiedBy: extracts initials from a verified-by full name (ClaimStatus.js 3643)
  // - getClaimAdjustmentText: text-only version of getClaimAdjustment for export
  // - getRepeatedClaimIdsWithColors: alternating row colors for repeated ClaimIds
  function getVerifiedBy(name) {
    if (!name) return "";
    return name
      .split(" ")
      .map((w) => (w[0] ? w[0].toUpperCase() : ""))
      .join("");
  }
  function getClaimAdjustmentText(claimId) {
    if (!adjustmentCodeData || adjustmentCodeData.length === 0) return "";
    const adj = adjustmentCodeData.find(
      (a) => String(a.ClaimID) === String(claimId),
    );
    return adj ? adj.ClaimAdjustmentCode : "";
  }
  // Build a map of repeated ClaimIds → alternating bg color so duplicate
  // CPT-line rows for the same claim are visually grouped (V1 line 4439).
  const { claimIdCount, claimIdColors } = useMemo(() => {
    const count = {};
    const colors = {};
    let toggle = true;
    claims.forEach((c) => {
      if (count[c.id]) count[c.id] += 1;
      else {
        count[c.id] = 1;
        colors[c.id] = toggle ? "#f3f4f6" : "#e5e7eb";
        toggle = !toggle;
      }
    });
    return { claimIdCount: count, claimIdColors: colors };
  }, [claims]);

  // PrintColumnModal column config — mirrors V1's printColumnsConfig
  // (ClaimStatus.js line 178) but uses V2's mapped claim shape.
  // Each col has a `label` shown in the modal + `render(c, i)` for the
  // exported cell. Index column shows row number.
  const printColumnsConfig = useMemo(() => ([
    { key: "index", label: "#", render: (_c, i) => i + 1 },
    { key: "LocationNickname", label: "Location", render: (c) => c.location || "" },
    { key: "ClaimDOS", label: "DOS", render: (c) => c.dos ? fmtDate(c.dos) : "" },
    { key: "ClaimId", label: "Claim ID", render: (c) => c.id || "" },
    { key: "PatientName", label: "Patient Name", render: (c) => c.patient || "" },
    { key: "ClaimConType", label: "BH/SUD", render: (c) => c.conType || "" },
    { key: "DOB", label: "DOB", render: (c) => c.dob ? fmtDate(c.dob) : "" },
    { key: "InsurancePri", label: "Insurance (Pri)", render: (c) => [c.payer, c.payerId, c.policyNumber].filter(Boolean).join("\n") },
    { key: "InsuranceSec", label: "Insurance (Sec)", render: (c) => [c.secPayer, c.secPayerId, c.secPolicyNumber].filter(Boolean).join("\n") },
    { key: "ClearingHouseName", label: "Clearing House", render: (c) => c.clearingHouse || "" },
    { key: "SubmitDate", label: "Submit Date (Pri)", render: (c) => c.submittedOn ? fmtDate(c.submittedOn) : "" },
    { key: "SecClaimSubmiDate", label: "Submit Date (Sec)", render: (c) => c.secSubmitDate ? fmtDate(c.secSubmitDate) : "" },
    { key: "ClaimStatus", label: "Status", render: (c) => c.status || "" },
    { key: "CPT", label: "CPT", render: (c) => c.cpt || "" },
    { key: "Units", label: "Units", render: (c) => c.units ?? "" },
    { key: "TotalCharge", label: "Total Charge", render: (c) => c.billed.toFixed(2) },
    { key: "RecvdPri", label: "Recvd Charge (Pri)", render: (c) => c.paid.toFixed(2) },
    { key: "RecvdSec", label: "Recvd Charge (Sec)", render: (c) => c.secPaid.toFixed(2) },
    { key: "Adjustment", label: "Contractual Adjustment", render: (c) => c.contractual.toFixed(2) },
    { key: "SecPendingAmt", label: "Secondary Pending", render: (c) => c.secPendingAmt ? `$${c.secPendingAmt.toFixed(2)}` : "0" },
    { key: "Deductible", label: "Deductible", render: (c) => c.deductible.toFixed(2) },
    { key: "PatientAmount", label: "Patient Responsibility", render: (c) => c.patRes.toFixed(2) },
    { key: "Copay", label: "Copay", render: (c) => { const v = c.copay ?? 0; const a = c.copayAmount; return a ? `${v} (${a})` : `${v}`; } },
    { key: "OtherAdjustment", label: "OtherAdjustment", render: (c) => c.otherAdj.toFixed(2) },
    { key: "FederalPayment", label: "Federal Reduction", render: (c) => c.federalPayment.toFixed(2) },
    { key: "SelfPay", label: "Self Pay", render: (c) => c.selfPay.toFixed(2) },
    { key: "WriteOffAmount", label: "WriteOff Amount", render: (c) => c.writeOff.toFixed(2) },
    { key: "AdminWriteOff", label: "Admin Write-off", render: (c) => c.adminWriteOff.toFixed(2) },
    { key: "Balance", label: "Balance", render: (c) => c.balance.toFixed(2) },
    { key: "PaymentChqueNo", label: "Cheque # (Pri)", render: (c) => c.checkNumPri || "" },
    { key: "PaymentChequeDate", label: "Cheque Date (Pri)", render: (c) => c.checkDatePri ? fmtDate(c.checkDatePri) : "" },
    { key: "PayerRefNumber", label: "Payer Ref # (Pri)", render: (c) => c.payerRefPri || "" },
    { key: "SecCPTChequeNumber", label: "Cheque # (Sec)", render: (c) => c.checkNumSec || "" },
    { key: "SecCPTChequeDate", label: "Cheque Date (Sec)", render: (c) => c.checkDateSec ? fmtDate(c.checkDateSec) : "" },
    { key: "SecPayerclaimRefNumber", label: "Payer Ref # (Sec)", render: (c) => c.payerRefSec || "" },
    { key: "Provider", label: "Provider", render: (c) => c.provider || "" },
    { key: "UpdatedBy", label: "Updated By", render: (c) => c.updatedBy || "" },
    { key: "SubmittedBy", label: "Submitted By", render: (c) => c.submittedBy || "" },
    { key: "AdjustmentCodes", label: "Adj/Denial/Rejection Codes", render: (c) => getClaimAdjustmentText(c.id) },
    { key: "Remarks", label: "Remarks", render: (c) => c.remarks || "" },
    { key: "ErrorDetails", label: "Error Details", render: (c) => c.denialReason || "" },
    { key: "Attachments", label: "Attachments", render: (c) => c.attachments || "" },
    { key: "VerifiedBy", label: "Verified By", render: (c) => getVerifiedBy(c.verifiedBy) },
    { key: "Notes", label: "Notes", render: (c) => c.notes || "" },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [adjustmentCodeData]);

  // Visibility map driven by the user's column-picker selections.
  // Default: every column visible (matches V1 behaviour at line 514).
  const [printColumnVisibility, setPrintColumnVisibility] = useState(() =>
    [
      "index", "LocationNickname", "ClaimDOS", "ClaimId", "PatientName", "ClaimConType", "DOB",
      "InsurancePri", "InsuranceSec", "ClearingHouseName", "SubmitDate", "SecClaimSubmiDate",
      "ClaimStatus", "CPT", "Units", "TotalCharge", "RecvdPri", "RecvdSec", "Adjustment",
      "SecPendingAmt", "Deductible", "PatientAmount", "Copay", "OtherAdjustment",
      "FederalPayment", "WriteOffAmount", "AdminWriteOff", "Balance", "PaymentChqueNo",
      "PaymentChequeDate", "PayerRefNumber", "SecCPTChequeNumber", "SecCPTChequeDate",
      "SecPayerclaimRefNumber", "Provider", "UpdatedBy", "SubmittedBy", "AdjustmentCodes",
      "Remarks", "ErrorDetails", "Attachments", "VerifiedBy", "Notes",
    ].reduce((acc, key) => ({ ...acc, [key]: true }), {}),
  );
  const allPrintSelected = printColumnsConfig.every((c) => printColumnVisibility[c.key]);
  const nonePrintSelected = printColumnsConfig.every((c) => !printColumnVisibility[c.key]);
  function togglePrintColumn(key) {
    setPrintColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }
  function handleSelectAllPrintCols() {
    const next = {};
    printColumnsConfig.forEach((c) => { next[c.key] = true; });
    setPrintColumnVisibility(next);
  }
  function handleDeselectAllPrintCols() {
    const next = {};
    printColumnsConfig.forEach((c) => { next[c.key] = false; });
    setPrintColumnVisibility(next);
  }
  // Print with column-picker selections. Opens a new window with a styled
  // table for the user to print. Mirrors V1 handlePrint (ClaimStatus.js 3940)
  // including auto-sized fonts for wide vs narrow column sets.
  function handlePrintWithColumns() {
    const selected = printColumnsConfig.filter((c) => printColumnVisibility[c.key]);
    if (selected.length === 0) {
      showToast("Please select at least one column to print", "warn");
      return;
    }
    const n = selected.length;
    let bodyFs = 16, cellFs = 15, headerFs = 16, titleFs = 26;
    if (n > 15 && n <= 25) { bodyFs = 14; cellFs = 13; headerFs = 14; titleFs = 24; }
    if (n > 25) { bodyFs = 12; cellFs = 11; headerFs = 12; titleFs = 22; }
    const rows = displayed.map((c, i) => {
      const cells = selected.map((col) => `<td>${escapeHtml(String(col.render(c, i) ?? ""))}</td>`).join("");
      return `<tr>${cells}</tr>`;
    }).join("");
    const totalsRow = `<tr style="background-color:lightgray;font-weight:bold">${selected.map((col) => {
      switch (col.key) {
        case "Units": return `<td>${displayed.reduce((s, c) => s + (c.units || 0), 0)}</td>`;
        case "TotalCharge": return `<td>$${displayed.reduce((s, c) => s + c.billed, 0).toFixed(2)}</td>`;
        case "RecvdPri": return `<td>$${displayed.reduce((s, c) => s + c.paid, 0).toFixed(2)}</td>`;
        case "RecvdSec": return `<td>$${displayed.reduce((s, c) => s + c.secPaid, 0).toFixed(2)}</td>`;
        case "Adjustment": return `<td>$${displayed.reduce((s, c) => s + c.contractual, 0).toFixed(2)}</td>`;
        case "SecPendingAmt": return `<td>$${displayed.reduce((s, c) => s + c.secPendingAmt, 0).toFixed(2)}</td>`;
        case "Deductible": return `<td>$${displayed.reduce((s, c) => s + c.deductible, 0).toFixed(2)}</td>`;
        case "PatientAmount": return `<td>$${displayed.reduce((s, c) => s + c.patRes, 0).toFixed(2)}</td>`;
        case "Copay": return `<td>$${displayed.reduce((s, c) => s + c.copay, 0).toFixed(2)}</td>`;
        case "OtherAdjustment": return `<td>$${displayed.reduce((s, c) => s + c.otherAdj, 0).toFixed(2)}</td>`;
        case "FederalPayment": return `<td>$${displayed.reduce((s, c) => s + c.federalPayment, 0).toFixed(2)}</td>`;
        case "SelfPay": return `<td>$${displayed.reduce((s, c) => s + c.selfPay, 0).toFixed(2)}</td>`;
        case "WriteOffAmount": return `<td>$${displayed.reduce((s, c) => s + c.writeOff, 0).toFixed(2)}</td>`;
        case "AdminWriteOff": return `<td>$${displayed.reduce((s, c) => s + c.adminWriteOff, 0).toFixed(2)}</td>`;
        case "Balance": return `<td>$${displayed.reduce((s, c) => s + c.balance, 0).toFixed(2)}</td>`;
        default: return `<td></td>`;
      }
    }).join("")}</tr>`;
    const headerHTML = selected.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("");
    const w = window.open("", "_blank");
    if (!w) { showToast("Please allow pop-ups to print", "warn"); return; }
    w.document.write(`<html><head><title>Claim Status</title><style>
      body{font-family:Arial,sans-serif;margin:20px;font-size:${bodyFs}px;color:#000}
      .print-title{text-align:center;font-size:${titleFs}px;font-weight:bold;margin-bottom:20px;color:#0173b7}
      table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #000;padding:6px;text-align:left;font-size:${cellFs}px;word-break:break-word}
      th{background-color:#e5e7eb;color:#000;font-weight:600;font-size:${headerFs}px}
      @media print{@page{size:A3 landscape;margin:10mm}}
      body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    </style></head><body>
      <table><thead><tr>${headerHTML}</tr></thead><tbody>${rows}${totalsRow}</tbody></table>
      <script>window.onload=function(){window.print()};window.onafterprint=function(){window.close()};</script>
    </body></html>`);
    w.document.close();
    setIsOpenPrintFilter(false);
  }
  // Excel export uses the same column visibility map as the print dialog.
  function handleExportExcelWithColumns() {
    const selected = printColumnsConfig.filter((c) => printColumnVisibility[c.key]);
    if (selected.length === 0) {
      showToast("Please select at least one column to export", "warn");
      return;
    }
    const rows = displayed.map((c, i) => {
      const obj = {};
      selected.forEach((col) => { obj[col.label] = col.render(c, i); });
      return obj;
    });
    // Totals row mirrors handlePrint above.
    const totals = {};
    selected.forEach((col) => {
      switch (col.key) {
        case "Units": totals[col.label] = displayed.reduce((s, c) => s + (c.units || 0), 0); break;
        case "TotalCharge": totals[col.label] = displayed.reduce((s, c) => s + c.billed, 0).toFixed(2); break;
        case "RecvdPri": totals[col.label] = displayed.reduce((s, c) => s + c.paid, 0).toFixed(2); break;
        case "RecvdSec": totals[col.label] = displayed.reduce((s, c) => s + c.secPaid, 0).toFixed(2); break;
        case "Adjustment": totals[col.label] = displayed.reduce((s, c) => s + c.contractual, 0).toFixed(2); break;
        case "SecPendingAmt": totals[col.label] = displayed.reduce((s, c) => s + c.secPendingAmt, 0).toFixed(2); break;
        case "Deductible": totals[col.label] = displayed.reduce((s, c) => s + c.deductible, 0).toFixed(2); break;
        case "PatientAmount": totals[col.label] = displayed.reduce((s, c) => s + c.patRes, 0).toFixed(2); break;
        case "Copay": totals[col.label] = displayed.reduce((s, c) => s + c.copay, 0).toFixed(2); break;
        case "OtherAdjustment": totals[col.label] = displayed.reduce((s, c) => s + c.otherAdj, 0).toFixed(2); break;
        case "FederalPayment": totals[col.label] = displayed.reduce((s, c) => s + c.federalPayment, 0).toFixed(2); break;
         case "SelfPay": totals[col.label] = displayed.reduce((s, c) => s + c.selfPay, 0).toFixed(2); break;
        case "WriteOffAmount": totals[col.label] = displayed.reduce((s, c) => s + c.writeOff, 0).toFixed(2); break;
        case "AdminWriteOff": totals[col.label] = displayed.reduce((s, c) => s + c.adminWriteOff, 0).toFixed(2); break;
        case "Balance": totals[col.label] = displayed.reduce((s, c) => s + c.balance, 0).toFixed(2); break;
        default: totals[col.label] = "";
      }
    });
    rows.push(totals);
    exportExcel(rows, "ClaimStatus", "ClaimStatus");
  }
  function parseApiPayload(payload) {
    if (payload == null) return payload;
    if (typeof payload === "string") {
      try {
        return JSON.parse(payload);
      } catch {
        return payload;
      }
    }
    if (typeof payload?.d === "string") {
      try {
        return JSON.parse(payload.d);
      } catch {
        return payload.d;
      }
    }
    return payload?.d ?? payload;
  }
  // .asmx classic ASP.NET endpoints do NOT allow the Authorization header in CORS,
  // so we strip it automatically. The original ClaimStatus.js follows the same convention.
  function isAsmxEndpoint(endpoint) {
    return typeof endpoint === "string" && /\.asmx\//i.test(endpoint);
  }
  function getAuthHeaders(includeAuth = true, endpoint = "") {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    const skipForAsmx = isAsmxEndpoint(endpoint);
    // Match original ClaimStatus.js: always send Authorization for non-asmx endpoints,
    // even if token looks empty (backend decides). Stripping it caused 401s on REST endpoints.
    if (includeAuth && !skipForAsmx) {
      headers.Authorization = `Bearer ${sAuthToken}`;
    }
    return headers;
  }
  // Default: NO Authorization header. The original ClaimStatus.js sends auth for only
  // ~5 specific endpoints; everything else relies on cookie/no-auth. Callers opt in
  // to auth by passing `true` as the last argument.
  function postJSON(endpoint, inputStruct, baseUrl = BASE_URL, includeAuth = false) {
    return fetch(baseUrl + endpoint, {
      method: "POST",
      headers: getAuthHeaders(includeAuth, endpoint),
      body: JSON.stringify({ sInputJSON: JSON.stringify(inputStruct) }),
    }).then(r => r.json());
  }
  function postDoubleEncodedJSON(endpoint, inputStruct, baseUrl = BASE_URL_BILLING, includeAuth = false) {
    return fetch(baseUrl + endpoint, {
      method: "POST",
      headers: getAuthHeaders(includeAuth, endpoint),
      body: JSON.stringify(JSON.stringify(inputStruct)),
    }).then((r) => r.json());
  }
  function getJSONWithQuery(
    endpoint,
    queryName,
    inputStruct,
    baseUrl = BASE_URL,
    includeAuth = false,
  ) {
    // Match original ClaimStatus.js: raw JSON in query, no URL encoding.
    // The backend's query parser expects unencoded JSON.
    const queryValue = JSON.stringify(inputStruct);
    const separator = endpoint.includes("?") ? "&" : "?";
    return fetch(`${baseUrl}${endpoint}${separator}${queryName}=${queryValue}`, {
      method: "GET",
      headers: getAuthHeaders(includeAuth, endpoint),
    }).then((r) => r.json());
  }
  function updateClaimLocal(rowKey, patch) {
    setClaims((prev) =>
      prev.map((claim) =>
        claim.rowKey === rowKey ? { ...claim, ...patch } : claim,
      ),
    );
    setSelectedClaim((prev) =>
      prev && prev.rowKey === rowKey ? { ...prev, ...patch } : prev,
    );
  }
  function setDraftValue(rowKey, field, value) {
    setDraftInputs((prev) => ({
      ...prev,
      [rowKey]: {
        ...(prev[rowKey] ?? {}),
        [field]: value,
      },
    }));
    setPaymentsEdited(true);
  }
  function getDraftValue(claim, field, fallback) {
    return draftInputs[claim.rowKey]?.[field] ?? fallback;
  }
  function clearDraftValue(rowKey, field) {
    setDraftInputs((prev) => {
      if (!prev[rowKey]) return prev;
      const nextFieldState = { ...(prev[rowKey] ?? {}) };
      delete nextFieldState[field];
      if (Object.keys(nextFieldState).length === 0) {
        const next = { ...prev };
        delete next[rowKey];
        setPaymentsEdited(Object.keys(next).length > 0);
        return next;
      }
      const next = {
        ...prev,
        [rowKey]: nextFieldState,
      };
      setPaymentsEdited(Object.keys(next).length > 0);
      return next;
    });
  }
  function closeTransientPanels() {
    setOpenRowMenu(null);
    setShowSuggestions(false);
    setShowCptList(false);
    setShowAdminWriteModal(false);
    setShowAttachmentModal(false);
    setShowEventsModal(false);
  }
  function recalculateClaimAmounts(claim, patch) {
    const next = { ...claim, ...patch };
    const contractual =
      next.billed -
      (next.paid +
        next.secPaid +
        next.deductible +
        next.patRes +
        next.writeOff +
        next.federalPayment +
        next.selfPay);
    const balance =
      next.billed -
      (next.paid +
        next.secPaid +
        contractual +
        next.otherAdj +
        next.writeOff +
        next.adminWriteOff +
        next.copay +
        next.federalPayment +
        next.selfPay);
    return {
      contractual: Number.isFinite(contractual) ? contractual : 0,
      balance: Number.isFinite(balance) ? balance : 0,
    };
  }

  function getExportColumns(view = activeView) {
    return VIEW_COLUMNS[view].filter((column) => column !== "check" && column !== "actions");
  }

  function getClaimColumnValue(claim, column) {
    switch (column) {
      case "patient":
        return claim.patient;
      case "claimId":
        return claim.id;
      case "dos":
        return formatDOSDate(claim.dos);
      case "bhsud":
        return claim.conType;
      case "location":
        return claim.location;
      case "insurance":
      case "insPri":
        return [claim.payer, claim.payerId, claim.policyNumber].filter(Boolean).join(" | ");
      case "submitDatePri":
        return claim.submittedOn ? fmtDate(claim.submittedOn) : "";
      case "insSec":
        return [claim.secPayer, claim.secPayerId, claim.secPolicyNumber].filter(Boolean).join(" | ");
      case "cpt":
        return claim.cpt;
      case "status":
        return claim.status;
      case "charge":
      case "totalCharge":
        return claim.billed.toFixed(2);
      case "balance":
        return claim.balance.toFixed(2);
      case "updated":
        return `${claim.updatedBy} ${fmtDate(claim.updatedOn)}`.trim();
      case "units":
        return String(claim.units || "");
      case "recvdPri":
        return claim.paid.toFixed(2);
      case "recvdSec":
        return claim.secPaid.toFixed(2);
      case "contractual":
        return claim.contractual.toFixed(2);
      case "deductible":
        return claim.deductible.toFixed(2);
      case "patRes":
        return claim.patRes.toFixed(2);
      case "copay":
        return claim.copay.toFixed(2);
      case "otherAdj":
        return claim.otherAdj.toFixed(2);
      case "secPendingAmt":
        return claim.secPendingAmt.toFixed(2);
      case "federalPayment":
        return claim.federalPayment.toFixed(2);
         case "selfPay":
        return claim.selfPay.toFixed(2);
      case "chequePri":
        return claim.checkNumPri;
      case "chequeDatePri":
        return claim.checkDatePri ? fmtDate(claim.checkDatePri) : "";
      case "payerRefPri":
        return claim.payerRefPri;
      case "chequeSec":
        return claim.checkNumSec;
      case "chequeDateSec":
        return claim.checkDateSec ? fmtDate(claim.checkDateSec) : "";
      case "payerRefSec":
        return claim.payerRefSec;
      case "writeOff":
        return claim.writeOff.toFixed(2);
      case "adminWriteOff":
        return claim.adminWriteOff.toFixed(2);
      case "clearingHouse":
        return claim.clearingHouse;
      case "provider":
        return claim.provider;
      case "submittedBy":
        return `${claim.submittedBy} ${fmtDate(claim.submittedOn)}`.trim();
      case "secSubmitDate":
        return claim.secSubmitDate ? fmtDate(claim.secSubmitDate) : "";
      case "updatedBy":
        return claim.updatedBy;
      case "verifiedBy":
        return claim.verifiedBy;
      case "denialCode":
        return claim.denialCode;
      case "remarks":
        return claim.remarks;
      case "attachments":
        return String(claim.attachments || "");
      case "notes":
        return claim.notes;
      default:
        return "";
    }
  }

  function openPrintableDocument(title, headers, rows, subtitle) {
    const printWindow = window.open("", "_blank", "noopener,noreferrer,width=1400,height=900");
    if (!printWindow) {
      showToast("Please allow pop-ups to print or export PDF", "warn");
      return;
    }

    const bodyRows = rows
      .map(
        (row) =>
          `<tr>${row
            .map((cell) => `<td>${escapeHtml(cell || "-")}</td>`)
            .join("")}</tr>`,
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${escapeHtml(title)}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
            h1 { margin: 0 0 8px; font-size: 24px; }
            p { margin: 0 0 16px; color: #475569; font-size: 13px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: left; vertical-align: top; }
            th { background: #e2e8f0; }
            tbody tr:nth-child(even) { background: #f8fafc; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(title)}</h1>
          ${subtitle ? `<p>${escapeHtml(subtitle)}</p>` : ""}
          <table>
            <thead><tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join("")}</tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  }

  function downloadCsv(filename, headers, rows) {
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`)
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  function printClaims(rows, title, view = activeView) {
    const columns = getExportColumns(view);
    openPrintableDocument(
      title,
      columns.map((column) => COL_LABELS[column] ?? column),
      rows.map((claim) => columns.map((column) => getClaimColumnValue(claim, column))),
      `${rows.length} claim(s) • ${view} view`,
    );
  }

  // Mirrors exportToExcel in ClaimStatus.js (line 4044): builds an array of
  // objects keyed by column label and hands it to the shared xlsx writer.
  function exportClaims(rows, filename, view = activeView) {
    const columns = getExportColumns(view);
    const headers = columns.map((column) => COL_LABELS[column] ?? column);
    const objectRows = rows.map((claim) => {
      const row = {};
      columns.forEach((column, idx) => {
        row[headers[idx]] = getClaimColumnValue(claim, column);
      });
      return row;
    });
    const baseName = String(filename || "ClaimStatus").replace(/\.[a-z]+$/i, "");
    exportExcel(objectRows, baseName, "ClaimStatus");
  }

  // Mirrors exportToPDF in ClaimStatus.js (line 4113): posts the rendered
  // table HTML to the services DataExport/PDF endpoint, which returns a URL
  // to the generated PDF.
  async function exportPdfFromTable(pageName = "ClaimStatus") {
    const tableEl = tableRef.current;
    const sPrintableData = tableEl
      ? (tableEl.parentNode?.innerHTML ?? tableEl.outerHTML)
      : "";
    if (!sPrintableData) {
      showToast("Nothing to export", "warn");
      return;
    }
    const PrintableDataDetails = {
      PrintableData: sPrintableData.replace(/gvClosedClaims/, "gvAcceptdClaims"),
      PageName: pageName,
    };
    const InputJSONLevelOne = JSON.stringify(PrintableDataDetails);
    const InputJSON = JSON.stringify(InputJSONLevelOne);
    try {
      const response = await fetch(SERVICES_URL + API.ExportPdfFile, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: InputJSON,
      });
      const data = await response.json();
      if (data && data[2]) {
        window.open(data[2], "_blank", "noopener,noreferrer");
        return;
      }
      showToast("PDF export failed", "warn");
    } catch (error) {
      console.error("exportPdfFromTable:", error);
      showToast("PDF export failed", "warn");
    }
  }

  function printBreakdown() {
    openPrintableDocument(
      "Claim Status Daily Breakdown",
      ["DOS", "Day", "All", "Submitted", "Non-sub", "Sent", "Fwd", "Received", "Posted", "Paid", "Rejected", "Denied"],
      dailyFiltered.map((row) => [
        row.DOS ?? "",
        row.DOSDay ?? "",
        String(row.AllClaims ?? ""),
        String(row.Submitted ?? ""),
        String(row.NonSubmitted ?? ""),
        String(row.SENT ?? ""),
        String(row.FORWARDED ?? ""),
        String(row.PaymentReceived ?? ""),
        String(row.PaymentPosted ?? ""),
        String(row.PAID ?? ""),
        String(row.REJECTED ?? ""),
        String(row.DENIED ?? ""),
      ]),
      `${dailyFiltered.length} day(s)`,
    );
  }

  // V1 parity (ClaimStatus.js exportToCountExcel line 3796): export Daily
  // Breakdown to .xlsx via the shared exportExcel utility. Includes a Total
  // row at the bottom mirroring V1.
  function exportBreakdown() {
    const rows = dailyFiltered.map((row) => ({
      "DOS": row.DOS ?? "",
      "Day": row.DOSDay ?? "",
      "All": row.AllClaims ?? "",
      "Submitted": row.Submitted ?? "",
      "Non-sub": row.NonSubmitted ?? "",
      "Sent": row.SENT ?? "",
      "Fwd": row.FORWARDED ?? "",
      "Received": row.PaymentReceived ?? "",
      "Posted": row.PaymentPosted ?? "",
      "Paid": row.PAID ?? "",
      "Rejected": row.REJECTED ?? "",
      "Denied": row.DENIED ?? "",
    }));
    rows.push({
      "DOS": "Total",
      "Day": "",
      "All": TC.allClaims,
      "Submitted": TC.totalSubmitClaim,
      "Non-sub": TC.totalNonSubmitted,
      "Sent": TC.totalSent,
      "Fwd": TC.totalForwarded,
      "Received": TC.totalReceivedClaim,
      "Posted": TC.totalPosted,
      "Paid": TC.totalPaid,
      "Rejected": TC.totalRejected,
      "Denied": TC.totalDenied,
    });
    exportExcel(rows, "ClaimStatus-DailyBreakdown", "ClaimStatus");
  }

  // V1 parity (ClaimStatus.js exportToCountPDF line 3827): exports the
  // Daily Breakdown count table to PDF via the DataExport/PDF service.
  async function exportBreakdownPdf() {
    const headers = ["DOS", "Day", "All", "Submitted", "Non-sub", "Sent", "Fwd", "Received", "Posted", "Paid", "Rejected", "Denied"];
    const headerHtml = headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("");
    const bodyHtml = dailyFiltered.map((row) =>
      `<tr><td>${escapeHtml(row.DOS || "")}</td><td>${escapeHtml(row.DOSDay || "")}</td><td>${row.AllClaims ?? ""}</td><td>${row.Submitted ?? ""}</td><td>${row.NonSubmitted ?? ""}</td><td>${row.SENT ?? ""}</td><td>${row.FORWARDED ?? ""}</td><td>${row.PaymentReceived ?? ""}</td><td>${row.PaymentPosted ?? ""}</td><td>${row.PAID ?? ""}</td><td>${row.REJECTED ?? ""}</td><td>${row.DENIED ?? ""}</td></tr>`
    ).join("");
    const totalsHtml = `<tr style="background:#e5e7eb;font-weight:bold"><td>Total</td><td></td><td>${TC.allClaims}</td><td>${TC.totalSubmitClaim}</td><td>${TC.totalNonSubmitted}</td><td>${TC.totalSent}</td><td>${TC.totalForwarded}</td><td>${TC.totalReceivedClaim}</td><td>${TC.totalPosted}</td><td>${TC.totalPaid}</td><td>${TC.totalRejected}</td><td>${TC.totalDenied}</td></tr>`;
    const tableHtml = `<table border="1" style="width:100%;border-collapse:collapse;font-family:Arial"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}${totalsHtml}</tbody></table>`;
    const PrintableDataDetails = {
      PrintableData: tableHtml,
      PageName: "ClaimStatusDailyBreakdown",
    };
    const InputJSONLevelOne = JSON.stringify(PrintableDataDetails);
    const InputJSON = JSON.stringify(InputJSONLevelOne);
    try {
      const response = await fetch(SERVICES_URL + API.ExportPdfFile, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: InputJSON,
      });
      const data = await response.json();
      if (data && data[2]) {
        window.open(data[2], "_blank", "noopener,noreferrer");
        return;
      }
      showToast("PDF export failed", "warn");
    } catch (error) {
      console.error("exportBreakdownPdf:", error);
      showToast("PDF export failed", "warn");
    }
  }

  /* ─── API: DROPDOWNS ─── */
  async function fetchDropdowns() {
    try {
      const data = await postJSON(API.GetClaimDropDowns, userData ?? {}, BASE_URL, false);
      const dd = parseApiPayload(data)?.Data;
      if (dd?.LocationDetails?.length) {
        setLocations(dd.LocationDetails);
      } else {
        setLocations(DEMO_LOCATIONS);
      }
      // Intentionally do NOT overwrite SEARCH_FILTER_OPTIONS / STATUS_FILTER_OPTIONS
      // from the API. The original ClaimStatus.js hardcodes these in JSX; relying on
      // backend SearchByFilters/StatusRadioButtons gives mismatched values/labels and
      // ends up rendering empty dropdowns. Keep the hardcoded constants.
    } catch (e) {
      console.error("fetchDropdowns:", e);
      setLocations(DEMO_LOCATIONS);
    }
  }

  /* ─── API: CLAIM SERVICES ─── */
  async function fetchClaimServices() {
    try {
      const data = await postJSON(API.GetClaimServices, { sPracticeID: userPracticeID }, BASE_URL, false);
      const parsed = parseApiPayload(data);
      // Accept array directly, { Data: [...] }, or { d: "..." } already unwrapped
      const list = Array.isArray(parsed) ? parsed
        : Array.isArray(parsed?.Data) ? parsed.Data
          : [];
      // eslint-disable-next-line no-console
      console.log("[ClaimStatusV2] GetCPTServiceNames raw response:", data, "parsed:", parsed, "list:", list);
      setClaimServices(list);
    } catch (e) { console.error("fetchClaimServices:", e); setClaimServices([]); }
  }

  /* ─── API: PRACTICE BILLING NPIs ─── */
  async function fetchPracticeBillingNPIs() {
    try {
      const data = await postJSON(API.GetPracticeBillingNPIs, { LoggedInUserDetails: userData }, BASE_URL, false);
      const parsed = parseApiPayload(data);
      const list = Array.isArray(parsed?.Data) ? parsed.Data : [];
      setPracticeBillingNPIs(list.length > 0 ? list : []);
    } catch (e) { console.error("fetchPracticeBillingNPIs:", e); setPracticeBillingNPIs([]); }
  }

  async function fetchTransactionCodes() {
    try {
      const data = await postJSON(API.GetTransactionCode, {}, BASE_URL, false);
      const parsed = parseApiPayload(data);
      const list = Array.isArray(parsed) ? parsed : [];
      setTransactionCodes(list.length > 0 ? list : []);
    } catch (error) {
      console.error("fetchTransactionCodes:", error);
      setTransactionCodes([]);
    }
  }

  /* ─── API: SAVE ADMIN WRITE-OFF ─── */
  async function saveAdminWriteOff(claimId, amount) {
    try {
      await postJSON(API.SaveAdminWriteOff, {
        sPracticeID: userPracticeID,
        sClaimID: claimId,
        sAdminWriteOff: amount,
        LoggedInUserDetails: userData,
      });
      showToast("Admin write-off saved for " + claimId);
      fetchClaimData();
    } catch { showToast("Failed to save admin write-off", "warn"); }
  }

  /* ─── API: MAIN CLAIM DATA ─── */
  async function fetchClaimData() {
    setLoading(true);
    setHasLoaded(true);
    try {
      // Original ClaimStatus.js sends dates in YYYY-MM-DD format (see
      // getCurrentDate() at line 45). The server returns 500 if we send
      // MM/dd/yyyy. Match the original format exactly.
      const sFrom = allDates ? "2015-01-01" : (startDate ? format(startDate, "yyyy-MM-dd") : "");
      const sTo = allDates ? format(new Date(), "yyyy-MM-dd") : (endDate ? format(endDate, "yyyy-MM-dd") : "");

      const filterText = searchBy === "7" ? selectedBillingNPI : searchText;
      // Match original: send raw ServiceID values (numbers). If user hasn't checked
      // Match the original ClaimStatus.js exactly:
      //   const initialCheckedServices = claimservices.map(item => item.ServiceID)
      //   selectedServiceIDs = checkedServices.filter(id => id !== 0)
      // The empty-string entry IS included in the array — only numeric 0 is
      // filtered. Without the empty-string entry the server returns 0 claims.
      const sourceIds = selectedServiceIDs.length > 0
        ? selectedServiceIDs
        : (claimservices || [])
          .map((s) => s?.ServiceID)
          .filter((v) => v !== 0); // matches original: only filter numeric 0
      const serviceIds = sourceIds; // send as-is, preserving "" and numeric types
      const claimRequest = {
        ObjClaims: {
          iPracticeID: String(userPracticeID ?? ""),
          sFromDate: sFrom,
          sToDate: sTo,
          sFilterType: dateType,
          sPatientID: searchBy === "1" ? (selectedSearchID || sPatID) : sPatID,
          sInsuranceID: searchBy === "3" ? (selectedSearchID || sInsID) : sInsID,
          sCPT: cptInput,
          sInsorPatFilter: insOrPatFilter,
          sLocationID: locationFilter,
          sFilterText: `${filterText ?? ""}`,
          ViewType: viewType,
          // Original sends isClaimClosed raw (boolean), not stringified.
          isClosedClaims: isClaimClosed,
          sService: serviceIds,
          sCPTGroups: [],
          sClaimStatus: selectedStatusRadioButton,
          sDefaultSearch: true,
          roleID: userData?.roleID,
          isMacro: "0",
          iProviderTemplateID: userData?.iProviderTemplateID,
          DefaultTimeZoneOffSet: userData?.DefaultTimeZoneOffSet,
        },
      };

      // Diagnostic logging — leave on while we debug the empty-results problem
      console.log("[ClaimStatusV2] fetchClaimData firing with payload:", claimRequest);
      const response = await getJSONWithQuery(
        API.GetClaimStatusData,
        "sInputJSON",
        claimRequest,
        BASE_URL_BILLING,
        true, // GetClaimStatusData needs Authorization header
      );
      console.log("[ClaimStatusV2] raw response:", response);
      const parsed = parseApiPayload(response);
      console.log("[ClaimStatusV2] parsed:", parsed);
      const dataRoot = parsed?.Data ?? parsed;
      console.log("[ClaimStatusV2] dataRoot:", dataRoot);

      const rawClaims = Array.isArray(dataRoot?.ClaimDetails)
        ? dataRoot.ClaimDetails
        : Array.isArray(dataRoot?.[0])
          ? dataRoot[0]
          : Array.isArray(dataRoot)
            ? dataRoot
            : [];
      const dailyRows = Array.isArray(dataRoot?.DailyBreakdown)
        ? dataRoot.DailyBreakdown
        : Array.isArray(dataRoot?.[1])
          ? dataRoot[1]
          : [];
      const adjustments = Array.isArray(dataRoot?.AdjustmentCodes)
        ? dataRoot.AdjustmentCodes
        : Array.isArray(dataRoot?.[2])
          ? dataRoot[2]
          : [];

      setClaims(rawClaims.map((item, index) => mapApiClaim(item, index)));
      setDailyData(dailyRows);
      setAdjustmentCodeData(adjustments);
      setApiTC({
        allClaims: dailyRows.reduce((sum, item) => sum + Number(item.AllClaims ?? 0), 0),
        totalSubmitClaim: dailyRows.reduce((sum, item) => sum + Number(item.Submitted ?? 0), 0),
        totalNonSubmitted: dailyRows.reduce((sum, item) => sum + Number(item.NonSubmitted ?? 0), 0),
        totalSent: dailyRows.reduce((sum, item) => sum + Number(item.SENT ?? 0), 0),
        totalForwarded: dailyRows.reduce((sum, item) => sum + Number(item.FORWARDED ?? 0), 0),
        totalReceivedClaim: dailyRows.reduce((sum, item) => sum + Number(item.PaymentReceived ?? 0), 0),
        totalPosted: dailyRows.reduce((sum, item) => sum + Number(item.PaymentPosted ?? 0), 0),
        totalPaid: dailyRows.reduce((sum, item) => sum + Number(item.PAID ?? 0), 0),
        totalRejected: dailyRows.reduce((sum, item) => sum + Number(item.REJECTED ?? 0), 0),
        totalDenied: dailyRows.reduce((sum, item) => sum + Number(item.DENIED ?? 0), 0),
      });
      setApiTotals({
        totalBilled: rawClaims.reduce((sum, item) => sum + toNum(item.totalcharge), 0),
        totalReceived: rawClaims.reduce((sum, item) => sum + toNum(item.recvdCharge), 0),
        totalBalance: rawClaims.reduce((sum, item) => sum + toNum(item.claimbln), 0),
      });
    } catch (e) {
      console.error("fetchClaimData:", e);
      setClaims([]);
      setDailyData([]);
      setApiTC({
        allClaims: 0, totalSubmitClaim: 0, totalNonSubmitted: 0,
        totalSent: 0, totalForwarded: 0, totalReceivedClaim: 0,
        totalPosted: 0, totalPaid: 0, totalRejected: 0, totalDenied: 0,
      });
      setApiTotals({ totalBilled: 0, totalReceived: 0, totalBalance: 0 });
    } finally {
      setLoading(false);
    }
  }

  /* ─── API: SAVE PAYMENT DATA (cheque #, admin w/o) ─── */
  async function savePayments() {
    try {
      await postJSON(API.SavePaymentData, {
        sPracticeID: userPracticeID, LoggedInUserDetails: userData,
        sClaimIDs: selectedRows.join(","),
      });
      showToast("Payment changes saved successfully");
      setPaymentsEdited(false);
    } catch { showToast("Failed to save payments", "warn"); }
  }

  /* ─── API: SAVE NOTES ─── */
  async function saveClaimNotes(claimId, notes) {
    try {
      await postJSON(API.SaveClaimNotes, {
        sPracticeID: userPracticeID, sClaimID: claimId, sNotes: notes, LoggedInUserDetails: userData,
      });
      showToast("Notes saved for " + claimId);
    } catch { showToast("Failed to save notes", "warn"); }
  }

  /* ─── API: CLOSE CLAIM(S) ─── */
  async function closeSelectedClaims() {
    try {
      await postJSON(API.CloseClaimsData, {
        sPracticeID: userPracticeID, sClaimIDs: selectedRows.join(","), LoggedInUserDetails: userData,
      });
      showToast(`${selectedRows.length} claim(s) closed`, "warn");
      setSelectedRows([]);
      fetchClaimData();
    } catch { showToast("Failed to close claims", "warn"); }
  }

  /* Resubmit handler removed — V1 has no resubmit API. See
     resubmitSelectedClaimsLegacy below: it just adds rows to the
     selection set and tells the user to finish in IntelliBill. */

  /* ─── API: CHECK ELIGIBILITY ─── */
  async function checkEligibility(claimId, patientName) {
    try {
      await postJSON(API.CheckEligibility, {
        sPracticeID: userPracticeID, sClaimID: claimId, LoggedInUserDetails: userData,
      });
      showToast("Eligibility check started for " + patientName);
    } catch { showToast("Eligibility check failed", "warn"); }
  }

  async function saveAdjustmentLegacy(claim, adjustmentValue, silent = false) {
    await postDoubleEncodedJSON(API.SaveAdjustmentCharges, {
      objClaim: {
        ClaimID: claim.id,
        PracticeID: userPracticeID,
        PatientID: claim.patientId,
        CPT: claim.cpt,
        PostingCharges: adjustmentValue,
        DOS: formatDateForApi(claim.dos),
        PriorSec: claim.priorSecClaim,
        UpdatedBy: userData?.userID,
      },
    });
    updateClaimLocal(claim.rowKey, { contractual: adjustmentValue });
    if (!silent) {
      showToast(`Adjustment saved for ${claim.id}`);
    }
  }

  async function saveMoneyFieldLegacy(
    claim,
    field,
    rawValue,
    endpoint,
    extraPayload = {},
    options = {},
  ) {
    const value = rawValue.trim() === "" ? 0 : parseFloat(rawValue);
    if (Number.isNaN(value)) {
      showToast("Please enter a valid amount", "warn");
      return;
    }

    await postDoubleEncodedJSON(endpoint, {
      objClaim: {
        ClaimID: claim.id,
        PracticeID: userPracticeID,
        PatientID: claim.patientId,
        CPT: claim.cpt,
        PostingCharges: value,
        DOS: formatDateForApi(claim.dos),
        PriorSec: options.priorSec ?? claim.priorSecClaim,
        UpdatedBy: userData?.userID,
        ...extraPayload,
      },
    });

    let patch = {};
    switch (field) {
      case "paid":
        patch = { paid: value };
        break;
      case "secPaid":
        patch = { secPaid: value };
        break;
      case "contractual":
        patch = { contractual: value };
        break;
      case "secPendingAmt":
        patch = { secPendingAmt: value };
        break;
      case "deductible":
        patch = { deductible: value };
        break;
      case "patRes":
        patch = { patRes: value };
        break;
      case "copay":
        patch = { copay: value };
        break;
      case "otherAdj":
        patch = { otherAdj: value };
        break;
      case "federalPayment":
        patch = { federalPayment: value };
        break;
      case "selfPay":
        patch = { selfPay: value };
        break;
      case "writeOff":
        patch = { writeOff: value };
        break;
      case "adminWriteOff":
        patch = { adminWriteOff: value };
        break;
      default:
        patch = {};
        break;
    }

    const totalsPatch = recalculateClaimAmounts(claim, patch);
    updateClaimLocal(claim.rowKey, { ...patch, ...totalsPatch });
    clearDraftValue(claim.rowKey, field);

    if (options.saveAdjustmentAfter && field !== "contractual") {
      await saveAdjustmentLegacy({ ...claim, ...patch, ...totalsPatch }, totalsPatch.contractual, true);
    }

    if (options.successMessage) {
      showToast(options.successMessage);
    }
  }

  async function saveTextFieldLegacy(
    claim,
    field,
    rawValue,
    endpoint,
    payloadBuilder,
    successMessage,
  ) {
    await postDoubleEncodedJSON(endpoint, payloadBuilder(rawValue));
    const patch =
      field === "checkNumPri"
        ? { checkNumPri: rawValue }
        : field === "checkNumSec"
          ? { checkNumSec: rawValue }
          : field === "payerRefPri"
            ? { payerRefPri: rawValue }
            : field === "payerRefSec"
              ? { payerRefSec: rawValue }
              : field === "checkDatePri"
                ? { checkDatePri: rawValue }
                : { checkDateSec: rawValue };
    updateClaimLocal(claim.rowKey, patch);
    clearDraftValue(claim.rowKey, field);
    showToast(successMessage);
  }

  async function saveClaimDraftsLegacy() {
    try {
      const entries = Object.entries(draftInputs);
      for (const [rowKey, rowDraft] of entries) {
        const claim = claims.find((item) => item.rowKey === rowKey);
        if (!claim) continue;

        for (const [field, value] of Object.entries(rowDraft)) {
          if (value == null) continue;
          if (field === "paid") {
            await saveMoneyFieldLegacy(claim, field, value, API.CPTReceviedCharge, {}, { priorSec: 1, saveAdjustmentAfter: true });
          } else if (field === "secPaid") {
            await saveMoneyFieldLegacy(claim, field, value, API.CPTReceviedCharge, {}, { priorSec: 2, saveAdjustmentAfter: true });
          } else if (field === "contractual") {
            await saveAdjustmentLegacy(claim, parseFloat(value) || 0, true);
            clearDraftValue(rowKey, field);
          } else if (field === "secPendingAmt") {
            await saveMoneyFieldLegacy(claim, field, value, API.SaveSecPendingCharges, {}, { successMessage: `Secondary pending updated for ${claim.id}` });
          } else if (field === "deductible") {
            await saveMoneyFieldLegacy(claim, field, value, API.UpdateClaimDeductible, {}, { saveAdjustmentAfter: true });
          } else if (field === "patRes") {
            await saveMoneyFieldLegacy(claim, field, value, API.UpdateClaimPatientResponsibility, {}, { saveAdjustmentAfter: true });
          } else if (field === "copay") {
            await saveCopayLegacy(claim, value);
          } else if (field === "otherAdj") {
            await saveMoneyFieldLegacy(claim, field, value, API.SaveOtherAdjustment, {}, { saveAdjustmentAfter: true });
          } else if (field === "federalPayment") {
            await saveMoneyFieldLegacy(claim, field, value, API.SaveFederalReduction, {}, { saveAdjustmentAfter: true });
          }
          else if (field === "selfPay") {
            await saveMoneyFieldLegacy(claim, field, value, API.SaveSelfPay, {}, { saveAdjustmentAfter: true });
          } else if (field === "writeOff") {
            await saveMoneyFieldLegacy(claim, field, value, API.UpdateWriteoff, {}, { saveAdjustmentAfter: true });
          } else if (field === "checkNumPri") {
            await saveTextFieldLegacy(claim, field, value, API.UpdateCheckNumber, (next) => ({
              objClaim: {
                ClaimID: claim.id,
                PracticeID: userPracticeID,
                PatientID: claim.patientId,
                CPT: claim.cpt,
                ChequeNumber: next,
                DOS: formatDateForApi(claim.dos),
                UpdatedBy: userData?.userID,
                PriorSec: 1,
                IsLineItemData: 1,
              },
            }), `Primary cheque updated for ${claim.id}`);
          } else if (field === "checkDatePri") {
            const formatted = formatDateForApi(value);
            await saveTextFieldLegacy(claim, field, formatted, API.UpdateCheckDate, (next) => ({
              objClaim: {
                ClaimID: claim.id,
                PracticeID: userPracticeID,
                PatientID: claim.patientId,
                CPT: claim.cpt,
                ChequeDate: next,
                DOS: formatDateForApi(claim.dos),
                UpdatedBy: userData?.userID,
                PriorSec: 1,
                IsLineItemData: 1,
              },
            }), `Primary cheque date updated for ${claim.id}`);
          } else if (field === "payerRefPri") {
            await saveTextFieldLegacy(claim, field, value, API.UpdateClaimReferenceNumber, (next) => ({
              objClaim: {
                ClaimID: claim.id,
                PracticeID: userPracticeID,
                PatientID: claim.patientId,
                DOS: formatDateForApi(claim.dos),
                UpdatedBy: userData?.userID,
                PriorSec: 1,
                PayerReferenceNumber: next,
              },
            }), `Primary payer reference updated for ${claim.id}`);
          } else if (field === "checkNumSec") {
            await saveTextFieldLegacy(claim, field, value, API.UpdateCheckNumber, (next) => ({
              objClaim: {
                ClaimID: claim.id,
                PracticeID: userPracticeID,
                PatientID: claim.patientId,
                CPT: claim.cpt,
                ChequeNumber: next,
                DOS: formatDateForApi(claim.dos),
                UpdatedBy: userData?.userID,
                PriorSec: 2,
                IsLineItemData: 1,
              },
            }), `Secondary cheque updated for ${claim.id}`);
          } else if (field === "checkDateSec") {
            const formatted = formatDateForApi(value);
            await saveTextFieldLegacy(claim, field, formatted, API.UpdateCheckDate, (next) => ({
              objClaim: {
                ClaimID: claim.id,
                PracticeID: userPracticeID,
                PatientID: claim.patientId,
                CPT: claim.cpt,
                ChequeDate: next,
                DOS: formatDateForApi(claim.dos),
                UpdatedBy: userData?.userID,
                PriorSec: 2,
                IsLineItemData: 1,
              },
            }), `Secondary cheque date updated for ${claim.id}`);
          } else if (field === "payerRefSec") {
            await saveTextFieldLegacy(claim, field, value, API.UpdateClaimReferenceNumber, (next) => ({
              objClaim: {
                ClaimID: claim.id,
                PracticeID: userPracticeID,
                PatientID: claim.patientId,
                DOS: formatDateForApi(claim.dos),
                UpdatedBy: userData?.userID,
                PriorSec: 2,
                PayerReferenceNumber: next,
              },
            }), `Secondary payer reference updated for ${claim.id}`);
          }
        }
      }

      setPaymentsEdited(false);
      showToast("Claim updates saved successfully");
    } catch (error) {
      console.error("saveClaimDraftsLegacy:", error);
      showToast("Failed to save claim updates", "warn");
    }
  }

  // Copay inline save — raw fetch mirroring original SavePatientCopay exactly.
  // The shared saveMoneyFieldLegacy helper was returning 401 for this endpoint;
  // mirroring the original (raw fetch, item fields, string PostingCharges) avoids that.
  async function saveCopayLegacy(claim, rawValue) {
    const item = claim.raw || {};
    const objClaim = {
      ClaimID: item.ClaimId,
      PracticeID: userPracticeID,
      PatientID: item.PatientID,
      CPT: item.CPTOld,
      PostingCharges: rawValue,                       // string, like original
      CopayType: "CASH",
      DOS: formatDOSDateLegacy(item.DOS || item.ClaimDOS),
      PriorSec: item.PriorSecClaim,
      UpdatedBy: userData?.userID,
    };
    const myInput = JSON.stringify(JSON.stringify({ objClaim: objClaim }));
    try {
      const response = await fetch(
        BASE_URL_BILLING + API.UpdateCopayData,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${sAuthToken}`,
          },
          body: myInput,
        },
      );
      if (!response.ok) throw new Error(`Copay returned ${response.status}`);
      const value = rawValue === "" ? 0 : parseFloat(rawValue) || 0;
      updateClaimLocal(claim.rowKey, { copay: value });
      clearDraftValue(claim.rowKey, "copay");
      showToast(`Copay updated for ${claim.id}`);
    } catch (error) {
      console.error("saveCopayLegacy:", error);
      showToast("Failed to update copay", "warn");
    }
  }

  // Small helper that formats a date the same way the original ClaimStatus uses
  // (en-US locale 2-digit MM/DD/YYYY).
  function formatDOSDateLegacy(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
  }

  async function saveClaimNotesLegacy(claim, notes) {
    try {
      await postDoubleEncodedJSON(API.UpdateClaimNotes, {
        objClaim: {
          ClaimID: claim.id,
          PracticeID: userPracticeID,
          PatientID: claim.patientId,
          DOS: formatDateForApi(claim.dos),
          UpdatedBy: userData?.userID,
          ClaimNotes: notes,
          FirstName: claim.raw.FirstName,
          LastName: claim.raw.LastName,
        },
      });
      updateClaimLocal(claim.rowKey, { notes });
      showToast(`Notes saved for ${claim.id}`);
    } catch {
      showToast("Failed to save notes", "warn");
    }
  }

  // V1 parity (ClaimStatus.js handleDelete line 2462): removes the selected
  // rows from the local table state (no server call — matches V1) and shows
  // a brief success modal that auto-dismisses after 5s.
  function handleOpenDeleteModal() {
    if (selectedRows.length === 0) {
      showToast("Please select at least one claim to delete", "warn");
      return;
    }
    setShowDeleteConfirm(true);
  }
  function handleDeleteSelected() {
    setClaims((prev) => prev.filter((c) => !selectedRows.includes(c.rowKey)));
    setDeleteRowCount(selectedRows.length);
    setSelectedRows([]);
    setShowDeleteConfirm(false);
    setShowDeleteSuccess(true);
    setTimeout(() => setShowDeleteSuccess(false), 5000);
  }

  async function closeClaimsWithCptLegacy(rowKeys = selectedRows) {
    const claimsToClose = claims.filter((claim) => rowKeys.includes(claim.rowKey));
    if (claimsToClose.length === 0) {
      showToast("Please select at least one claim", "warn");
      return;
    }
    try {
      await postJSON(API.CloseClaimWithCPT, {
        JsonDataArr: claimsToClose.map((claim) => ({
          ClaimID: claim.id,
          PracticeID: userPracticeID,
          iIsCompClaim: 0,
          CPTcode: claim.cpt,
          DOS: formatDateForApi(claim.dos),
        })),
        LoggedInUserDetails: userData,
      }, BASE_URL, false);
      showToast(`${claimsToClose.length} claim(s) closed`);
      setSelectedRows([]);
      fetchClaimData();
    } catch (error) {
      console.error("closeClaimsWithCptLegacy:", error);
      showToast("Failed to close claims", "warn");
    }
  }

  // V1 parity (ClaimStatus.js 217, 2399-2423): V1's Resubmit column is
  // SELECTION-ONLY — checking the box adds the row to selectedResubmitRows
  // but never fires a fetch. The actual resubmission happens on a separate
  // page (IntelliBill / Workable Claims). To match that exactly, V2 just
  // toggles selection state and shows a confirmation toast — no network
  // call. (Earlier V2 versions called UpdateSelectedClaims.asmx which is
  // the IntelliBill save endpoint, not a resubmit endpoint.)
  function resubmitSelectedClaimsLegacy(rowKeys) {
    const claimsToResubmit = claims.filter((claim) => rowKeys.includes(claim.rowKey));
    if (claimsToResubmit.length === 0) {
      showToast("Please select at least one claim", "warn");
      return;
    }
    // Add the rows to the selection set so the user can review them, then
    // direct them to IntelliBill / Workable Claims to actually resubmit.
    setSelectedRows((prev) => Array.from(new Set([...prev, ...rowKeys])));
    showToast(
      `${claimsToResubmit.length} claim(s) marked for resubmission — open IntelliBill or Workable Claims to submit`,
    );
  }

  async function saveAdminWriteOffBatchLegacy() {
    const claimsToWriteOff = claims.filter((claim) => selectedRows.includes(claim.rowKey));
    if (claimsToWriteOff.length === 0) {
      showToast("Please select at least one claim", "warn");
      return;
    }
    try {
      await postDoubleEncodedJSON(API.SaveClaimCPTAdminWriteOffCharges, {
        objClaims: claimsToWriteOff.map((claim) => ({
          ClaimID: claim.id,
          PostingCharges: parseFloat(
            getDraftValue(
              claim,
              "adminWriteOff",
              claim.adminWriteOff ? String(claim.adminWriteOff) : "0",
            ),
          ) || 0,
          PracticeID: userPracticeID,
          CPT: claim.cpt,
          DOS: formatDateForApi(claim.dos),
          UpdatedBy: userData?.userID,
          TransactionCode: selectedTransactionCode,
        })),
      });
      setShowAdminWriteModal(false);
      setPaymentsEdited(false);
      showToast("Admin write-off saved");
      fetchClaimData();
    } catch (error) {
      console.error("saveAdminWriteOffBatchLegacy:", error);
      showToast("Failed to save admin write-off", "warn");
    }
  }

  async function checkEligibilityLegacy(rowKey, patientName) {
    const claim = claims.find((item) => item.rowKey === rowKey);
    if (!claim) return;
    try {
      const response = await getJSONWithQuery(
        API.GetPatientEligibility,
        "sInputJSON",
        {
          objPatientDetails: {
            PatientID: claim.patientId,
            PracticeID: userPracticeID,
            PolicyNumber: `${claim.policyNumber ?? ""}`,
            DOS: formatDateForApi(claim.dos),
            RowIndex: 0,
          },
        },
        PATIENT_PORTAL_URL,
        false,
      );
      const parsed = parseApiPayload(response);
      const status = parsed?.InsStatus || parsed?.Data?.InsStatus || "requested";
      showToast(`Eligibility for ${patientName}: ${status}`);
    } catch (error) {
      console.error("checkEligibilityLegacy:", error);
      showToast("Eligibility check failed", "warn");
    }
  }

  // Secondary insurance eligibility check. Mirrors original handleSecCheckeligibility
  // (line 3544 of ClaimStatus.js) — uses CheckPatientEligibility endpoint and
  // sends secondary payer / insured fields.
  async function checkSecEligibilityLegacy(rowKey, patientName) {
    const claim = claims.find((item) => item.rowKey === rowKey);
    if (!claim) return;
    const item = claim.raw || {};
    const eligibilityDOS = formatDateForApi(claim.dos);
    const inputStruct = {
      PayerID: `${item.SecPayerID ?? ""}`,
      sIns_PolicyNumber: `${claim.secPolicyNumber ?? claim.policyNumber ?? ""}`,
      patientID: claim.patientId,
      sInsuredFName: item.FirstName ?? "",
      sInsuredLName: item.LastName ?? "",
      sInsuredStateName: `${item.InsuredStateName ?? ""}`,
      sInsuredDOB: claim.dob ? formatDateForApi(claim.dob) : "",
      sInsCoID: `${item.PrimaryInsurance ?? ""}`,
      EligibilityDOS: eligibilityDOS,
      EligibilityRowIndex: 0,
      LoggedInUserDetails: userData,
    };
    try {
      const data = await postJSON(API.CheckPatientEligibility, inputStruct);
      const parsed = parseApiPayload(data);
      const status =
        (Array.isArray(parsed) ? parsed[0]?.InsStatus : parsed?.InsStatus) ||
        parsed?.Data?.InsStatus ||
        "requested";
      showToast(`Sec. eligibility for ${patientName}: ${status}`);
    } catch (error) {
      console.error("checkSecEligibilityLegacy:", error);
      showToast("Secondary eligibility check failed", "warn");
    }
  }

  // Batch eligibility: iterate the rows the user checked in the Insurance tab
  // and call the single-row helpers for each. The original UI re-uses the same
  // request pattern; we do it row-by-row so each row's toast/status applies.
  async function runBatchPriEligibility() {
    if (eligPriRows.length === 0) {
      showToast("Select at least one row for primary eligibility", "warn");
      return;
    }
    showToast(`Checking primary eligibility for ${eligPriRows.length} claim(s)…`);
    for (const rowKey of eligPriRows) {
      const claim = claims.find((item) => item.rowKey === rowKey);
      if (!claim) continue;
      // eslint-disable-next-line no-await-in-loop
      await checkEligibilityLegacy(rowKey, claim.patient);
    }
  }
  async function runBatchSecEligibility() {
    if (eligSecRows.length === 0) {
      showToast("Select at least one row for secondary eligibility", "warn");
      return;
    }
    showToast(`Checking secondary eligibility for ${eligSecRows.length} claim(s)…`);
    for (const rowKey of eligSecRows) {
      const claim = claims.find((item) => item.rowKey === rowKey);
      if (!claim) continue;
      // eslint-disable-next-line no-await-in-loop
      await checkSecEligibilityLegacy(rowKey, claim.patient);
    }
  }

  async function fetchClaimAttachmentsLegacy(claim) {
    try {
      const response = await getJSONWithQuery(
        API.GetClaimAttachments,
        "sInputJSON",
        { ClaimID: claim.id, PracticeID: userPracticeID },
        BASE_URL_BILLING,
        false,
      );
      const parsed = parseApiPayload(response);
      setAttachmentData(Array.isArray(parsed) ? parsed : []);
      setAttachmentClaimId(claim.id);
      setShowAttachmentModal(true);
    } catch (error) {
      console.error("fetchClaimAttachmentsLegacy:", error);
      setAttachmentData([]);
      setAttachmentClaimId(claim.id);
      setShowAttachmentModal(true);
    }
  }

  async function fetchClaimEventsLegacy(claim) {
    try {
      const response = await getJSONWithQuery(
        API.GetClaimAllEvents,
        "sJSONInput",
        {
          PracticeID: userPracticeID,
          ModuleName: "Billing",
          dtFromDate: startDate ? format(startDate, "MM/dd/yyyy") : "",
          dtToDate: endDate ? format(endDate, "MM/dd/yyyy") : "",
          DataID: claim.id,
          UserID: 0,
          DateType: "DOS",
        },
        COMMON_BASE_URL,
        false,
      );
      const parsed = parseApiPayload(response);
      setEventData(Array.isArray(parsed?.Data) ? parsed.Data : []);
      setEventClaimId(claim.id);
      setShowEventsModal(true);
    } catch (error) {
      console.error("fetchClaimEventsLegacy:", error);
      setEventData([]);
      setEventClaimId(claim.id);
      setShowEventsModal(true);
    }
  }

  // Mirrors GetPDFCMS in ClaimStatus.js (line 4191). Uses raw fetch so the URL
  // and headers exactly match the old UI's request.
  async function openCmsPdfLegacy(claim) {
    const item = claim.raw || {};
    const claimFilters = {
      PracticeID: userPracticeID,
      ClaimID: item.ClaimId ?? claim.id,
      PatientID: item.PatientID ?? claim.patientId,
    };
    const sInputJSON = JSON.stringify(claimFilters);
    const url = `${BASE_URL_BILLING}${API.GetPDFCMS1500}?sInputJSON=${sInputJSON}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      const docUrl = parsed?.DocumentURL ?? parsed?.Data?.DocumentURL;
      if (docUrl) {
        window.open(docUrl, "_blank", "noopener,noreferrer");
        return;
      }
      showToast("CMS PDF is not available for this claim", "warn");
    } catch (error) {
      console.error("openCmsPdfLegacy:", error);
      showToast("Failed to open CMS PDF", "warn");
    }
  }

  /* ─── PORTED FLOWS: handlers ─── */

  function openVerifyModalLegacy(claim) {
    if (claim.isVerified) {
      showToast(`Claim ${claim.id} is already verified`, "warn");
      return;
    }
    setVerifyClaim(claim);
    setVerifyComment("");
    setShowVerifyModal(true);
    setOpenRowMenu(null);
  }

  async function saveVerifiedDataLegacy() {
    if (!verifyClaim) return;
    setVerifySaving(true);
    try {
      const verifiedByName =
        userFullName ||
        userData?.userFullName ||
        userData?.shortName ||
        "Verified";
      const verifiedOn = format(new Date(), "MM/dd/yyyy");
      await postJSON(API.SaveVerifiedData, {
        sPracticeID: userPracticeID,
        sClaimID: verifyClaim.id,
        Comments: verifyComment,
        DOS: formatDateForApi(verifyClaim.dos),
        CPTCode: verifyClaim.cpt,
        LoggedInUserDetails: userData,
      });
      updateClaimLocal(verifyClaim.rowKey, {
        isVerified: true,
        verifiedBy: verifiedByName,
        verifiedOn,
        verificationComment: verifyComment,
        raw: {
          ...(verifyClaim.raw || {}),
          isVerified: true,
          verifiedBy: verifiedByName,
          verifiedOn,
          VerificationComment: verifyComment,
        },
      });
      showToast(`Claim ${verifyClaim.id} verified`);
      setShowVerifyModal(false);
      setVerifyClaim(null);
      setVerifyComment("");

    } catch (error) {
      console.error("saveVerifiedDataLegacy:", error);
      showToast("Failed to save verification", "warn");
    } finally {
      setVerifySaving(false);
    }
  }

  function openCopayModalLegacy(claim) {
    setCopayClaim(claim);
    setCopayInput(claim.copayAmount ? String(claim.copayAmount) : (claim.copay ? String(claim.copay) : ""));
    setShowCopayModal(true);
    setOpenRowMenu(null);
  }

  // Mirrors the full old-UI flow: SavePatientCopay (line 2920) first persists the
  // amount, then handleApplyCopayData (line 4344) calls ApplyCopay. The old UI
  // assumed the user saved via the inline cell before clicking Apply, but the
  // V2 modal lets them type directly — so we run both steps here.
  async function applyCopayLegacy() {
    if (!copayClaim) return;
    const item = copayClaim.raw || {};
    const amount = (copayInput && copayInput.trim() !== "") ? copayInput : String(item.CopayAmount ?? "");
    if (!amount || amount.trim() === "") {
      showToast("Enter a copay amount", "warn");
      return;
    }
    setCopaySaving(true);
    try {
      // Step 1 — persist the copay value (equivalent to inline SavePatientCopay).
      const saveStruct = {
        ClaimID: item.ClaimId,
        PracticeID: userPracticeID,
        PatientID: item.PatientID,
        CPT: item.CPTOld,
        PostingCharges: amount,
        CopayType: "CASH",
        DOS: formatDOSDateLegacy(item.DOS || item.ClaimDOS),
        PriorSec: item.PriorSecClaim,
        UpdatedBy: userData?.userID,
      };
      const saveBody = JSON.stringify(JSON.stringify({ objClaim: saveStruct }));
      const saveResponse = await fetch(
        BASE_URL_BILLING + API.UpdateCopayData,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${sAuthToken}`,
          },
          body: saveBody,
        },
      );
      if (!saveResponse.ok) {
        throw new Error(`UpdateCopayData returned ${saveResponse.status}`);
      }
      // Step 2 — apply the now-persisted copay.
      const applyStruct = {
        ClaimID: item.ClaimId,
        PostingCharges: amount,
        PracticeID: userPracticeID,
        CPT: item.CPTOld,
        DOS: formatDateForApi(item.ClaimDOS),
        PatientID: item.PatientID,
        UpdatedBy: userData?.userID,
        CopayType: "Cash",
      };
      const applyBody = JSON.stringify(JSON.stringify({ objClaim: applyStruct }));
      const applyResponse = await fetch(
        BASE_URL_BILLING + API.ApplyCopay,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${sAuthToken}`,
          },
          body: applyBody,
        },
      );
      if (!applyResponse.ok) {
        throw new Error(`ApplyCopay returned ${applyResponse.status}`);
      }
      updateClaimLocal(copayClaim.rowKey, {
        copay: toNum(amount),
        copayAmount: toNum(amount),
        copayAppliedClaim: copayClaim.id,
        copayAppliedByShortName: userData?.shortName ?? userFullName,
        copayAppliedOn: format(new Date(), "MM/dd/yyyy"),
      });
      showToast(`Copay amount ${amount} applied to claim ${copayClaim.id}`);
      setShowCopayModal(false);
      setCopayClaim(null);
      setCopayInput("");
    } catch (error) {
      console.error("applyCopayLegacy:", error);
      showToast("Failed to apply copay", "warn");
    } finally {
      setCopaySaving(false);
    }
  }

  async function regenerateCmsPdfLegacy(claim) {
    setRegenLoadingKey(claim.rowKey);
    setOpenRowMenu(null);
    try {
      await postDoubleEncodedJSON(API.SaveRegeneratePDFCMS1500, {
        objClaim: {
          PatientID: claim.patientId,
          ClaimID: claim.id,
          PracticeID: userPracticeID,
          PriorSecClaim: claim.priorSecClaim,
          UserTimeZoneOffSet: 300,
          UserID: userId,
        },
      });
      showToast(`CMS1500 regenerated for ${claim.id}`);
    } catch (error) {
      console.error("regenerateCmsPdfLegacy:", error);
      showToast("Failed to regenerate CMS1500", "warn");
    } finally {
      setRegenLoadingKey("");
    }
  }

  // Mirrors GetProviderPDF in ClaimStatus.js (line 4270). Uses raw fetch with the
  // exact URL/headers/body shape the old UI uses so we don't risk helper-level
  // transformations breaking the request.
  async function openCptDocumentLegacy(claim) {
    setOpenRowMenu(null);
    const item = claim.raw || {};
    const claimFilters = {
      PracticeID: userPracticeID,
      PatientID: item.PatientID ?? claim.patientId,
      DOS: formatDateForApi(item.ClaimDOS ?? claim.dos),
      CPT: item.CPTOld ?? claim.cpt,
    };
    const sInputJSON = JSON.stringify({ objPatDetails: claimFilters });
    const url = `${PATIENT_PORTAL_URL}${API.CPTDocument}?sInputJSON=${sInputJSON}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${sAuthToken}`,
        },
      });
      const data = await response.json();
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      const docUrl = parsed?.DocumentURL ?? parsed?.Data?.DocumentURL;
      if (docUrl) {
        window.open(docUrl, "_blank", "noopener,noreferrer");
        return;
      }
      showToast("CPT document not available for this claim", "warn");
    } catch (error) {
      console.error("openCptDocumentLegacy:", error);
      showToast("Failed to open CPT document", "warn");
    }
  }

  async function fetchClaimSubmitHxLegacy(claim) {
    setSubmitHxClaim(claim);
    setOpenRowMenu(null);
    try {
      const indicator = (claim.claimHx || "").split("$")[1] ?? "";
      const response = await getJSONWithQuery(
        API.GetClaimSubmitHx,
        "sInputJSON",
        {
          iPracticeID: userPracticeID,
          sClaimID: claim.id,
          ClaimHxIndicator: indicator,
          ClaimPriOrSec: claim.priorSecClaim,
        },
        BASE_URL_BILLING,
        false,
      );
      const parsed = parseApiPayload(response);
      const list = Array.isArray(parsed?.Data) ? parsed.Data : Array.isArray(parsed) ? parsed : [];
      setSubmitHxData(list);
      setShowSubmitHxModal(true);
    } catch (error) {
      console.error("fetchClaimSubmitHxLegacy:", error);
      setSubmitHxData([]);
      setShowSubmitHxModal(true);
    }
  }

  // Load claim history (events + submit history) into inline drawer state.
  // Used by the drawer's History tab — does NOT open the modal versions.
  async function loadDrawerHistoryLegacy(claim) {
    if (!claim) return;
    setDrawerHistoryClaimId(claim.id);
    setDrawerHistoryLoading(true);
    try {
      const [eventsResp, submitHxResp] = await Promise.all([
        getJSONWithQuery(
          API.GetClaimAllEvents,
          "sJSONInput",
          {
            PracticeID: userPracticeID,
            ModuleName: "Billing",
            dtFromDate: startDate ? format(startDate, "MM/dd/yyyy") : "",
            dtToDate: endDate ? format(endDate, "MM/dd/yyyy") : "",
            DataID: claim.id,
            UserID: 0,
            DateType: "DOS",
          },
          COMMON_BASE_URL,
          false,
        ).catch(() => null),
        getJSONWithQuery(
          API.GetClaimSubmitHx,
          "sInputJSON",
          {
            iPracticeID: userPracticeID,
            sClaimID: claim.id,
            ClaimHxIndicator: (claim.claimHx || "").split("$")[1] ?? "",
            ClaimPriOrSec: claim.priorSecClaim,
          },
          BASE_URL_BILLING,
          false,
        ).catch(() => null),
      ]);
      const parsedEvents = parseApiPayload(eventsResp);
      const eventsList = Array.isArray(parsedEvents?.Data)
        ? parsedEvents.Data
        : Array.isArray(parsedEvents) ? parsedEvents : [];
      const parsedSubmitHx = parseApiPayload(submitHxResp);
      const submitHxList = Array.isArray(parsedSubmitHx?.Data)
        ? parsedSubmitHx.Data
        : Array.isArray(parsedSubmitHx) ? parsedSubmitHx : [];
      setDrawerHistoryEvents(eventsList);
      setDrawerHistorySubmitHx(submitHxList);
    } catch (error) {
      console.error("loadDrawerHistoryLegacy:", error);
      setDrawerHistoryEvents([]);
      setDrawerHistorySubmitHx([]);
    } finally {
      setDrawerHistoryLoading(false);
    }
  }

  async function fetchProvidersList() {
    try {
      const data = await postJSON(
        API.GetIntelliProvidersNames,
        { sPracticeID: userPracticeID, SearchKey: "" },
        BASE_URL,
        false,
      );
      const parsed = parseApiPayload(data);
      const list = Array.isArray(parsed?.Data) ? parsed.Data : Array.isArray(parsed) ? parsed : [];
      setProviderList(list);
    } catch (error) {
      console.error("fetchProvidersList:", error);
      setProviderList([]);
    }
  }

  /* ─── DERIVED / FILTERED DATA ─── */
  const filtered = useMemo(() => {
    let rows = [...claims];
    const normalizedStatusTab = normalizeStatusTab(activeStatusTab);
    if (normalizedStatusTab !== "all") {
      rows = rows.filter((claim) => claimMatchesStatusTab(claim, normalizedStatusTab));
    }
    // V1 parity: status column header mini-buttons filter on raw
    // ClaimStatusFlag (separate axis from the tab pills above). N maps to
    // Pending, D=Denied, P=Paid, F=Forwarded, R=Rejected, S=Sent,
    // P0=Posted with $0 received, S0=secondary outstanding.
    if (statusFlagFilter) {
      rows = rows.filter(c => {
        const flag = removeHtmlTags(c.raw?.ClaimStatusFlag ?? "").toUpperCase();
        switch (statusFlagFilter) {
          case "N": return flag === "N" || c.status === "Pending";
          case "D": return flag === "D" || c.status === "Denied";
          case "P": return flag === "P" || c.status === "Paid";
          case "F": return flag === "F" || c.status === "Forwarded";
          case "R": return flag === "R" || c.status === "Rejected";
          case "S": return flag === "S" || c.status === "Sent";
          case "P0": return flag === "P0";
          case "S0": return flag === "S0";
          default: return true;
        }
      });
    }
    // Daily-breakdown drill-down: filter by clicked DOS row
    if (dosFilter) { rows = rows.filter(c => { const d = parseApiDate(c.dos); const f = parseApiDate(dosFilter); return d && f && d === f; }); }
    const cfMap = {
      patient: (c, v) => c.patient.toLowerCase().includes(v.toLowerCase()),
      claimId: (c, v) => c.id.toLowerCase().includes(v.toLowerCase()),
      mrn: (c, v) => (c.mrn || "").toLowerCase().includes(v.toLowerCase()),
      dob: (c, v) => ((c.dob || "") + " " + formatDOSDate(c.dob)).toLowerCase().includes(v.toLowerCase()),
      dos: (c, v) => formatDOSDate(c.dos).toLowerCase().includes(v.toLowerCase()),
      bhsud: (c, v) => c.conType.toLowerCase().includes(v.toLowerCase()),
      location: (c, v) => (c.location || "").toLowerCase().includes(v.toLowerCase()),
      insurance: (c, v) => c.payer.toLowerCase().includes(v.toLowerCase()),
      insPri: (c, v) => c.payer.toLowerCase().includes(v.toLowerCase()),
      submitDatePri: (c, v) => (c.submittedOn || "").toLowerCase().includes(v.toLowerCase()) || formatDOSDate(c.submittedOn).toLowerCase().includes(v.toLowerCase()),
      insSec: (c, v) => c.secPayer.toLowerCase().includes(v.toLowerCase()),
      secSubmitDate: (c, v) => ((c.secSubmitDate || "") + " " + fmtDate(c.secSubmitDate)).toLowerCase().includes(v.toLowerCase()),
      clearingHouse: (c, v) => (c.clearingHouse || "").toLowerCase().includes(v.toLowerCase()),
      cpt: (c, v) => c.cpt.toLowerCase().includes(v.toLowerCase()),
      units: (c, v) => String(c.units || "").includes(v),
      status: (c, v) => c.status.toLowerCase().includes(v.toLowerCase()),
      charge: (c, v) => c.billed.toFixed(2).includes(v),
      totalCharge: (c, v) => c.billed.toFixed(2).includes(v),
      balance: (c, v) => c.balance.toFixed(2).includes(v),
      updated: (c, v) => (c.updatedBy + " " + fmtDate(c.updatedOn)).toLowerCase().includes(v.toLowerCase()),
      recvdPri: (c, v) => c.paid.toFixed(2).includes(v),
      recvdSec: (c, v) => c.secPaid.toFixed(2).includes(v),
      contractual: (c, v) => c.contractual.toFixed(2).includes(v),
      secPendingAmt: (c, v) => c.secPendingAmt.toFixed(2).includes(v),
      deductible: (c, v) => c.deductible.toFixed(2).includes(v),
      patRes: (c, v) => c.patRes.toFixed(2).includes(v),
      copay: (c, v) => c.copay.toFixed(2).includes(v),
      otherAdj: (c, v) => c.otherAdj.toFixed(2).includes(v),
      federalPayment: (c, v) => c.federalPayment.toFixed(2).includes(v),
      selfPay: (c, v) => c.selfPay.toFixed(2).includes(v),
      chequePri: (c, v) => c.checkNumPri.toLowerCase().includes(v.toLowerCase()),
      chequeDatePri: (c, v) => (c.checkDatePri || "").toLowerCase().includes(v.toLowerCase()),
      payerRefPri: (c, v) => c.payerRefPri.toLowerCase().includes(v.toLowerCase()),
      chequeSec: (c, v) => c.checkNumSec.toLowerCase().includes(v.toLowerCase()),
      writeOff: (c, v) => c.writeOff.toFixed(2).includes(v),
      adminWriteOff: (c, v) => c.adminWriteOff.toFixed(2).includes(v),
      provider: (c, v) => c.provider.toLowerCase().includes(v.toLowerCase()),
      submittedBy: (c, v) => c.submittedBy.toLowerCase().includes(v.toLowerCase()),
      updatedBy: (c, v) => c.updatedBy.toLowerCase().includes(v.toLowerCase()),
      verifiedBy: (c, v) => c.verifiedBy.toLowerCase().includes(v.toLowerCase()),
      denialCode: (c, v) => c.denialCode.toLowerCase().includes(v.toLowerCase()),
      remarks: (c, v) => c.remarks.toLowerCase().includes(v.toLowerCase()),
      errorDetails: (c, v) => (c.denialReason || "").toLowerCase().includes(v.toLowerCase()),
      attachments: (c, v) => c.attachments.toString().includes(v),
      chequeDateSec: (c, v) => (c.checkDateSec || "").toLowerCase().includes(v.toLowerCase()),
      payerRefSec: (c, v) => c.payerRefSec.toLowerCase().includes(v.toLowerCase()),
      notes: (c, v) => c.notes.toLowerCase().includes(v.toLowerCase()),
    };
    Object.entries(colFilters).forEach(([col, val]) => {
      if (val.trim() && cfMap[col]) rows = rows.filter(c => cfMap[col](c, val));
    });
    rows.sort((a, b) => { const av = a[sortKey] ?? ""; const bv = b[sortKey] ?? ""; const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true }); return sortDir === "asc" ? cmp : -cmp; });
    return rows;
  }, [activeStatusTab, sortKey, sortDir, colFilters, claims, dosFilter, statusFlagFilter]);

  // V1 parity (ClaimStatus.js 525-602): when perPage="all" we don't render
  // every row at once — that locks up the browser on big datasets. Instead
  // we render `visibleRecords` rows (default 200) and load 200 more each
  // time the user scrolls near the bottom (see loadMoreRecords effect).
  // When perPage is a specific number, that count wins.
  const totalPages = useMemo(() => perPage === "all" ? 1 : Math.max(1, Math.ceil(filtered.length / Number(perPage))), [filtered.length, perPage]);

  useEffect(() => {
    // keep current page within bounds when filter / perPage changes
    if (pageNumber > totalPages) setPageNumber(totalPages);
  }, [pageNumber, totalPages]);

  const displayed = useMemo(() => {
    if (perPage === "all") return filtered.slice(0, visibleRecords);
    const per = Number(perPage) || 100;
    const start = (Math.max(1, pageNumber) - 1) * per;
    return filtered.slice(start, start + per);
  }, [filtered, perPage, visibleRecords, pageNumber]);

  // Reset infinite-scroll window when the underlying filter set changes.
  useEffect(() => {
    setVisibleRecords(ROWS_PER_PAGE);
    setPageNumber(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, perPage]);

  // Scroll-to-bottom auto-loader (V1 handleScroll). Adds the next 200
  // rows and pops a brief notification so the user knows more loaded.
  useEffect(() => {
    if (perPage !== "all") return;
    const onScroll = () => {
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 40;
      if (!atBottom) return;
      setVisibleRecords((prev) => {
        const next = Math.min(prev + ROWS_PER_PAGE, filtered.length);
        if (next > prev) {
          setScrollPopupMsg(`${next} of ${filtered.length} records loaded`);
          setShowScrollPopup(true);
          setTimeout(() => setShowScrollPopup(false), 100);
        }
        return next;
      });
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [perPage, filtered.length]);

  // Use API-provided counts when available, fall back to client-side computation
  const TC = useMemo(() => {
    if (apiTC.allClaims > 0) return apiTC;
    return {
      allClaims: claims.length,
      totalSubmitClaim: claims.filter(c => claimMatchesStatusTab(c, "submitted")).length,
      totalNonSubmitted: claims.filter(c => claimMatchesStatusTab(c, "nonsub")).length,
      totalSent: claims.filter(c => claimMatchesStatusTab(c, "sent")).length,
      totalForwarded: claims.filter(c => claimMatchesStatusTab(c, "forwarded")).length,
      totalReceivedClaim: claims.filter(c => claimMatchesStatusTab(c, "received")).length,
      totalPosted: claims.filter(c => claimMatchesStatusTab(c, "posted")).length,
      totalPaid: claims.filter(c => claimMatchesStatusTab(c, "paid")).length,
      totalDenied: claims.filter(c => claimMatchesStatusTab(c, "denied")).length,
      totalRejected: claims.filter(c => claimMatchesStatusTab(c, "rejected")).length,
    };
  }, [apiTC, claims]);

  const totals = useMemo(() => {
    if (apiTotals.totalBilled > 0) return apiTotals;
    return {
      totalBilled: claims.reduce((s, c) => s + c.billed, 0),
      totalReceived: claims.filter(c => c.status === "Paid").reduce((s, c) => s + c.paid, 0),
      totalBalance: claims.reduce((s, c) => s + c.balance, 0),
    };
  }, [apiTotals, claims]);

  const totalsRow = useMemo(() => ({
    billed: displayed.reduce((s, c) => s + c.billed, 0),
    paid: displayed.reduce((s, c) => s + c.paid, 0),
    secPaid: displayed.reduce((s, c) => s + c.secPaid, 0),
    contractual: displayed.reduce((s, c) => s + c.contractual, 0),
    deductible: displayed.reduce((s, c) => s + c.deductible, 0),
    patRes: displayed.reduce((s, c) => s + c.patRes, 0),
    copay: displayed.reduce((s, c) => s + c.copay, 0),
    otherAdj: displayed.reduce((s, c) => s + c.otherAdj, 0),
    writeOff: displayed.reduce((s, c) => s + c.writeOff, 0),
    adminWriteOff: displayed.reduce((s, c) => s + c.adminWriteOff, 0),
    balance: displayed.reduce((s, c) => s + c.balance, 0),
  }), [displayed]);

  const dailyFiltered = useMemo(() => dosSearch ? dailyData.filter(d => d.DOS.includes(dosSearch)) : dailyData, [dosSearch, dailyData]);
  const activeColFilterCount = Object.values(colFilters).filter(v => v.trim()).length;

  function toggleSort(k) { if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(k); setSortDir("asc"); } }
  function toggleRow(rowKey) { setSelectedRows(r => r.includes(rowKey) ? r.filter(x => x !== rowKey) : [...r, rowKey]); }
  function isResubmitCheckboxEnabled(claim) {
    const item = claim.raw || {};
    return parseFloat(item.recvdCharge) <= 0 || item?.ClaimStatus === "Rejected";
  }
  function handleStatusTab(tab) {
    const normalizedTab = normalizeStatusTab(tab);
    setActiveStatusTab(normalizedTab);
    setActiveCounts(normalizedTab === "all" ? null : normalizedTab);
  }
  // Daily-breakdown drill-down: clicking a count in a per-DOS row filters the main table
  // by that DOS + status. Click again on the same cell to clear.
  function drillDailyCell(dos, statusKey) {
    const same = dosFilter === dos && (statusKey === "all" ? activeStatusTab === "all" : activeStatusTab === statusKey);
    if (same) { setDosFilter(""); setActiveStatusTab("all"); setActiveCounts(null); return; }
    setDosFilter(dos);
    setActiveStatusTab(statusKey);
    setActiveCounts(statusKey === "all" ? null : statusKey);
  }
  function clearDosDrill() { setDosFilter(""); }
  function openClaim(c, tab = "overview") { setSelectedClaim(c); setDrawerTab(tab); setOpenRowMenu(null); }

  /* ─── RENDER HELPERS ─── */
  function renderHeader(col) {
    const label = COL_LABELS[col] ?? col;
    const isNum = NUM_COLS.has(col);
    const hasFilter = !!(colFilters[col]?.trim());
    const sortField = SORT_KEY_MAP[col];
    if (col === "#") return (
      <th key="#" style={{ width: 38 }}>
        #
      </th>
    );
    if (col === "actions") return <th key="actions" style={{ width: 90 }} />;
    if (col === "resubmit") {
      const enabledResubmitRows = filtered.filter(isResubmitCheckboxEnabled);
      const enabledRowKeys = enabledResubmitRows.map(c => c.rowKey);
      return (
        <th key="resubmit" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span className="cs-th-label">Resubmit</span>
            <input
              type="checkbox"
              title="Select all visible rows for resubmit"
              style={{ accentColor: "#e8812a", cursor: "pointer" }}
              checked={
                enabledResubmitRows.length > 0 &&
                enabledResubmitRows.every(c => selectedRows.includes(c.rowKey))
              }
              onChange={e => {
                if (e.target.checked) setSelectedRows(prev => Array.from(new Set([...prev, ...enabledRowKeys])));
                else setSelectedRows(prev => prev.filter(rowKey => !enabledRowKeys.includes(rowKey)));
              }}
              onClick={e => e.stopPropagation()}
            />
          </div>
        </th>
      );
    }
    if (col === "writeOff" || col === "adminWriteOff") {
      const isClose = col === "writeOff";
      const selected = isClose ? closeRows : adminWriteOffRows;
      const setSelected = isClose ? setCloseRows : setAdminWriteOffRows;
      // Mirror the old UI: "Close" button under Write-Off (calls CloseClaimWithCPT
      // on selected rows), "Write-off" button under Admin Write-Off (opens the
      // admin-writeoff modal with selected rows). Both reuse the existing
      // closeClaimsWithCptLegacy / setShowAdminWriteModal flows.
      const onAction = () => {
        if (selected.length === 0) {
          showToast(`Select at least one row for ${isClose ? "close" : "admin write-off"}`, "warn");
          return;
        }
        if (isClose) {
          closeClaimsWithCptLegacy(selected);
          setCloseRows([]);
        } else {
          setSelectedRows(selected);
          // Seed adminWriteOff drafts with each claim's total charge, matching
          // the old UI's saveAdminClaimWriteOff which uses item.totalcharge.
          selected.forEach(rowKey => {
            const claim = claims.find(c => c.rowKey === rowKey);
            if (claim) setDraftValue(rowKey, "adminWriteOff", claim.billed ? claim.billed.toFixed(2) : "0.00");
          });
          setShowAdminWriteModal(true);
        }
      };
      const allChecked = filtered.length > 0 && selected.length === filtered.length;
      return (
        <th key={col} className="num">
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <input
              type="checkbox"
              title={`Select all visible rows for ${isClose ? "close claim" : "admin write-off"}`}
              style={{ accentColor: "#e8812a", cursor: "pointer" }}
              checked={allChecked}
              onChange={e => {
                if (e.target.checked) setSelected(filtered.map(c => c.rowKey));
                else setSelected([]);
              }}
              onClick={e => e.stopPropagation()}
            />
            <span className={`cs-th-label${hasFilter ? " cs-th-filtered" : ""}`}>{label}</span>
            <button
              type="button"
              className="cs-check-btn"
              style={{ padding: "2px 8px", fontSize: 11, background: "#e8812a", color: "#fff", border: "none" }}
              onClick={e => { e.stopPropagation(); onAction(); }}
              title={isClose ? "Close selected claims" : "Open Admin Write-off modal for selected claims"}
            >{isClose ? "Close" : "Write-off"}{selected.length > 0 ? ` (${selected.length})` : ""}</button>
          </div>
        </th>
      );
    }
    if (col === "insPri" || col === "insSec") {
      const isPri = col === "insPri";
      const selected = isPri ? eligPriRows : eligSecRows;
      const setSelected = isPri ? setEligPriRows : setEligSecRows;
      const onBatch = isPri ? runBatchPriEligibility : runBatchSecEligibility;
      const allChecked = filtered.length > 0 && selected.length === filtered.length;
      return (
        <th key={col}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <input
              type="checkbox"
              title={`Select all visible rows for ${isPri ? "primary" : "secondary"} eligibility`}
              style={{ accentColor: "#1a9b8c", cursor: "pointer" }}
              checked={allChecked}
              onChange={e => {
                if (e.target.checked) setSelected(filtered.map(c => c.rowKey));
                else setSelected([]);
              }}
              onClick={e => e.stopPropagation()}
            />
            <span className={`cs-th-label${hasFilter ? " cs-th-filtered" : ""}`}>{label}</span>
            {selected.length > 0 && (
              <button
                type="button"
                className="cs-check-btn"
                style={{ padding: "2px 6px", fontSize: 11 }}
                onClick={e => { e.stopPropagation(); onBatch(); }}
                title={`Check eligibility for ${selected.length} selected row(s)`}
              >Check ({selected.length})</button>
            )}
          </div>
        </th>
      );
    }
    if (col === "status") return (
      // V1 parity (ClaimStatus.js 6317-6323 + handleStatusFilterChange 3660):
      // status column header has clickable N/D/P/F/R/P0/S0 mini-buttons
      // that filter the table by raw ClaimStatusFlag. Click again to clear.
      <th
        key="status"
        onClick={() => sortField && toggleSort(sortField)}
        style={sortField ? { cursor: "pointer" } : undefined}
        title="N=Pending · D=Denied · P=Paid · F=Forwarded · R=Rejected · S=Sent · PO=Posted · SO=Sec. Outstanding"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span className={`cs-th-label${hasFilter ? " cs-th-filtered" : ""}`} onClick={() => sortField && toggleSort(sortField)} style={sortField ? { cursor: "pointer" } : undefined}>Status</span>
          <span style={{ display: "inline-flex", gap: 2, width: '100px' }}>
            {[
              { k: "N", label: "Not-Submitted", bg: STATUS_CFG.Pending.dot },
              { k: "D", label: "Denied", bg: STATUS_CFG.Denied.dot },
              { k: "P", label: "Paid", bg: STATUS_CFG.Paid.dot },
              { k: "F", label: "Forwarded", bg: STATUS_CFG.Forwarded.dot },
              { k: "R", label: "Rejected", bg: STATUS_CFG.Rejected.dot },
              { k: "S", label: "Sent", bg: STATUS_CFG.Sent.dot },
              { k: "P0", label: "Posted-no-pay", bg: STATUS_CFG.Posted.dot },
              { k: "S0", label: "Sec-outstanding", bg: STATUS_CFG.Received.dot },
            ].map(b => {
              const active = statusFlagFilter === b.k;
              return (
                <span
                  key={b.k}
                  title={b.label + (active ? " (active — click to clear)" : " (click to filter)")}
                  style={{
                    background: active ? b.bg : "transparent",
                    color: active ? "#fff" : b.bg,
                    border: `1px solid ${b.bg}`,
                    fontSize: 9,
                    fontWeight: 700,
                    padding: "1px 1px",
                    borderRadius: 3,
                    minWidth: 14,
                    textAlign: "center",
                    lineHeight: "12px",
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusFlagFilter(prev => prev === b.k ? null : b.k);
                  }}
                >{b.k}</span>
              );
            })}
          </span>
          {sortField && sortKey === sortField && <span style={{ opacity: .6 }}>{sortDir === "asc" ? "↑" : "↓"}</span>}
        </div>
      </th>
    );
    return (
      <th
        key={col}
        className={isNum ? "num" : ""}
        onClick={() => sortField && toggleSort(sortField)}
        style={sortField ? { cursor: "pointer" } : undefined}
      >
        <span className={`cs-th-label${hasFilter ? " cs-th-filtered" : ""}`}>{label}</span>
        {sortField && sortKey === sortField && <span style={{ marginLeft: 4, opacity: .8, color: "#fff" }}>{sortDir === "asc" ? "↑" : "↓"}</span>}
      </th>
    );
  }

  function renderFilterCell(col) {
    if (col === "#" || col === "check" || col === "actions") return <th key={col} className="cs-col-filter" />;
    const isNum = NUM_COLS.has(col);
    return (
      <th key={col} className="cs-col-filter">
        <input type="text" className="cs-col-filter-input"
          value={colFilters[col] || ""}
          onChange={e => setColFilters(prev => ({ ...prev, [col]: e.target.value }))}
          onClick={e => e.stopPropagation()}
          placeholder="Search"
          style={isNum ? { textAlign: "right" } : {}} />
      </th>
    );
  }

  function renderTotalsCell(col) {
    const fmt = (n) => n > 0 ? `$${n.toFixed(2)}` : "-";
    switch (col) {
      case "check": return <td key="check" />;
      case "actions": return <td key="actions" />;
      case "bhsud": return <td key="bhsud" />;
      case "patient": return <td key="patient" style={{ fontWeight: 700, color: "#374151" }}>{displayed.length} claims</td>;
      case "charge":
      case "totalCharge": return <td key={col} className="num cs-total-cell">{fmt(totalsRow.billed)}</td>;
      case "balance": return <td key="balance" className="num cs-total-cell" style={{ color: totalsRow.balance > 0 ? "#dc2626" : "#15803d" }}>{fmt(totalsRow.balance)}</td>;
      case "recvdPri": return <td key="recvdPri" className="num cs-total-cell cs-paid-amt">{fmt(totalsRow.paid)}</td>;
      case "recvdSec": return <td key="recvdSec" className="num cs-total-cell cs-paid-amt">{fmt(totalsRow.secPaid)}</td>;
      case "contractual": return <td key="contractual" className="num cs-total-cell">{fmt(totalsRow.contractual)}</td>;
      case "deductible": return <td key="deductible" className="num cs-total-cell">{fmt(totalsRow.deductible)}</td>;
      case "patRes": return <td key="patRes" className="num cs-total-cell">{fmt(totalsRow.patRes)}</td>;
      case "copay": return <td key="copay" className="num cs-total-cell">{fmt(totalsRow.copay)}</td>;
      case "otherAdj": return <td key="otherAdj" className="num cs-total-cell">{fmt(totalsRow.otherAdj)}</td>;
      case "units": return <td key="units" className="num cs-total-cell">{displayed.reduce((s, c) => s + c.units, 0) || "-"}</td>;
      case "secPendingAmt": return <td key="secPendingAmt" className="num cs-total-cell">{fmt(displayed.reduce((s, c) => s + c.secPendingAmt, 0))}</td>;
      case "federalPayment": return <td key="federalPayment" className="num cs-total-cell">{fmt(displayed.reduce((s, c) => s + c.federalPayment, 0))}</td>;
      case "selfPay": return <td key="selfPay" className="num cs-total-cell">{fmt(displayed.reduce((s, c) => s + c.selfPay, 0))}</td>;
      case "location": return <td key="location" />;
      case "clearingHouse": return <td key="clearingHouse" />;
      case "secSubmitDate": return <td key="secSubmitDate" />;
      case "writeOff": return <td key="writeOff" className="num cs-total-cell">{fmt(totalsRow.writeOff)}</td>;
      case "adminWriteOff": return <td key="adminWriteOff" className="num cs-total-cell">{fmt(totalsRow.adminWriteOff)}</td>;
      default: return <td key={col} />;
    }
  }

  function renderDraftInput(
    claim,
    field,
    width,
    onBlur,
    placeholder = "",
    inputType = "text",
  ) {
    const fallback =
      field === "checkNumPri" ? claim.checkNumPri :
        field === "checkDatePri" ? formatDateForInput(claim.checkDatePri) :
          field === "payerRefPri" ? claim.payerRefPri :
            field === "checkNumSec" ? claim.checkNumSec :
              field === "checkDateSec" ? formatDateForInput(claim.checkDateSec) :
                field === "payerRefSec" ? claim.payerRefSec :
                  field === "adminWriteOff" ? (claim.adminWriteOff ? claim.adminWriteOff.toFixed(2) : "") :
                    field === "writeOff" ? (claim.writeOff ? claim.writeOff.toFixed(2) : "") :
                      field === "paid" ? (claim.paid ? claim.paid.toFixed(2) : "") :
                        field === "secPaid" ? (claim.secPaid ? claim.secPaid.toFixed(2) : "") :
                          field === "contractual" ? (claim.contractual ? claim.contractual.toFixed(2) : "") :
                            field === "secPendingAmt" ? (claim.secPendingAmt ? claim.secPendingAmt.toFixed(2) : "") :
                              field === "deductible" ? (claim.deductible ? claim.deductible.toFixed(2) : "") :
                                field === "patRes" ? (claim.patRes ? claim.patRes.toFixed(2) : "") :
                                  field === "copay" ? (claim.copay ? claim.copay.toFixed(2) : "") :
                                    field === "otherAdj" ? (claim.otherAdj ? claim.otherAdj.toFixed(2) : "") :
                                      field === "federalPayment" ? (claim.federalPayment ? claim.federalPayment.toFixed(2) : "") :
                                       field === "selfPay" ? (claim.selfPay ? claim.selfPay.toFixed(2) : "") :
                                        "";

    return (
      <input
        type={inputType}
        className="hv-cellinput"
        style={{ width }}
        value={getDraftValue(claim, field, fallback)}
        placeholder={placeholder}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => setDraftValue(claim.rowKey, field, event.target.value)}
        onBlur={(event) => onBlur(event.target.value)}
        onKeyDown={(event) => {
          // Enter triggers the same save as blur — without this, users who hit
          // Enter and never click out see no network call. Tab/click-away still
          // works via onBlur as before.
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
      />
    );
  }

  function renderCell(col, c, index) {
    switch (col) {

      case "#": {
        const per = perPage === "all" ? null : Number(perPage) || 50;
        const globalIndex = per ? ((pageNumber - 1) * per) + index + 1 : index + 1;
        return <td key={col} className="cs-row-number">{globalIndex}</td>;
      }
      case "patient": {
        // V1 parity (ClaimStatus.js 6784): patient name is a click-target
        // that opens patient-dashboard in a new tab. Color/underline
        // reflects whether eligibility has been checked (green vs red).
        const eligChecked = (c.raw?.EligibilityCheckCount ?? 0) > 0;
        const hasSecIns = (c.raw?.PatSecInscount ?? 0) > 0;
        return (
          <td key="patient" onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                className={`cs-pat-name ${eligChecked ? "cs-elig-checked" : "cs-elig-not-checked"}`}
                style={{ cursor: "pointer", textDecoration: "underline" }}
                title={eligChecked ? "Eligibility checked" : "Eligibility not checked"}
                onClick={() => {
                  localStorage.setItem("statusPatientID", c.patientId);
                  window.open(`/#/patientdashboard/${c.patientId}/primary-insurance`, "_blank");
                }}
              >{c.patient}</span>
              {hasSecIns && (
                <FontAwesomeIcon icon={faShieldHalved} title="Has secondary insurance" style={{ color: "#1a9b8c", fontSize: 11 }} />
              )}
            </div>
            <div className="cs-pat-meta">{c.mrn} · {fmtDate(c.dob)}</div>
          </td>
        );
      }
      case "claimId": {
        // V1 parity (ClaimStatus.js 6754-6781): Claim # is a multi-element
        // cell — clickable id, ClaimHx prefix, faFile icon for events,
        // arrows-rotate to regenerate CMS1500, faFilePdf to open CMS PDF.
        const claimHx = c.claimHx || "";
        const [hxPrefix] = claimHx ? claimHx.split("$") : [""];
        const rowKey = c.rowKey;
        return (
          <td key="claimId" onClick={e => e.stopPropagation()}>
            <span
              className="cs-claim-id"
              style={{ cursor: "pointer", textDecoration: "underline", color: "#1d4ed8" }}
              title="Open encounter"
              onClick={() => window.open(`/#/new-encounter/${c.id}`, "_blank")}
            >{c.id}</span>
            {hxPrefix && hxPrefix !== "F0" && (
              <span
                style={{ marginLeft: 6, fontSize: 10, color: "#7c3aed", cursor: "pointer", textDecoration: "underline" }}
                title="Submit history"
                onClick={() => fetchClaimSubmitHxLegacy(c)}
              >{hxPrefix}</span>
            )}
            <FontAwesomeIcon
              icon={faFile}
              title="View claim events"
              style={{ marginLeft: 8, color: "#0f172a", cursor: "pointer", fontSize: 11 }}
              onClick={() => fetchClaimEventsLegacy(c)}
            />
            <FontAwesomeIcon
              icon={faRotate}
              spin={regenLoadingKey === rowKey}
              title="Regenerate CMS1500 PDF"
              style={{ marginLeft: 6, color: "#0e6b60", cursor: "pointer", fontSize: 11 }}
              onClick={() => regenerateCmsPdfLegacy(c)}
            />
            <FontAwesomeIcon
              icon={faFilePdf}
              title="Open CMS1500 PDF"
              style={{ marginLeft: 6, color: "#dc2626", cursor: "pointer", fontSize: 11 }}
              onClick={() => openCmsPdfLegacy(c)}
            />
          </td>
        );
      }
      case "mrn": return <td key="mrn" style={{ fontSize: 12, fontFamily: "monospace", color: "#475569" }}>{c.mrn || "-"}</td>;
      case "dob": return <td key="dob" style={{ whiteSpace: "nowrap", fontSize: 12, color: "#475569" }}>{c.dob ? fmtDate(c.dob) : "-"}</td>;
      case "dos": {
        // V1 parity (ClaimStatus.js 6761): DOS opens transactionlogs in a
        // new tab. Persists the clicked item so the destination page can
        // re-fetch context.
        return (
          <td key="dos" style={{ whiteSpace: "nowrap", fontSize: 12 }} onClick={e => e.stopPropagation()}>
            <span
              style={{ cursor: "pointer", textDecoration: "underline", color: "#1d4ed8" }}
              title="Open transaction log"
              onClick={() => {
                localStorage.setItem("selectedClaimItem", JSON.stringify(c.raw || {}));
                window.open(`/#/transactionlogs/${c.id}`, "_blank");
              }}
            >{fmtDate(c.dos)}</span>
          </td>
        );
      }
      case "bhsud": return <td key="bhsud"><span className={`cs-bhsud ${c.conType.toLowerCase()}`}>{c.conType}</span></td>;
      case "location": return <td key="location" style={{ fontSize: 12, maxWidth: 130, color: "#475569" }}>{c.location || "-"}</td>;
      case "insurance": return <td key="insurance" style={{ fontSize: 12, maxWidth: 140 }}>{c.payer}</td>;
      case "cpt": return <td key="cpt"><span style={{ fontFamily: "monospace", fontSize: 12, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>{c.cpt}</span></td>;
      case "status": return <td key="status"><StatusBadge status={c.status} /></td>;
      case "charge": return <td key="charge" className="num">${c.billed.toFixed(2)}</td>;
      case "balance": return <td key="balance" className="num"><span className={c.balance > 0 ? "cs-bal-amt" : "cs-zero-amt"}>{c.balance > 0 ? `$${c.balance.toFixed(2)}` : "-"}</span></td>;
      case "updated": return <td key="updated"><div style={{ fontSize: 12 }}>{c.updatedBy}</div><div className="cs-pat-meta">{fmtDate(c.updatedOn)}</div></td>;
      case "actions": return (
        <td key="actions" onClick={e => e.stopPropagation()}>
          <div className="cs-row-actions">
            <button className="cs-row-btn" title="Patient 360° View" onClick={() => setPatient360Claim(c)}><FontAwesomeIcon icon={faEye} /></button>
            {/* Pen-to-square universally means "edit" — opens the
                encounter editor in a new tab so the user can change
                CPT/charge/units etc. on this claim. */}
            <button className="cs-row-btn" title="Edit Encounter" onClick={() => window.open(`/#/new-encounter/${c.id}`, "_blank")}><FontAwesomeIcon icon={faPenToSquare} /></button>
            <div className="cs-row-menu-wrap">
              <button className={`cs-row-btn${openRowMenu === c.rowKey ? " active" : ""}`} title="More actions"
                onClick={() => setOpenRowMenu(openRowMenu === c.rowKey ? null : c.rowKey)}>
                <FontAwesomeIcon icon={faEllipsisVertical} />
              </button>
              {openRowMenu === c.rowKey && (
                <div className="cs-row-dropdown">
                  <div className="cs-row-dd-item" onClick={() => openClaim(c, "overview")}>
                    <FontAwesomeIcon icon={faEye} /> View Details
                  </div>
                  <div className="cs-row-dd-item" onClick={() => { checkEligibilityLegacy(c.rowKey, c.patient); setOpenRowMenu(null); }}>
                    <FontAwesomeIcon icon={faShieldHalved} /> Check Eligibility
                  </div>
                  <div className="cs-row-dd-item" onClick={() => { openClaim(c, "notes"); setOpenRowMenu(null); }}>
                    <FontAwesomeIcon icon={faNotesMedical} /> Add / View Notes
                  </div>
                  <div className="cs-row-dd-item" onClick={() => { fetchClaimAttachmentsLegacy(c); setOpenRowMenu(null); }}>
                    <FontAwesomeIcon icon={faPaperclip} />
                    Attachments {c.attachments > 0 && <span className="cs-dd-badge">{c.attachments}</span>}
                  </div>
                  <div className="cs-row-dd-item" onClick={() => { openClaim(c, "financials"); setOpenRowMenu(null); }}>
                    <FontAwesomeIcon icon={faFileInvoiceDollar} /> View EOB
                  </div>
                  <div className="cs-row-dd-item" onClick={() => { fetchClaimEventsLegacy(c); setOpenRowMenu(null); }}>
                    <FontAwesomeIcon icon={faListCheck} /> Claim History
                  </div>
                  <div className="cs-row-dd-item" onClick={() => { setPatient360Claim(c); setOpenRowMenu(null); }} style={{ background: "#fef3c7" }}>
                    <FontAwesomeIcon icon={faIdBadge} style={{ color: "#b45309" }} />
                    <span style={{ fontWeight: 600, color: "#92400e" }}>Patient 360° View</span>
                  </div>
                  <div className="cs-row-dd-sep" />
                  {(c.status === "Denied" || c.status === "Rejected" || c.status === "Submitted" || c.status === "Sent") && (
                    <div className="cs-row-dd-item" onClick={() => { resubmitSelectedClaimsLegacy([c.rowKey]); setOpenRowMenu(null); }}>
                      <FontAwesomeIcon icon={faRotate} /> Resubmit
                    </div>
                  )}
                  {(c.status === "Denied" || c.status === "Rejected") && (
                    <div className="cs-row-dd-item" onClick={() => { showToast("Appeal initiated for " + c.id); setOpenRowMenu(null); }}>
                      <FontAwesomeIcon icon={faTriangleExclamation} /> Appeal
                    </div>
                  )}
                  <div className="cs-row-dd-item" onClick={() => {
                    setSelectedRows([c.rowKey]);
                    setDraftValue(c.rowKey, "adminWriteOff", c.adminWriteOff > 0 ? c.adminWriteOff.toFixed(2) : "0.00");
                    setShowAdminWriteModal(true);
                    setOpenRowMenu(null);
                  }}>
                    <FontAwesomeIcon icon={faXmark} /> Admin Write-off
                  </div>
                  <div className="cs-row-dd-sep" />
                  {!c.isVerified && (
                    <div className="cs-row-dd-item" onClick={() => openVerifyModalLegacy(c)}>
                      <FontAwesomeIcon icon={faCheck} /> Verify Claim
                    </div>
                  )}
                  <div className="cs-row-dd-item" onClick={() => openCopayModalLegacy(c)}>
                    <FontAwesomeIcon icon={faSackDollar} /> Apply Copay
                  </div>
                  <div className="cs-row-dd-item" onClick={() => regenerateCmsPdfLegacy(c)}>
                    <FontAwesomeIcon icon={regenLoadingKey === c.rowKey ? faSpinner : faRotate} spin={regenLoadingKey === c.rowKey} /> Regenerate CMS1500
                  </div>
                  <div className="cs-row-dd-item" onClick={() => openCptDocumentLegacy(c)}>
                    <FontAwesomeIcon icon={faFile} /> CPT Document
                  </div>
                  <div className="cs-row-dd-item" onClick={() => fetchClaimSubmitHxLegacy(c)}>
                    <FontAwesomeIcon icon={faList} /> Submit History
                  </div>
                  <div className="cs-row-dd-sep" />
                  <div className="cs-row-dd-item danger" onClick={() => { setSelectedRows([c.rowKey]); closeClaimsWithCptLegacy([c.rowKey]); setOpenRowMenu(null); }}>
                    <FontAwesomeIcon icon={faBoxArchive} /> Close Claim
                  </div>
                </div>
              )}
            </div>
          </div>
        </td>
      );
      case "units": return <td key="units" className="num">{c.units > 0 ? c.units : "-"}</td>;
      case "totalCharge": return <td key="totalCharge" className="num">${c.billed.toFixed(2)}</td>;
      case "recvdPri": return <td key="recvdPri" className="num">{renderDraftInput(c, "paid", 88, (value) => saveMoneyFieldLegacy(c, "paid", value, API.CPTReceviedCharge, {}, { priorSec: 1, saveAdjustmentAfter: true }), "0.00")}</td>;
      case "recvdSec": return <td key="recvdSec" className="num">{renderDraftInput(c, "secPaid", 88, (value) => saveMoneyFieldLegacy(c, "secPaid", value, API.CPTReceviedCharge, {}, { priorSec: 2, saveAdjustmentAfter: true }), "0.00")}</td>;
      case "contractual": return <td key="contractual" className="num">{renderDraftInput(c, "contractual", 92, (value) => saveAdjustmentLegacy(c, parseFloat(value) || 0), "0.00")}</td>;
      case "deductible": return <td key="deductible" className="num">{renderDraftInput(c, "deductible", 88, (value) => saveMoneyFieldLegacy(c, "deductible", value, API.UpdateClaimDeductible, {}, { saveAdjustmentAfter: true }), "0.00")}</td>;
      case "patRes": return <td key="patRes" className="num">{renderDraftInput(c, "patRes", 88, (value) => saveMoneyFieldLegacy(c, "patRes", value, API.UpdateClaimPatientResponsibility, {}, { saveAdjustmentAfter: true }), "0.00")}</td>;
      case "copay": return (
        // V1 parity (ClaimStatus.js 6960-6982): inline copay input PLUS an
        // Apply / Applied button next to it. The button toggles based on
        // CopayAppliedClaim:
        //   - "0" / "" → orange Apply button (calls ApplyCopay)
        //   - matches this ClaimId → grey Applied button (tooltip shows who/when)
        <td key="copay" className="num" onClick={e => e.stopPropagation()}>
          {renderDraftInput(c, "copay", 88, (value) => saveCopayLegacy(c, value), "0.00")}
          {(c.copayAppliedClaim === "0" || c.copayAppliedClaim === "" || c.copayAppliedClaim == null) ? (
            <button
              className="cs-check-btn"
              style={{ marginLeft: 4, padding: "2px 6px", fontSize: 10, background: "#e8812a", color: "#fff", border: "none" }}
              title="Apply copay collected from patient"
              onClick={() => openCopayModalLegacy(c)}
            >{c.copayAmount || 0}</button>
          ) : String(c.copayAppliedClaim) === String(c.id) ? (
            <button
              className="cs-check-btn"
              style={{ marginLeft: 4, padding: "2px 6px", fontSize: 10, background: "#94a3b8", color: "#fff", border: "none" }}
              title={`Applied to ${c.copayAppliedClaim} by ${c.copayAppliedByShortName || "?"} on ${c.copayAppliedOn || "?"}`}
            >{c.copayAmount || 0}</button>
          ) : null}
        </td>
      );
      case "otherAdj": return <td key="otherAdj" className="num">{renderDraftInput(c, "otherAdj", 88, (value) => saveMoneyFieldLegacy(c, "otherAdj", value, API.SaveOtherAdjustment, {}, { saveAdjustmentAfter: true }), "0.00")}</td>;
      case "secPendingAmt": return <td key="secPendingAmt" className="num">{renderDraftInput(c, "secPendingAmt", 92, (value) => saveMoneyFieldLegacy(c, "secPendingAmt", value, API.SaveSecPendingCharges), "0.00")}</td>;
      case "federalPayment": return <td key="federalPayment" className="num">{renderDraftInput(c, "federalPayment", 92, (value) => saveMoneyFieldLegacy(c, "federalPayment", value, API.SaveFederalReduction, {}, { saveAdjustmentAfter: true }), "0.00")}</td>;
      case "selfPay": return <td key="selfPay" className="num">{renderDraftInput(c, "selfPay", 92, (value) => saveMoneyFieldLegacy(c, "selfPay", value, API.SaveSelfPay, {}, { saveAdjustmentAfter: true }), "0.00")}</td>;
      case "insPri": return (
        // V1 parity (ClaimStatus.js 6811-6855): primary insurance cell shows
        // payer, payerId, policyNumber, batch eligibility checkbox, Check
        // button, and the red "Skipped" pill when IsPrimarySkipped=true so
        // the user knows the claim went straight to the secondary payer.
        <td key="insPri" style={{ fontSize: 12, background: c.isPrimarySkipped ? "#fef9c3" : undefined }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
            <input
              type="checkbox"
              title="Include this row in batch primary eligibility check"
              style={{ accentColor: "#1a9b8c", cursor: "pointer", marginTop: 2 }}
              checked={eligPriRows.includes(c.rowKey)}
              onChange={e => {
                e.stopPropagation();
                setEligPriRows(prev => prev.includes(c.rowKey) ? prev.filter(k => k !== c.rowKey) : [...prev, c.rowKey]);
              }}
              onClick={e => e.stopPropagation()}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: "#0f172a" }}>{c.payer || "-"}</div>
              {c.payerId && <div className="cs-pat-meta">{c.payerId}</div>}
              {c.policyNumber && <div className="cs-pat-meta">{c.policyNumber}</div>}
              <button className="cs-check-btn" onClick={e => { e.stopPropagation(); checkEligibilityLegacy(c.rowKey, c.patient); }}>Check</button>
              {c.isPrimarySkipped && (
                <div style={{ marginTop: 3, display: "inline-block", padding: "1px 6px", background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 3 }}>Skipped</div>
              )}
            </div>
          </div>
        </td>
      );
      case "submitDatePri": return (
        // Shows Skipped pill on the primary submit date too, since V1
        // displays both columns when IsPrimarySkipped=true.
        <td key="submitDatePri" style={{ fontSize: 12, whiteSpace: "nowrap", background: c.isPrimarySkipped ? "#fef9c3" : undefined }}>
          {c.submittedOn ? fmtDate(c.submittedOn) : "-"}
          {c.isPrimarySkipped && <div style={{ marginTop: 3, display: "inline-block", padding: "1px 6px", background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: 3 }}>Skipped</div>}
        </td>
      );
      case "chequePri": return <td key="chequePri" onClick={e => e.stopPropagation()}>{renderDraftInput(c, "checkNumPri", 110, (value) => saveTextFieldLegacy(c, "checkNumPri", value, API.UpdateCheckNumber, (next) => ({ objClaim: { ClaimID: c.id, PracticeID: userPracticeID, PatientID: c.patientId, CPT: c.cpt, ChequeNumber: next, DOS: formatDateForApi(c.dos), UpdatedBy: userData?.userID, PriorSec: 1, IsLineItemData: 1 } }), `Primary cheque updated for ${c.id}`), "-")}</td>;
      case "chequeDatePri": return <td key="chequeDatePri" onClick={e => e.stopPropagation()}>{renderDraftInput(c, "checkDatePri", 130, (value) => saveTextFieldLegacy(c, "checkDatePri", formatDateForApi(value), API.UpdateCheckDate, (next) => ({ objClaim: { ClaimID: c.id, PracticeID: userPracticeID, PatientID: c.patientId, CPT: c.cpt, ChequeDate: next, DOS: formatDateForApi(c.dos), UpdatedBy: userData?.userID, PriorSec: 1, IsLineItemData: 1 } }), `Primary cheque date updated for ${c.id}`), "", "date")}</td>;
      case "payerRefPri": return <td key="payerRefPri" onClick={e => e.stopPropagation()}>{renderDraftInput(c, "payerRefPri", 120, (value) => saveTextFieldLegacy(c, "payerRefPri", value, API.UpdateClaimReferenceNumber, (next) => ({ objClaim: { ClaimID: c.id, PracticeID: userPracticeID, PatientID: c.patientId, DOS: formatDateForApi(c.dos), UpdatedBy: userData?.userID, PriorSec: 1, PayerReferenceNumber: next } }), `Primary payer reference updated for ${c.id}`), "-")}</td>;
      case "insSec": return (
        <td key="insSec" style={{ fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
            <input
              type="checkbox"
              title="Include this row in batch secondary eligibility check"
              style={{ accentColor: "#1a9b8c", cursor: "pointer", marginTop: 2 }}
              checked={eligSecRows.includes(c.rowKey)}
              onChange={e => {
                e.stopPropagation();
                setEligSecRows(prev => prev.includes(c.rowKey) ? prev.filter(k => k !== c.rowKey) : [...prev, c.rowKey]);
              }}
              onClick={e => e.stopPropagation()}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: "#0f172a" }}>{c.secPayer || "-"}</div>
              {c.secPayerId && <div className="cs-pat-meta">{c.secPayerId}</div>}
              {c.secPolicyNumber && <div className="cs-pat-meta">{c.secPolicyNumber}</div>}
              <button className="cs-check-btn" onClick={e => { e.stopPropagation(); checkSecEligibilityLegacy(c.rowKey, c.patient); }}>Check</button>
            </div>
          </div>
        </td>
      );
      case "chequeSec": return <td key="chequeSec" onClick={e => e.stopPropagation()}>{renderDraftInput(c, "checkNumSec", 110, (value) => saveTextFieldLegacy(c, "checkNumSec", value, API.UpdateCheckNumber, (next) => ({ objClaim: { ClaimID: c.id, PracticeID: userPracticeID, PatientID: c.patientId, CPT: c.cpt, ChequeNumber: next, DOS: formatDateForApi(c.dos), UpdatedBy: userData?.userID, PriorSec: 2, IsLineItemData: 1 } }), `Secondary cheque updated for ${c.id}`), "-")}</td>;
      case "chequeDateSec": return <td key="chequeDateSec" onClick={e => e.stopPropagation()}>{renderDraftInput(c, "checkDateSec", 130, (value) => saveTextFieldLegacy(c, "checkDateSec", formatDateForApi(value), API.UpdateCheckDate, (next) => ({ objClaim: { ClaimID: c.id, PracticeID: userPracticeID, PatientID: c.patientId, CPT: c.cpt, ChequeDate: next, DOS: formatDateForApi(c.dos), UpdatedBy: userData?.userID, PriorSec: 2, IsLineItemData: 1 } }), `Secondary cheque date updated for ${c.id}`), "", "date")}</td>;
      case "payerRefSec": return <td key="payerRefSec" onClick={e => e.stopPropagation()}>{renderDraftInput(c, "payerRefSec", 120, (value) => saveTextFieldLegacy(c, "payerRefSec", value, API.UpdateClaimReferenceNumber, (next) => ({ objClaim: { ClaimID: c.id, PracticeID: userPracticeID, PatientID: c.patientId, DOS: formatDateForApi(c.dos), UpdatedBy: userData?.userID, PriorSec: 2, PayerReferenceNumber: next } }), `Secondary payer reference updated for ${c.id}`), "-")}</td>;
      case "writeOff": return (
        <td key="writeOff" className="num" onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
            <input
              type="checkbox"
              title="Include this row in batch Close action"
              style={{ accentColor: "#e8812a", cursor: "pointer" }}
              checked={closeRows.includes(c.rowKey)}
              onChange={e => {
                e.stopPropagation();
                setCloseRows(prev => prev.includes(c.rowKey) ? prev.filter(k => k !== c.rowKey) : [...prev, c.rowKey]);
              }}
              onClick={e => e.stopPropagation()}
            />
            {renderDraftInput(c, "writeOff", 88, (value) => saveMoneyFieldLegacy(c, "writeOff", value, API.UpdateWriteoff, {}, { saveAdjustmentAfter: true }), "0.00")}
          </div>
        </td>
      );
      case "adminWriteOff": return (
        <td key="adminWriteOff" className="num" onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
            <input
              type="checkbox"
              title="Include this row in batch Admin Write-off action"
              style={{ accentColor: "#e8812a", cursor: "pointer" }}
              checked={adminWriteOffRows.includes(c.rowKey)}
              onChange={e => {
                e.stopPropagation();
                setAdminWriteOffRows(prev => prev.includes(c.rowKey) ? prev.filter(k => k !== c.rowKey) : [...prev, c.rowKey]);
              }}
              onClick={e => e.stopPropagation()}
            />
            {renderDraftInput(c, "adminWriteOff", 88, (value) => setDraftValue(c.rowKey, "adminWriteOff", value), "0.00")}
          </div>
        </td>
      );
      case "clearingHouse": return <td key="clearingHouse" style={{ fontSize: 12, color: "#475569" }}>{c.clearingHouse || "-"}</td>;
      case "secSubmitDate": return <td key="secSubmitDate" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{c.secSubmitDate ? fmtDate(c.secSubmitDate) : "-"}</td>;
      case "provider": return (
        // V1 parity (ClaimStatus.js 7121): provider name + inline PDF link
        // for the supervising-provider document on this DOS+CPT.
        <td key="provider" style={{ fontSize: 12 }} onClick={e => e.stopPropagation()}>
          {c.provider}
          {c.provider && (
            <FontAwesomeIcon
              icon={faFilePdf}
              title="Open provider PDF"
              style={{ marginLeft: 6, color: "#dc2626", cursor: "pointer", fontSize: 11 }}
              onClick={() => openCptDocumentLegacy(c)}
            />
          )}
        </td>
      );
      case "submittedBy": return <td key="submittedBy"><div style={{ fontSize: 12 }}>{c.submittedBy}</div><div className="cs-pat-meta">{fmtDate(c.submittedOn)}</div></td>;
      case "updatedBy": return <td key="updatedBy"><div style={{ fontSize: 12 }}>{c.updatedBy}</div><div className="cs-pat-meta">{fmtDate(c.updatedOn)}</div></td>;
      case "verifiedBy": {
        // V1 parity (ClaimStatus.js 7161): show 2-letter initials with a
        // tooltip describing who verified the claim + when + with what
        // comment. Click → open the verify modal.
        const verifiedByName = c.verifiedBy || (c.isVerified ? "Verified" : "");
        const tooltip = verifiedByName || c.verifiedOn || c.verificationComment
          ? `Verified by ${verifiedByName} on ${c.verifiedOn ? fmtDate(c.verifiedOn) : ""}${c.verificationComment ? `\nComment: ${c.verificationComment}` : ""}`
          : "Click to verify this claim";
        return (
          <td key="verifiedBy" style={{ fontSize: 12 }} title={tooltip} onClick={e => e.stopPropagation()}>
            {c.isVerified || verifiedByName ? (
              <span style={{ display: "inline-block", padding: "2px 6px", background: "#dcfce7", color: "#166534", borderRadius: 4, fontWeight: 700, fontSize: 11 }}>
                {getVerifiedBy(verifiedByName)}
              </span>
            ) : (
              <button
                className="cs-check-btn"
                style={{ padding: "2px 8px", fontSize: 11 }}
                onClick={() => openVerifyModalLegacy(c)}
              >Verify</button>
            )}
          </td>
        );
      }
      case "denialCode": return <td key="denialCode"><span style={{ fontFamily: "monospace", fontSize: 11, color: c.denialCode ? "#dc2626" : "#94a3b8" }}>{c.denialCode || "-"}</span></td>;
      case "remarks": return <td key="remarks" style={{ maxWidth: 160, fontSize: 11, color: "#475569" }}>{c.remarks || "-"}</td>;
      case "errorDetails": return <td key="errorDetails" style={{ maxWidth: 160, fontSize: 11, color: "#475569" }}>{c.denialReason || "-"}</td>;
      case "attachments": return <td key="attachments">{c.attachments > 0 ? <span style={{ fontSize: 12, color: "#1a9b8c", cursor: "pointer" }} onClick={e => { e.stopPropagation(); fetchClaimAttachmentsLegacy(c); }}><FontAwesomeIcon icon={faPaperclip} style={{ marginRight: 4 }} />{c.attachments}</span> : <span className="cs-zero-amt">-</span>}</td>;
      case "notes": return <td key="notes" style={{ maxWidth: 180, fontSize: 11, color: "#475569" }}>{c.notes || "-"}</td>;
      case "resubmit": return (
        <td key="resubmit" style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
          <input type="checkbox"
            style={{ accentColor: "#e8812a", width: 15, height: 15, cursor: isResubmitCheckboxEnabled(c) ? "pointer" : "not-allowed" }}
            checked={isResubmitCheckboxEnabled(c) && selectedRows.includes(c.rowKey)}
            disabled={!isResubmitCheckboxEnabled(c)}
            onChange={() => toggleRow(c.rowKey)} />
        </td>
      );
      default: return <td key={col}>-</td>;
    }
  }

  const PILLS = [
    { k: "all", label: "All", count: TC.allClaims, color: "#1e293b", bg: "#f1f5f9" },
    null,
    { k: "submitted", label: "Submitted", count: TC.totalSubmitClaim, color: "#1d4ed8", bg: "#dbeafe" },
    { k: "nonsub", label: "Non-Sub", count: TC.totalNonSubmitted, color: "#64748b", bg: "#f1f5f9" },
    { k: "sent", label: "Sent", count: TC.totalSent, color: "#0369a1", bg: "#e0f2fe" },
    { k: "forwarded", label: "Fwd", count: TC.totalForwarded, color: "#6d28d9", bg: "#ede9fe" },
    null,
    { k: "received", label: "Received", count: TC.totalReceivedClaim, color: "#0e7490", bg: "#cffafe" },
    { k: "posted", label: "Posted", count: TC.totalPosted, color: "#0e6b60", bg: "#e6f6f4" },
    null,
    { k: "paid", label: "Paid", count: TC.totalPaid, color: "#15803d", bg: "#dcfce7" },
    null,
    { k: "rejected", label: "Rejected", count: TC.totalRejected, color: "#c2410c", bg: "#ffedd5" },
    { k: "denied", label: "Denied", count: TC.totalDenied, color: "#dc2626", bg: "#fee2e2" },
  ];

  /* ─── JSX ─── */
  return (
    <div className="hv-container hv-page hv-page--dense hv-page--fill" ref={pageTopRef}>

      {/* BREADCRUMB STRIP */}
      <div className="hv-pagehead">
        <span className="hv-pagehead__sub">Home / Billing / <strong>Claim Status</strong></span>
        <span className="hv-pagehead__actions">Logged in as <strong>{userFullName}</strong></span>
      </div>

      <div className="hv-stack">

        {/* PAGE HEADER */}
        <div className="hv-pagehead">
          <div>
            <h1 className="hv-pagehead__title">Claim Status</h1>
            {hasLoaded && (
              <p className="hv-pagehead__sub">
                {TC.allClaims} total claims &nbsp;·&nbsp;
                ${totals.totalBilled.toLocaleString("en-US", { minimumFractionDigits: 2 })} billed &nbsp;·&nbsp;
                <span style={{ color: "#ffffff", fontWeight: 600 }}>${totals.totalReceived.toLocaleString("en-US", { minimumFractionDigits: 2 })} collected</span>
                &nbsp;·&nbsp;
                <span style={{ color: totals.totalBalance > 0 ? "#dc2626" : "#ffffff", fontWeight: 600 }}>${totals.totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2 })} outstanding</span>
              </p>
            )}
          </div>
          <div className="hv-pagehead__actions">
            {/* Print → opens PrintColumnModal so user picks columns (V1 parity). */}
            <button className="hv-btn hv-btn--sm" onClick={() => setIsOpenPrintFilter(true)}><FontAwesomeIcon icon={faPrint} /> Print</button>
            <button className="hv-btn hv-btn--sm" onClick={handleExportExcelWithColumns}><FontAwesomeIcon icon={faFileExcel} style={{ color: "#16a34a" }} /> Excel</button>
            <button className="hv-btn hv-btn--sm" onClick={() => exportPdfFromTable("ClaimStatus")}><FontAwesomeIcon icon={faFilePdf} style={{ color: "#dc2626" }} /> PDF</button>
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="hv-toolbar">
          <div className="hv-toolbar__group">
            <label>Date type</label>
            <select className="hv-select" value={dateType} onChange={e => setDateType(e.target.value)}>
              <option value="DOS">DOS</option>
              <option value="ToApprove">To Approve</option>
              <option value="SubmitDate">Submit Date</option>
              <option value="UpdateDate">Update Date</option>
            </select>
          </div>
          <div className="hv-toolbar__group" style={{ minWidth: 128 }}>
            <label>From</label>
            <DatePicker
              className="hv-date"
              selected={startDate}
              onChange={d => setStartDate(d)}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              disabled={allDates}
              placeholderText="Start date"
              popperProps={{ strategy: "fixed" }}
              calendarContainer={({ children }) => (
                <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6, }}>
                  {children}
                  <div style={{ padding: 8, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
                    <button type="button" onClick={() => setStartDate(new Date())} style={{ padding: "4px 16px", border: "1px solid #1a9b8c", borderRadius: 4, background: "#1a9b8c", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", height: "32px" }}>Today</button>
                  </div>
                </div>
              )}
            />
          </div>
          <div className="hv-toolbar__group" style={{ minWidth: 128 }}>
            <label>To</label>
            <DatePicker
              className="hv-date"
              selected={endDate}
              onChange={d => setEndDate(d)}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              disabled={allDates}
              placeholderText="End date"
              popperProps={{ strategy: "fixed" }}
              calendarContainer={({ children }) => (
                <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 6 }}>
                  {children}
                  <div style={{ padding: 8, borderTop: "1px solid #e2e8f0", textAlign: "center" }}>
                    <button type="button" onClick={() => setEndDate(new Date())} style={{ padding: "4px 16px", border: "1px solid #1a9b8c", borderRadius: 4, background: "#1a9b8c", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer", height: "32px" }}>Today</button>
                  </div>
                </div>
              )}
            />
          </div>
          <label className="hv-check"><input type="checkbox" checked={allDates} onChange={e => setAllDates(e.target.checked)} /> All dates</label>
          <div className="cs-ff-sep" />
          <div className="hv-toolbar__group" ref={locRef} style={{ minWidth: 130 }}>
            <label>Location</label>
            <button ref={locBtnRef} className="cs-loc-btn" onClick={() => {
              if (showLocDropdown) { setShowLocDropdown(false); return; }
              const r = locBtnRef.current.getBoundingClientRect();
              setLocDropdownPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 170) });
              setShowLocDropdown(true);
            }}>
              {locationFilter === "-1"
                ? <span className="cs-loc-btn-chip" style={{ background: "#1e293b", color: "#fff" }}>All</span>
                : (() => {
                  const idx = locations.findIndex(l => l.LocationId === locationFilter);
                  const col = LOC_COLORS[(idx < 0 ? 0 : idx) % LOC_COLORS.length];
                  const lbl = locations.find(l => l.LocationId === locationFilter)?.LocationNickname ?? "Location";
                  return <span className="cs-loc-btn-chip" style={{ background: col.bg, color: col.text }}>{lbl}</span>;
                })()
              }
              <span className="cs-loc-btn-arrow">▾</span>
            </button>
          </div>
          <div className="hv-toolbar__group">
            <label>Search by</label>
            <select
              className="hv-select"
              value={searchBy}
              onChange={e => {
                const nextValue = e.target.value;
                setSearchBy(nextValue);
                setInsOrPatFilter(getSearchFilterType(nextValue));
                setSearchText("");
                setSelectedSearchID("");
                setSelectedBillingNPI("");
                setSuggestions([]);
              }}>
              {searchFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="hv-toolbar__group hv-toolbar__group--grow" ref={searchRef} style={{ position: "relative" }}>
            <label>{searchBy === "7" ? "Billing NPI" : "Search"}</label>
            {searchBy === "7" ? (
              <select className="hv-select" value={selectedBillingNPI} onChange={e => setSelectedBillingNPI(e.target.value)}>
                <option value="">Select Billing NPI</option>
                {practiceBillingNPIs.map((item, index) => (
                  <option
                    key={`${item?.BillingNPI ?? item?.NPI ?? index}`}
                    value={String(item?.BillingNPI ?? item?.NPI ?? item?.ValueField ?? "")}
                  >
                    {String(item?.BillingNPI ?? item?.NPI ?? item?.TextField ?? item?.Label ?? "Billing NPI")}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input
                  className="hv-input"
                  type="text"
                  ref={searchInputRef}
                  value={searchText}
                  onChange={e => {
                    setSearchText(e.target.value);
                    setSelectedSearchID("");
                    // Pre-position the floating dropdown so it appears
                    // exactly under the input even before the API call
                    // finishes — avoids the visible "jump" on first show.
                    updateSuggestPos();
                  }}
                  onFocus={() => {
                    updateSuggestPos();
                    // V1 behaviour: re-fetch suggestions on focus when
                    // the field already has text so users don't have to
                    // retype to see the list again.
                    if (searchText.trim()) {
                      fetchSuggestions(searchBy, searchText);
                    } else if (suggestions.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === "Enter") { setShowSuggestions(false); fetchClaimData(); return; }
                    // V1 parity (ClaimStatus.js 1821 handleInputKeyPress):
                    // Backspace re-triggers suggestion fetch with the
                    // trimmed value, OR clears the dropdown when text is empty.
                    if (e.key === "Backspace") {
                      if (searchText === "" && suggestions.length > 0) {
                        setSuggestions([]);
                        return;
                      }
                      const next = searchText.slice(0, -1);
                      if (next.trim()) {
                        updateSuggestPos();
                        fetchSuggestions(searchBy, next);
                      } else {
                        setSuggestions([]);
                        setShowSuggestions(false);
                      }
                    }
                  }}
                  placeholder="Type to search…"
                />
              </>
            )}
          </div>
          <div className="hv-toolbar__group" ref={cptRef}>
            <label>CPT</label>
            <input
              className="hv-input"
              type="text"
              ref={cptInputRef}
              value={cptInput}
              onChange={e => { setCptInput(e.target.value); updateCptListPos(); }}
              onFocus={() => {
                updateCptListPos();
                // Re-fetch matches on focus so users see the list again
                // even if they came back to the field after typing elsewhere.
                if (cptInput.trim()) fetchCPTList(cptInput);
                else if (cptValueList.length > 0) setShowCptList(true);
              }}
              style={{ width: 90 }}
              placeholder="e.g. 99213"
            />
          </div>
          <div className="hv-toolbar__group">
            <label>All Claims</label>
            <select className="hv-select" value={selectedStatusRadioButton} onChange={e => {
              const v = e.target.value;
              setSelectedStatusRadioButton(v);
              localStorage.setItem("selectedStatusRadioButton", v);
            }}>
              {statusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label || "All Claims"}</option>
              ))}
            </select>
          </div>
          {showFilters && (
            <>
              <label className="hv-check"><input type="checkbox" checked={isClaimClosed} onChange={e => setIsClaimClosed(e.target.checked)} /><span>Closed Claims</span></label>
              {claimservices.map((service, index) => {
                const serviceId = String(service?.ServiceID ?? index);
                // Match original ClaimStatus: empty/null ServiceName falls back to "No Service"
                const serviceLabel =
                  service?.ServiceName ||
                  service?.ServiceDescription ||
                  service?.TextField ||
                  service?.Label ||
                  "No Service";
                return (
                  <label key={serviceId} className="hv-check">
                    <input
                      type="checkbox"
                      checked={checkedServices.includes(serviceId)}
                      onChange={() => {
                        setCheckedServices((prev) => {
                          if (serviceId === "0") {
                            return prev.includes(serviceId)
                              ? []
                              : claimservices.map((item, serviceIndex) => String(item?.ServiceID ?? serviceIndex));
                          }
                          return prev.includes(serviceId)
                            ? prev.filter((value) => value !== serviceId && value !== "0")
                            : [...prev.filter((value) => value !== "0"), serviceId];
                        });
                      }}
                    />
                    <span>{serviceLabel}</span>
                  </label>
                );
              })}
            </>
          )}


          <div className="hv-toolbar__actions ml-5">
            <button className="hv-btn hv-btn--sm" onClick={() => setShowFilters(v => !v)}>
              {showFilters ? "Close Filters" : "More Filters"}
            </button>
            <button className="hv-btn hv-btn--sm hv-btn--primary" onClick={() => { pageTopRef.current?.scrollIntoView({ behavior: "smooth" }); fetchClaimData(); }} disabled={loading}>
              {loading
                ? <FontAwesomeIcon icon={faSpinner} spin />
                : <FontAwesomeIcon icon={faMagnifyingGlass} />
              } Search
            </button>
          </div>
        </div>


        {/* STATUS STRIP */}
        <div className="hv-tabbar">
          {PILLS.map((p, i) => {
            if (!p) return <div key={i} className="cs-strip-sep" />;
            const isActive = activeStatusTab === p.k;
            return (
              <button key={p.k} className={`hv-tab${isActive ? " active" : ""}`}
                style={{ "--pill-color": p.color, "--pill-bg": p.bg }}
                onClick={() => handleStatusTab(p.k)}>
                <span className="cs-pill-label">{p.label}</span>
                <span className="cs-pill-count">{p.count}</span>
              </button>
            );
          })}

        </div>

        {/* DAILY BREAKDOWN */}
        <div className="hv-panel hv-panel--flush">
          <div className="hv-panel__head">
            <div>
              <h3>Daily Breakdown</h3>
              <span className="hv-panel__hint">Per-DOS counts · click any cell to drill down</span>
            </div>
            <div className="hv-panel__actions">
              {dosFilter && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "#dbeafe", color: "#1d4ed8", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer" }} onClick={clearDosDrill} title="Click to clear DOS drill-down">
                  Filtered by DOS: {dosFilter} <FontAwesomeIcon icon={faXmark} />
                </span>
              )}
              {showBreakdown && <input className="hv-input" type="text" placeholder="Search DOS…" value={dosSearch} onChange={e => setDosSearch(e.target.value)} />}
              <button className="hv-btn hv-btn--sm text-black" onClick={() => setShowBreakdown(v => !v)}>
                <FontAwesomeIcon icon={showBreakdown ? faAnglesUp : faAnglesDown} /> {showBreakdown ? "Hide" : "View"}
              </button>
              <button className="hv-icon-btn" title="Print" onClick={printBreakdown}><FontAwesomeIcon icon={faPrint} /></button>
              <button className="hv-icon-btn" title="Excel" onClick={exportBreakdown}><FontAwesomeIcon icon={faFileExcel} style={{ color: "#16a34a" }} /></button>
              <button className="hv-icon-btn" title="PDF" onClick={exportBreakdownPdf}><FontAwesomeIcon icon={faFilePdf} style={{ color: "#dc2626" }} /></button>
            </div>
          </div>
          {showBreakdown && (
            <table className="hv-table hv-table--striped">
              <thead><tr>
                <th>DOS</th>
                <th className="num">All</th><th className="num">Submitted</th><th className="num">Non-sub</th>
                <th className="num">Sent</th><th className="num">Fwd</th><th className="num">Received</th>
                <th className="num">Posted</th><th className="num cs-paid">Paid</th>
                <th className="num cs-rejected">Rejected</th><th className="num cs-denied">Denied</th>
              </tr></thead>
              <tbody>
                {dailyFiltered.length === 0 && (
                  <tr><td colSpan={11} className="cs-empty-cell" style={{ padding: 24 }}>
                    {loading ? "Loading…" : "No data. Run a search first."}
                  </td></tr>
                )}
                {dailyFiltered.map((d, i) => {
                  const dos = d.DOS;
                  const isActiveDos = dosFilter && parseApiDate(dosFilter) === parseApiDate(dos);
                  const cellCls = (status, extra = "") =>
                    `num cs-drill ${extra}${isActiveDos && activeStatusTab === status ? " active" : ""}`;
                  return (
                    <tr key={i} className={isActiveDos ? "cs-drill-row-active" : ""}>
                      <td>{dos} <span style={{ color: "#94a3b8", fontSize: 11 }}>{d.DOSDay}</span></td>
                      <td className={cellCls("all")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "all")}>{d.AllClaims}</td>
                      <td className={cellCls("submitted")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "submitted")}>{d.Submitted}</td>
                      <td className={cellCls("nonsub")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "nonsub")}>{d.NonSubmitted}</td>
                      <td className={cellCls("sent")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "sent")}>{d.SENT}</td>
                      <td className={cellCls("forwarded")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "forwarded")}>{d.FORWARDED}</td>
                      <td className={cellCls("received")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "received")}>{d.PaymentReceived}</td>
                      <td className={cellCls("posted")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "posted")}>{d.PaymentPosted}</td>
                      <td className={cellCls("paid", "cs-paid")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "paid")}>{d.PAID}</td>
                      <td className={cellCls("rejected", "cs-rejected")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "rejected")}>{d.REJECTED}</td>
                      <td className={cellCls("denied", "cs-denied")} style={{ cursor: "pointer" }} onClick={() => drillDailyCell(dos, "denied")}>{d.DENIED}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr>
                <td>Total</td>
                <td className={`num cs-tot${activeCounts === "all" ? " active" : ""}`} onClick={() => handleStatusTab("all")}>{TC.allClaims}</td>
                <td className={`num cs-tot${activeCounts === "submitted" ? " active" : ""}`} onClick={() => handleStatusTab("submitted")}>{TC.totalSubmitClaim}</td>
                <td className={`num cs-tot${activeCounts === "nonsub" ? " active" : ""}`} onClick={() => handleStatusTab("nonsub")}>{TC.totalNonSubmitted}</td>
                <td className={`num cs-tot${activeCounts === "sent" ? " active" : ""}`} onClick={() => handleStatusTab("sent")}>{TC.totalSent}</td>
                <td className={`num cs-tot${activeCounts === "forwarded" ? " active" : ""}`} onClick={() => handleStatusTab("forwarded")}>{TC.totalForwarded}</td>
                <td className={`num cs-tot${activeCounts === "received" ? " active" : ""}`} onClick={() => handleStatusTab("received")}>{TC.totalReceivedClaim}</td>
                <td className={`num cs-tot${activeCounts === "posted" ? " active" : ""}`} onClick={() => handleStatusTab("posted")}>{TC.totalPosted}</td>
                <td className={`num cs-tot cs-paid${activeCounts === "paid" ? " active" : ""}`} onClick={() => handleStatusTab("paid")}>{TC.totalPaid}</td>
                <td className={`num cs-tot cs-rejected${activeCounts === "rejected" ? " active" : ""}`} onClick={() => handleStatusTab("rejected")}>{TC.totalRejected}</td>
                <td className={`num cs-tot cs-denied${activeCounts === "denied" ? " active" : ""}`} onClick={() => handleStatusTab("denied")}>{TC.totalDenied}</td>
              </tr></tfoot>
            </table>
          )}
        </div>

        {/* MAIN CARD */}
        <div className={`hv-panel hv-panel--flush cs-${activeView}-view`}>
          {/* Status tabs */}

          <div className="hv-tablebar">
            <div className="hv-tabbar">
              <button className={`hv-tab${activeView === "claimInfo" ? " active" : ""}`} onClick={() => setActiveView("claimInfo")}><FontAwesomeIcon icon={faCircleUser} /> Claim Info</button>
              <button className={`hv-tab${activeView === "insurance" ? " active" : ""}`} onClick={() => setActiveView("insurance")}><FontAwesomeIcon icon={faShieldHalved} /> Insurance</button>
              <button className={`hv-tab${activeView === "payments" ? " active" : ""}`} onClick={() => setActiveView("payments")}><FontAwesomeIcon icon={faCreditCard} /> Payments</button>
              <button className={`hv-tab${activeView === "notesWorkflow" ? " active" : ""}`} onClick={() => setActiveView("notesWorkflow")}><FontAwesomeIcon icon={faListCheck} /> Notes & Workflow</button>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {activeView === "payments" && paymentsEdited && (
                <button className="hv-btn hv-btn--sm hv-btn--primary" onClick={saveClaimDraftsLegacy}>
                  <FontAwesomeIcon icon={faCheck} /> Save Changes
                </button>
              )}
              <div className="hv-panel__hint">{VIEW_TITLES[activeView]} view</div>
            </div>
          </div>

          {/* View tabs */}

          <div className="hv-tablebar">
            <div className="hv-panel__hint"><strong>{displayed.length}</strong> of <strong>{filtered.length}</strong> claims</div>
            {activeColFilterCount > 0 && (
              <button className="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => setColFilters({})}>
                <FontAwesomeIcon icon={faFilter} /> {activeColFilterCount} col filter{activeColFilterCount > 1 ? "s" : ""} · clear
              </button>
            )}

            <div className="hv-toolbar__group">
              <label className="hv-toolbar__label">View</label>
              <select className="hv-select" value={perPage} onChange={e => { setPerPage(e.target.value); setPageNumber(1); }}>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="all">All</option>
              </select>
              <span className="cs-per-text"> per page</span>

              {perPage && (
                <div className="hv-btn-group">
                  <button className="hv-btn hv-btn--sm" onClick={() => setPageNumber(1)} title="First page" disabled={pageNumber === 1}>«</button>
                  <button className="hv-btn hv-btn--sm" onClick={() => setPageNumber(p => Math.max(1, p - 1))} title="Previous page" disabled={pageNumber === 1}>‹</button>
                  <span className="page-info">Page {pageNumber} of {totalPages}</span>
                  <button className="hv-btn hv-btn--sm" onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))} title="Next page" disabled={pageNumber === totalPages}>›</button>
                  <button className="hv-btn hv-btn--sm" onClick={() => setPageNumber(totalPages)} title="Last page" disabled={pageNumber === totalPages}>»</button>
                </div>
              )}
            </div>
            <div className="hv-btn-group">
              <button className="hv-icon-btn" title="Print (choose columns)" onClick={() => setIsOpenPrintFilter(true)}><FontAwesomeIcon icon={faPrint} /></button>
              <button className="hv-icon-btn" title="Excel" onClick={handleExportExcelWithColumns}><FontAwesomeIcon icon={faFileExcel} style={{ color: "#16a34a" }} /></button>
              <button className="hv-icon-btn" title="PDF" onClick={() => exportPdfFromTable("ClaimStatus")}><FontAwesomeIcon icon={faFilePdf} style={{ color: "#dc2626" }} /></button>
            </div>
          </div>

          {/* Batch bar */}
          {selectedRows.length > 0 && (
            <div className="cs-batch-bar">
              <span><strong>{selectedRows.length}</strong> claim{selectedRows.length > 1 ? "s" : ""} selected</span>
              <div className="cs-spacer" />
              <button onClick={() => requireSelection(() => selectedClaims[0] && checkEligibilityLegacy(selectedClaims[0].rowKey, selectedClaims[0].patient))}><FontAwesomeIcon icon={faShieldHalved} /> Check Eligibility</button>
              <button onClick={() => requireSelection(() => setShowAdminWriteModal(true))}><FontAwesomeIcon icon={faXmark} /> Admin Write-off</button>
              <button onClick={() => requireSelection(() => closeClaimsWithCptLegacy(selectedRows))}><FontAwesomeIcon icon={faLock} /> Close Claim</button>
              <button onClick={() => requireSelection(() => resubmitSelectedClaimsLegacy(selectedRows))}><FontAwesomeIcon icon={faPaperPlane} /> Resubmit</button>
              <button onClick={handleOpenDeleteModal} title="Remove selected rows from this view (local only)"><FontAwesomeIcon icon={faTrash} /> Delete</button>
              <button className="primary" onClick={() => requireSelection(() => exportClaims(selectedClaims, "ClaimStatus-Selected"))}><FontAwesomeIcon icon={faFileExcel} /> Export</button>
              <div className="cs-close-batch" title="Clear selection" onClick={() => setSelectedRows([])}><FontAwesomeIcon icon={faXmark} /></div>
            </div>
          )}

          {/* Data table */}
          <div className="hv-table-scroll hv-table-scroll--fade">
            {(() => {
              const activeCols = VIEW_COLUMNS[activeView] ?? VIEW_COLUMNS.claimInfo;
              return (
                <table className="hv-table hv-worksheet hv-worksheet--pinned hv-table--striped" ref={tableRef}>
                  <thead>
                    <tr>{activeCols.map(col => renderHeader(col))}</tr>
                    <tr className="cs-col-filter-row">{activeCols.map(col => renderFilterCell(col))}</tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={activeCols.length} className="cs-loading-cell">
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} />Loading claims…
                      </td></tr>
                    )}
                    {!loading && !hasLoaded && (
                      <tr><td colSpan={activeCols.length} className="cs-empty-cell">
                        Set filters above and click <strong>Search</strong> to load claims.
                      </td></tr>
                    )}
                    {!loading && hasLoaded && displayed.length === 0 && (
                      <tr><td colSpan={activeCols.length} className="cs-empty-cell">No claims match the current filters.</td></tr>
                    )}
                    {!loading && displayed.map((c, rowIndex) => {
                      // V1 parity: row visual states + repeated claim id colors.
                      // - iClosed=1 → muted gray (table-secondary)
                      // - DataValidationMessage present → red (table-danger) with
                      //   tooltip so billers see incomplete data flags immediately.
                      // - Repeated ClaimId → alternating bg color (claimIdColors).
                      // - updatedby + UpdatedDateTime → pink background to flag
                      //   rows recently touched by another user.
                      const isClosed = c.isClosed;
                      const validationMsg = c.raw?.DataValidationMessage;
                      const isRepeated = claimIdCount[c.id] > 1;
                      const rowBg = isClosed
                        ? "#e2e8f0"
                        : validationMsg
                          ? "#fee2e2"
                          : (c.raw?.updatedby && c.raw?.UpdatedDateTime)
                            ? "#fce7f3"
                            : isRepeated
                              ? claimIdColors[c.id]
                              : undefined;
                      const cls = [
                        selectedRows.includes(c.rowKey) ? "selected" : "",
                        selectedClaim?.rowKey === c.rowKey ? "viewing" : "",
                        isClosed ? "cs-row-closed" : "",
                        validationMsg ? "cs-row-danger" : "",
                      ].filter(Boolean).join(" ");
                      return (
                        <tr key={c.rowKey}
                          className={cls}
                          title={validationMsg || ""}
                          style={{ cursor: "pointer", background: rowBg }}
                          onClick={() => openClaim(c, "overview")}>
                          {activeCols.map(col => renderCell(col, c, rowIndex))}
                        </tr>
                      );
                    })}
                  </tbody>
                  {!loading && displayed.length > 0 && (
                    <tfoot>
                      <tr className="cs-tbl-footer">
                        {activeCols.map(col => renderTotalsCell(col))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              );
            })()}
          </div>
        </div>
      </div>

      {selectedClaim && (
        <DetailDrawer
          claim={selectedClaim} tab={drawerTab}
          onTabChange={setDrawerTab}
          onClose={() => setSelectedClaim(null)}
          onToast={showToast}
          onSaveNotes={(claim, notes) => saveClaimNotesLegacy(claim, notes)}
          onOpenAttachments={fetchClaimAttachmentsLegacy}
          onResubmit={(claim) => resubmitSelectedClaimsLegacy([claim.rowKey])}
          onOpenEob={openCmsPdfLegacy}
          onCheckEligibility={(claim) => checkEligibilityLegacy(claim.rowKey, claim.patient)}
          onPrintClaim={openCmsPdfLegacy}
          onVerify={openVerifyModalLegacy}
          onApplyCopay={openCopayModalLegacy}
          onRegeneratePdf={regenerateCmsPdfLegacy}
          onOpenCptDocument={openCptDocumentLegacy}
          onViewSubmitHistory={fetchClaimSubmitHxLegacy}
          onLoadHistory={loadDrawerHistoryLegacy}
          historyEvents={drawerHistoryClaimId === selectedClaim.id ? drawerHistoryEvents : []}
          historySubmitHx={drawerHistoryClaimId === selectedClaim.id ? drawerHistorySubmitHx : []}
          historyLoading={drawerHistoryLoading}
          regenLoadingKey={regenLoadingKey} />
      )}

      {toast && (
        <div className={`cs-toast${toastType === "warn" ? " warn" : ""}`}>
          {toastType === "warn" && <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 8 }} />}
          {toast}
        </div>
      )}

      {/* Infinite-scroll loaded-count popup (V1 ClaimStatus.js 4404). */}
      {showScrollPopup && (
        <div style={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#0f172a",
          color: "#fff",
          padding: "10px 18px",
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          boxShadow: "0 8px 24px rgba(0,0,0,.25)",
          zIndex: 9000,
        }}>{scrollPopupMsg}</div>
      )}

      {/* PrintColumnModal — V1 parity (ClaimStatus.js line 4291).
          Lets the user toggle which of the 42 columns appear in the
          printed/exported document. Print button on the page top &
          export-group toolbar both open this. */}
      <PrintColumnModal
        isOpen={isOpenPrintFilter}
        onClose={() => setIsOpenPrintFilter(false)}
        columns={printColumnsConfig}
        columnVisibility={printColumnVisibility}
        toggleColumn={togglePrintColumn}
        handleSelectAll={handleSelectAllPrintCols}
        handleDeselectAll={handleDeselectAllPrintCols}
        allSelected={allPrintSelected}
        noneSelected={nonePrintSelected}
        onPrint={handlePrintWithColumns}
      />

      {/* Delete confirm modal — V1 parity (ClaimStatus.js 2462). */}
      {showDeleteConfirm && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => setShowDeleteConfirm(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(420px, calc(100vw - 32px))", background: "#fff", borderRadius: 14, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: ".06em" }}>Delete Claims</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>Confirm removal</div>
              </div>
              <button className="cs-drawer-close" onClick={() => setShowDeleteConfirm(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569" }}>
              Remove <strong>{selectedRows.length}</strong> claim{selectedRows.length !== 1 ? "s" : ""} from this view? This only removes the rows locally and does not call the server (matches V1 behaviour).
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button className="cs-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="cs-btn" style={{ background: "#dc2626", color: "#fff", border: "none" }} onClick={handleDeleteSelected}><FontAwesomeIcon icon={faTrash} /> Delete</button>
            </div>
          </div>
        </>
      )}

      {/* Delete success notification — auto-dismisses after 5s. */}
      {showDeleteSuccess && (
        <div style={{ position: "fixed", top: 24, right: 24, background: "#dcfce7", border: "1px solid #86efac", color: "#166534", padding: "12px 18px", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 9000, display: "flex", alignItems: "center", gap: 10 }}>
          <FontAwesomeIcon icon={faCheck} />
          <span><strong>{deleteRowCount}</strong> claim{deleteRowCount !== 1 ? "s" : ""} removed from view</span>
          <button onClick={() => setShowDeleteSuccess(false)} style={{ background: "none", border: "none", color: "#166534", cursor: "pointer", marginLeft: 6 }}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
      )}

      {/* LOCATION DROPDOWN - fixed portal so filter-bar overflow doesn't clip it */}
      {showAdminWriteModal && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => setShowAdminWriteModal(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(560px, calc(100vw - 32px))", background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>Admin Write-off</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Apply transaction code</div>
              </div>
              <button className="cs-drawer-close" onClick={() => setShowAdminWriteModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 13, color: "#475569" }}>
              {selectedClaims.length} claim{selectedClaims.length !== 1 ? "s" : ""} selected. Enter write-off values in the table, then confirm the transaction code here.
            </p>
            <label style={{ display: "grid", gap: 6, fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Transaction Code
              <select value={selectedTransactionCode} onChange={e => setSelectedTransactionCode(e.target.value)}>
                {transactionCodes.map((item) => (
                  <option key={item.TransactionCodeID} value={item.TransactionCodeID}>
                    {item.TransactionCodeDescription}
                  </option>
                ))}
              </select>
            </label>
            {selectedClaims.length > 0 && (
              <div style={{ marginTop: 12, maxHeight: 180, overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: 12, padding: 12, background: "#f8fafc" }}>
                {selectedClaims.map((claim) => (
                  <div key={claim.rowKey} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "6px 0", borderBottom: "1px solid #e2e8f0" }}>
                    <span style={{ color: "#0f172a", fontWeight: 600 }}>{claim.id} • {claim.patient}</span>
                    <span style={{ color: "#475569" }}>${getDraftValue(claim, "adminWriteOff", claim.adminWriteOff ? claim.adminWriteOff.toFixed(2) : "0.00")}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button className="cs-btn" onClick={() => setShowAdminWriteModal(false)}>Cancel</button>
              <button className="cs-btn cs-btn-primary" onClick={saveAdminWriteOffBatchLegacy}>Submit</button>
            </div>
          </div>
        </>
      )}

      {showAttachmentModal && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => setShowAttachmentModal(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(900px, calc(100vw - 32px))", maxHeight: "80vh", overflow: "auto", background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>Attachments</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Claim {attachmentClaimId}</div>
              </div>
              <button className="cs-drawer-close" onClick={() => setShowAttachmentModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <table className="hv-table hv-table--striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Document Name</th>
                  <th>Preview</th>
                  <th>Uploaded Date</th>
                  <th>Type</th>
                  <th>Method</th>
                  <th>ACN</th>
                </tr>
              </thead>
              <tbody>
                {attachmentData.length === 0 && (
                  <tr><td colSpan={7} className="cs-empty-cell">No attachments found.</td></tr>
                )}
                {attachmentData.map((item, index) => (
                  <tr key={`${item.DocumentName ?? "attachment"}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.DocumentName ?? "-"}</td>
                    <td>{item.FileUrl ? <button className="cs-btn cs-btn-sm" onClick={() => window.open(item.FileUrl, "_blank", "noopener,noreferrer")}><FontAwesomeIcon icon={faFilePdf} /> Open</button> : "-"}</td>
                    <td>{item.UploadedDate ? formatDOSDate(parseApiDate(item.UploadedDate)) : "-"}</td>
                    <td>{item.PWK01 ?? "-"}</td>
                    <td>{item.PWK02 ?? "-"}</td>
                    <td>{item.PWK06 ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showEventsModal && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => setShowEventsModal(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(720px, calc(100vw - 32px))", maxHeight: "80vh", overflow: "auto", background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>Claim Events</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Claim {eventClaimId}</div>
              </div>
              <button className="cs-drawer-close" onClick={() => setShowEventsModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            {/* V1 parity (ClaimStatus.js showFaxModal 7421-7461): events
                render as a horizontal timeline with arrow icons between
                each step, falling back to a vertical stack on narrow
                screens. Each step shows status + date + user. */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
              {eventData.length === 0 && (
                <div className="cs-empty-cell" style={{ padding: 24, width: "100%" }}>No events found.</div>
              )}
              {eventData.map((item, index) => (
                <div key={`${item.DataCurStatus ?? "event"}-${index}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ minWidth: 170, maxWidth: 220, padding: 10, border: "1px solid #c8e5e0", borderRadius: 10, background: "#e6f6f4" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <span style={{ width: 24, height: 24, borderRadius: 999, display: "grid", placeItems: "center", background: "#1a9b8c", color: "#fff", fontWeight: 700, fontSize: 11 }}>{index + 1}</span>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{item.DataCurStatus ?? "Status update"}</div>
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      {item.EventDateTime ? fmtDate(parseApiDate(item.EventDateTime)) : "-"}
                    </div>
                    {item.UserFName && (
                      <div style={{ fontSize: 11, color: "#64748b" }}>{item.UserFName}</div>
                    )}
                  </div>
                  {index < eventData.length - 1 && (
                    <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 18, color: "#1a9b8c" }} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showVerifyModal && verifyClaim && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => !verifySaving && setShowVerifyModal(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(520px, calc(100vw - 32px))", background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>Verify Claim</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Claim {verifyClaim.id}</div>
              </div>
              <button className="cs-drawer-close" onClick={() => !verifySaving && setShowVerifyModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div style={{ display: "grid", gap: 8, marginBottom: 14, fontSize: 13, color: "#475569" }}>
              <div><strong style={{ color: "#0f172a" }}>Patient:</strong> {verifyClaim.patient}</div>
              <div><strong style={{ color: "#0f172a" }}>DOS:</strong> {fmtDate(verifyClaim.dos)} · <strong style={{ color: "#0f172a" }}>CPT:</strong> {verifyClaim.cpt}</div>
              <div><strong style={{ color: "#0f172a" }}>Payer:</strong> {verifyClaim.payer || "-"}</div>
            </div>
            <label style={{ display: "grid", gap: 6, fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Verification Comments
              <textarea
                value={verifyComment}
                onChange={e => setVerifyComment(e.target.value)}
                placeholder="Optional comments…"
                rows={4}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#374151", background: "#f8fafc", outline: "none", resize: "vertical", fontFamily: "inherit" }}
              />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button className="cs-btn" disabled={verifySaving} onClick={() => setShowVerifyModal(false)}>Cancel</button>
              <button className="cs-btn cs-btn-primary" disabled={verifySaving} onClick={saveVerifiedDataLegacy}>
                {verifySaving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faCheck} /> Mark Verified</>}
              </button>
            </div>
          </div>
        </>
      )}

      {showCopayModal && copayClaim && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => !copaySaving && setShowCopayModal(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(480px, calc(100vw - 32px))", background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>Apply Copay</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Claim {copayClaim.id}</div>
              </div>
              <button className="cs-drawer-close" onClick={() => !copaySaving && setShowCopayModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <div style={{ display: "grid", gap: 8, marginBottom: 14, fontSize: 13, color: "#475569" }}>
              <div><strong style={{ color: "#0f172a" }}>Patient:</strong> {copayClaim.patient}</div>
              <div><strong style={{ color: "#0f172a" }}>DOS:</strong> {fmtDate(copayClaim.dos)} · <strong style={{ color: "#0f172a" }}>CPT:</strong> {copayClaim.cpt}</div>
              {copayClaim.copayAppliedClaim && (
                <div style={{ color: "#c2410c" }}><FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: 6 }} />Copay already applied{copayClaim.copayAppliedOn ? ` on ${fmtDate(copayClaim.copayAppliedOn)}` : ""}.</div>
              )}
            </div>
            <label style={{ display: "grid", gap: 6, fontSize: 13, fontWeight: 600, color: "#334155" }}>
              Copay Amount ($)
              <input
                type="number"
                step="0.01"
                min="0"
                value={copayInput}
                onChange={e => setCopayInput(e.target.value)}
                placeholder="0.00"
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, color: "#0f172a", background: "#fff", outline: "none", fontFamily: "inherit" }}
              />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button className="cs-btn" disabled={copaySaving} onClick={() => setShowCopayModal(false)}>Cancel</button>
              <button className="cs-btn cs-btn-primary" disabled={copaySaving} onClick={applyCopayLegacy}>
                {copaySaving ? <><FontAwesomeIcon icon={faSpinner} spin /> Applying…</> : <><FontAwesomeIcon icon={faSackDollar} /> Apply Copay</>}
              </button>
            </div>
          </div>
        </>
      )}

      {showSubmitHxModal && submitHxClaim && (
        <>
          <div className="cs-drawer-backdrop" onClick={() => setShowSubmitHxModal(false)} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(820px, calc(100vw - 32px))", maxHeight: "80vh", overflow: "auto", background: "#fff", borderRadius: 16, boxShadow: "0 24px 60px rgba(15,23,42,.24)", zIndex: 400, padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>Submit History</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>Claim {submitHxClaim.id} · {submitHxClaim.patient}</div>
              </div>
              <button className="cs-drawer-close" onClick={() => setShowSubmitHxModal(false)}><FontAwesomeIcon icon={faXmark} /></button>
            </div>
            <table className="hv-table hv-table--striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Submit Date</th>
                  <th>Clearing House</th>
                  <th>Status</th>
                  <th>Reference #</th>
                  <th>Submitted By</th>
                </tr>
              </thead>
              <tbody>
                {submitHxData.length === 0 && (
                  <tr><td colSpan={6} className="cs-empty-cell">No submission history found.</td></tr>
                )}
                {submitHxData.map((item, index) => (
                  <tr key={`${item.ClaimID ?? "hx"}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.SubmitDate ? fmtDate(parseApiDate(item.SubmitDate)) : "-"}</td>
                    <td>{item.ClearingHouseName ?? item.ClearingHouse ?? "-"}</td>
                    <td>{item.SubmitStatus ?? item.Status ?? "-"}</td>
                    <td>{item.ReferenceNumber ?? item.ClaimRefNumber ?? "-"}</td>
                    <td>{item.SubmittedBy ?? item.SubmitterName ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showLocDropdown && (
        <div className="cs-loc-dropdown"
          style={{ position: "fixed", top: locDropdownPos.top, left: locDropdownPos.left, minWidth: locDropdownPos.width, zIndex: 9999 }}>
          <div className="cs-loc-item" style={{ background: "#1e293b", color: "#fff" }}
            onMouseDown={e => { e.preventDefault(); setLocationFilter("-1"); localStorage.setItem("selectedLocation", "-1"); setShowLocDropdown(false); }}>
            All
          </div>
          {locations.map((l, i) => {
            const col = LOC_COLORS[i % LOC_COLORS.length];
            return (
              <div key={l.LocationId} className="cs-loc-item"
                style={{ background: col.bg, color: col.text }}
                onMouseDown={e => { e.preventDefault(); setLocationFilter(l.LocationId); localStorage.setItem("selectedLocation", l.LocationId); setShowLocDropdown(false); }}>
                {l.LocationNickname}
              </div>
            );
          })}
        </div>
      )}

      {/* Search-by suggestions dropdown — rendered at the page root with
          position:fixed so it floats above the filter-bar's overflow clip.
          Coordinates come from searchInputRef.getBoundingClientRect() in
          updateSuggestPos(). Closes when the user clicks outside (mousedown
          handler attached to document) or hits Escape. */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="cs-floating-suggest"
          style={{
            position: "fixed",
            top: suggestPos.top,
            left: suggestPos.left,
            width: suggestPos.width,
            background: "#fff",
            border: "1px solid #c0d8d4",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,.18)",
            zIndex: 9999,
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {suggestions.map((s, i) => (
            <div
              key={i}
              style={{ padding: "8px 12px", fontSize: 13, color: "#0e6b60", cursor: "pointer", borderBottom: "1px solid #e6f6f4" }}
              onMouseDown={(e) => {
                e.preventDefault();
                setSearchText(s.label);
                setSelectedSearchID(s.val);
                setShowSuggestions(false);
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#e6f6f4")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              {s.label}
            </div>
          ))}
        </div>
      )}

      {/* CPT code dropdown — same floating-portal pattern as suggestions. */}
      {showCptList && cptValueList.length > 0 && (
        <div
          className="cs-floating-cpt"
          style={{
            position: "fixed",
            top: cptListPos.top,
            left: cptListPos.left,
            minWidth: cptListPos.width,
            background: "#fff",
            border: "1px solid #c0d8d4",
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,.18)",
            zIndex: 9999,
            maxHeight: 240,
            overflowY: "auto",
          }}
        >
          {cptValueList.slice(0, 20).map((x, i) => (
            <div
              key={i}
              style={{ padding: "7px 12px", fontSize: 13, color: "#0e6b60", cursor: "pointer", borderBottom: "1px solid #e6f6f4", display: "flex", gap: 8 }}
              onMouseDown={(e) => {
                e.preventDefault();
                setCptInput(x.CPTCode);
                setShowCptList(false);
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#e6f6f4")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#1a9b8c" }}>{x.CPTCode}</span>
              <span style={{ color: "#556b68", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.CPTDescription}</span>
            </div>
          ))}
        </div>
      )}

      {patient360Claim && (
        <Patient360Modal
          claim={patient360Claim}
          allClaims={claims}
          adjustmentCodes={adjustmentCodeData}
          onClose={() => setPatient360Claim(null)}
          onViewDetail={(c) => { openClaim(c, "overview"); setPatient360Claim(null); }}
          onClaimStatus={(c) => { fetchClaimEventsLegacy(c); }}
          onVoid={(c) => { closeClaimsWithCptLegacy([c.rowKey]); }}
          onEditVisit={(c) => { openClaim(c, "notes"); setPatient360Claim(null); }}
          onOverview={(c) => { openClaim(c, "overview"); setPatient360Claim(null); }}
          onPrint={(c) => openCmsPdfLegacy(c)}
          onUnapply={(c) => showToast(`Unapply payment — claim ${c.id}`, "warn")}
          onRefund={(c) => showToast(`Refund — claim ${c.id} (not yet wired)`, "warn")}
          onReversal={(c) => showToast(`Reversal — claim ${c.id} (not yet wired)`, "warn")}
          onReassign={(c) => showToast(`Reassign — claim ${c.id} (not yet wired)`, "warn")}
          onCollections={(c) => showToast(`Collections — claim ${c.id} (not yet wired)`, "warn")}
          onPmtPlan={(c) => showToast(`Payment Plan — claim ${c.id} (not yet wired)`, "warn")}
          onShowToast={showToast}
        />
      )}
    </div>
  );
}

/* ─── PATIENT 360 MODAL ───
   Full patient timeline. Aggregates every claim for this patient currently
   loaded in the table and groups them by Date of Service. Shows:
     - Patient demographics (name, DOB, MRN, patientId)
     - Insurance summary (all unique primary/secondary payers + policy #s)
     - Total billed, collected, balance, write-offs across all visits
     - Per-visit timeline with: services (CPT), provider, charges, payments,
       balance, status, denial codes / remarks, notes, and submission history
   Reads from the existing claims array — no additional API call needed since
   the same patient typically has multiple rows in the current dataset (one
   per CPT line). For data beyond the current date range, the user can widen
   the date filter and reopen Patient 360. */
/* ─── PATIENT 360 MODAL — billing-history grid layout ───
   Recreates the look of a classic billing-history viewer: filter bar at the
   top, big grid in the middle, filtered totals + action buttons at the
   bottom. Columns match the patient-billing reference: CPT/Code,
   Pmt Source (Pri/Sec/Pat), I/B/A flags, Service Date, Provider, Facility,
   Visit/Check #, Charge Amount, Paid/Applied, Patient/Insurance/Total
   Balance. Data comes from the existing claims array — no extra API. */
function Patient360Modal({ claim, allClaims, adjustmentCodes, onClose, onViewDetail, onClaimStatus, onVoid, onEditVisit, onOverview, onPrint, onUnapply, onRefund, onReversal, onReassign, onCollections, onPmtPlan, onShowToast }) {
  // Selected row in the grid — the action buttons operate on this row.
  // Default to the claim that was originally clicked to open this modal.
  const [selectedRowKey, setSelectedRowKey] = useState(claim?.rowKey ?? null);
  // Claim Detail popup — opens when user clicks a Claim Number in the grid.
  const [claimDetail, setClaimDetail] = useState(null);
  const allPatientClaims = useMemo(() => {
    if (!claim) return [];
    return allClaims.filter((c) => {
      if (claim.patientId && c.patientId) return c.patientId === claim.patientId;
      return c.patient === claim.patient;
    });
  }, [claim, allClaims]);

  // Filter state
  const [dateType, setDateType] = useState("SERVICE");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [visitNum, setVisitNum] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [viewMode, setViewMode] = useState("summary"); // summary | detail
  const [groupByVisit, setGroupByVisit] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest"); // newest | oldest
  const [itemFilter, setItemFilter] = useState("all"); // all | open | voided | unbilled
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Build provider list for the Provider dropdown
  const providerList = useMemo(() => {
    const set = new Set();
    allPatientClaims.forEach((c) => { if (c.provider) set.add(c.provider); });
    return Array.from(set);
  }, [allPatientClaims]);

  // Apply filters
  const filtered = useMemo(() => {
    let rows = allPatientClaims.slice();
    if (fromDate) {
      rows = rows.filter((c) => {
        const d = new Date(c.dos);
        return !Number.isNaN(d.getTime()) && d >= new Date(fromDate);
      });
    }
    if (toDate) {
      rows = rows.filter((c) => {
        const d = new Date(c.dos);
        return !Number.isNaN(d.getTime()) && d <= new Date(toDate);
      });
    }
    if (visitNum) {
      rows = rows.filter((c) => String(c.id).includes(visitNum));
    }
    if (providerFilter) {
      rows = rows.filter((c) => c.provider === providerFilter);
    }
    if (itemFilter === "open") rows = rows.filter((c) => c.balance > 0);
    if (itemFilter === "voided") rows = rows.filter((c) => c.isClosed);
    if (itemFilter === "unbilled") rows = rows.filter((c) => c.status === "Pending");
    // Sort
    rows.sort((a, b) => {
      const da = new Date(a.dos).getTime() || 0;
      const db = new Date(b.dos).getTime() || 0;
      return sortOrder === "newest" ? db - da : da - db;
    });
    return rows;
  }, [allPatientClaims, fromDate, toDate, visitNum, providerFilter, itemFilter, sortOrder]);

  // Paged rows
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageRows = filtered.slice(pageStart, pageStart + pageSize);

  // Filtered totals
  const totals = useMemo(() => filtered.reduce((acc, c) => {
    acc.charge += Number(c.billed || 0);
    acc.paidApplied += Number(c.paid || 0) + Number(c.secPaid || 0);
    acc.patBal += Number(c.patRes || 0) + Number(c.copay || 0);
    acc.insBal += Math.max(0, Number(c.balance || 0) - Number(c.patRes || 0) - Number(c.copay || 0));
    acc.totalBal += Number(c.balance || 0);
    return acc;
  }, { charge: 0, paidApplied: 0, patBal: 0, insBal: 0, totalBal: 0 }), [filtered]);

  const money = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const reset = () => {
    setDateType("SERVICE"); setFromDate(""); setToDate("");
    setVisitNum(""); setProviderFilter("");
    setViewMode("summary"); setGroupByVisit(false);
    setSortOrder("newest"); setItemFilter("all"); setPage(1);
  };

  // Determine row coloring (matches reference: highlight row, special tones for refunds)
  const rowColor = (c, idx) => {
    if (c.status === "Denied") return "#fee2e2";
    if (c.balance < 0) return "#dbeafe"; // refund / credit balance
    if (c.balance > 0) return "#fef9c3"; // open balance
    return idx % 2 === 0 ? "#fff" : "#f8fafc";
  };

  // Payment source label
  const pmtSource = (c) => {
    if (c.paid > 0 && c.secPaid > 0) return "Pri+Sec";
    if (c.paid > 0) return "Primary";
    if (c.secPaid > 0) return "Secondary";
    if (c.copay > 0 || c.patRes > 0) return "Patient";
    return "";
  };

  // Flag helpers — I=Insurance billed, B=Billed (submitted), A=Adjudicated
  const flagI = (c) => (c.payer ? "Y" : "N");
  const flagB = (c) => (c.status === "Pending" || !c.submittedOn ? "N" : "Y");
  const flagA = (c) => (c.status === "Paid" || c.status === "Posted" || c.status === "Denied" || c.status === "Rejected" ? "X" : "");

  return (
    <>
      <div className="cs-drawer-backdrop" onClick={onClose} style={{ zIndex: 900 }} />
      <div style={{ position: "fixed", top: "2vh", left: "50%", transform: "translateX(-50%)", width: "min(1400px, 96vw)", maxHeight: "96vh", background: "#fff", borderRadius: 10, boxShadow: "0 24px 64px rgba(0,0,0,.3)", display: "flex", flexDirection: "column", zIndex: 1000, overflow: "hidden", fontFamily: "'Segoe UI', Arial, sans-serif" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#f1f5f9", borderBottom: "1px solid #cbd5e1" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 999, background: "#1a9b8c", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>History — {claim.patient}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>
                {claim.mrn && <>MRN: {claim.mrn} · </>}
                {claim.dob && <>DOB: {fmtDate(claim.dob)} · </>}
                Patient ID: {claim.patientId || "—"}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "#fff", color: "#0f172a", border: "1px solid #cbd5e1", borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Filter bar */}
        <div style={{ display: "grid", gridTemplateColumns: "auto auto 1fr auto auto", gap: 12, padding: "10px 14px", background: "#fafbfc", borderBottom: "1px solid #e2e8f0", alignItems: "center", fontSize: 12 }}>
          {/* Column 1: date type, from, provider */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 74 }}>Date Type:</label>
              <select value={dateType} onChange={(e) => setDateType(e.target.value)} style={{ padding: "3px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4, width: 130 }}>
                <option value="SERVICE">SERVICE</option>
                <option value="POSTING">POSTING</option>
                <option value="UPDATED">UPDATED</option>
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 74 }}>From Date:</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ padding: "3px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4, width: 130 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 74 }}>Provider:</label>
              <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} style={{ padding: "3px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4, width: 130 }}>
                <option value="">All Providers</option>
                {providerList.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Column 2: visit#, to date */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 46 }}>Visit #:</label>
              <input type="text" value={visitNum} onChange={(e) => setVisitNum(e.target.value)} style={{ padding: "3px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4, width: 120 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 46 }}>To:</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ padding: "3px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4, width: 120 }} />
            </div>
            <div style={{ height: 24 }} />
          </div>

          {/* Column 3: view mode + group + sort radios */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
            <div style={{ display: "flex", gap: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                <input type="radio" checked={viewMode === "summary"} onChange={() => setViewMode("summary")} /> Summary
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                <input type="radio" checked={viewMode === "detail"} onChange={() => setViewMode("detail")} /> Detail
              </label>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={groupByVisit} onChange={(e) => setGroupByVisit(e.target.checked)} /> Group by visit
            </label>
          </div>

          {/* Column 4: sort radios */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="radio" checked={sortOrder === "newest"} onChange={() => setSortOrder("newest")} /> Newest first
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
              <input type="radio" checked={sortOrder === "oldest"} onChange={() => setSortOrder("oldest")} /> Oldest first
            </label>
          </div>

          {/* Column 5: item filter radios + reload/reset */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                <input type="radio" checked={itemFilter === "all"} onChange={() => setItemFilter("all")} /> All Items
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                <input type="radio" checked={itemFilter === "open"} onChange={() => setItemFilter("open")} /> Open Items
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                <input type="radio" checked={itemFilter === "voided"} onChange={() => setItemFilter("voided")} /> Voided Items
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                <input type="radio" checked={itemFilter === "unbilled"} onChange={() => setItemFilter("unbilled")} /> Unbilled Items
              </label>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <button onClick={() => setPage(1)} style={{ padding: "4px 14px", fontSize: 12, fontWeight: 600, border: "1px solid #cbd5e1", background: "#fff", borderRadius: 4, cursor: "pointer", minWidth: 70 }}>Reload</button>
              <button onClick={reset} style={{ padding: "4px 14px", fontSize: 12, fontWeight: 600, border: "1px solid #cbd5e1", background: "#fff", borderRadius: 4, cursor: "pointer", minWidth: 70 }}>Reset</button>
            </div>
          </div>
        </div>

        {/* Pagination strip */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "4px 14px", background: "#fafbfc", borderBottom: "1px solid #e2e8f0", fontSize: 11, color: "#475569" }}>
          <button onClick={() => setPage(1)} disabled={page === 1} style={{ padding: "2px 8px", fontSize: 11, border: "1px solid #cbd5e1", background: page === 1 ? "#f1f5f9" : "#fff", borderRadius: 4, cursor: page === 1 ? "default" : "pointer" }}>«</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: "2px 8px", fontSize: 11, border: "1px solid #cbd5e1", background: page === 1 ? "#f1f5f9" : "#fff", borderRadius: 4, cursor: page === 1 ? "default" : "pointer" }}>‹</button>
          <span style={{ padding: "0 8px" }}>{filtered.length === 0 ? "0 of 0" : `${pageStart + 1} - ${Math.min(pageStart + pageSize, filtered.length)} of ${filtered.length}`}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: "2px 8px", fontSize: 11, border: "1px solid #cbd5e1", background: page === totalPages ? "#f1f5f9" : "#fff", borderRadius: 4, cursor: page === totalPages ? "default" : "pointer" }}>›</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={{ padding: "2px 8px", fontSize: 11, border: "1px solid #cbd5e1", background: page === totalPages ? "#f1f5f9" : "#fff", borderRadius: 4, cursor: page === totalPages ? "default" : "pointer" }}>»</button>
        </div>

        {/* Grid */}
        <div style={{ flex: "1 1 auto", overflow: "auto", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
              <tr style={{ background: "#1e40af", color: "#fff" }}>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>Claim Number</th>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>DOS</th>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>Location</th>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>Provider</th>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>Check</th>
                <th style={{ padding: "6px 6px", textAlign: "right", borderRight: "1px solid #1e3a8a" }}>Charge Amount</th>
                <th style={{ padding: "6px 6px", textAlign: "right", borderRight: "1px solid #1e3a8a" }}>Paid</th>
                <th style={{ padding: "6px 6px", textAlign: "right", borderRight: "1px solid #1e3a8a" }}>Due Balance</th>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>Primary Insurance</th>
                <th style={{ padding: "6px 6px", textAlign: "left", borderRight: "1px solid #1e3a8a" }}>Secondary Insurance</th>
                <th style={{ padding: "6px 6px", textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No history records match the current filters.</td></tr>
              )}
              {pageRows.map((c, i) => {
                const isSelected = c.rowKey === selectedRowKey;
                const bg = isSelected ? "#fed7aa" : rowColor(c, i);
                const paidTotal = Number(c.paid || 0) + Number(c.secPaid || 0);
                return (
                  <tr key={c.rowKey || i}
                    onClick={() => setSelectedRowKey(c.rowKey)}
                    style={{ background: bg, borderBottom: "1px solid #e2e8f0", cursor: "pointer", outline: isSelected ? "2px solid #ea580c" : "none", outlineOffset: -2 }}>
                    {/* Claim Number — clickable to open Claim Detail modal */}
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0", fontFamily: "monospace", fontWeight: 600 }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setClaimDetail(c); }}
                        style={{ background: "none", border: "none", padding: 0, color: "#1e40af", fontWeight: 700, cursor: "pointer", textDecoration: "underline", fontFamily: "monospace", fontSize: 11 }}
                        title="Open claim details">
                        {c.id}
                      </button>
                    </td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{fmtDate(c.dos)}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0", textTransform: "uppercase" }}>{c.location || "-"}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0", textTransform: "uppercase" }}>{c.provider || "-"}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0", fontFamily: "monospace" }}>
                      {c.checkNumPri || c.checkNumSec || "-"}
                    </td>
                    <td style={{ padding: "4px 6px", textAlign: "right", borderRight: "1px solid #e2e8f0", fontVariantNumeric: "tabular-nums" }}>{money(c.billed)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", borderRight: "1px solid #e2e8f0", fontVariantNumeric: "tabular-nums", color: paidTotal > 0 ? "#15803d" : "#94a3b8" }}>{money(paidTotal)}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", borderRight: "1px solid #e2e8f0", fontVariantNumeric: "tabular-nums", fontWeight: c.balance > 0 ? 700 : 400, color: c.balance > 0 ? "#dc2626" : "#94a3b8" }}>{money(c.balance)}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0" }}>{c.payer || "-"}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e2e8f0" }}>{c.secPayer || "-"}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: c.balance > 0 ? "#dc2626" : (c.balance < 0 ? "#1e40af" : "#15803d") }}>{money(c.balance)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action button strip — buttons operate on the selected row (orange highlight). */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderTop: "1px solid #e2e8f0", background: "#fafbfc", flexWrap: "wrap" }}>
          {(() => {
            const selected = filtered.find((r) => r.rowKey === selectedRowKey);
            const requireSel = (cb, label) => () => {
              if (!selected) {
                if (onShowToast) onShowToast(`Select a row first to use "${label}"`, "warn");
                else alert(`Select a row first to use "${label}"`);
                return;
              }
              cb(selected);
            };
            const Btn = ({ label, onClick, primary, danger }) => (
              <button onClick={onClick}
                style={{ padding: "5px 12px", fontSize: 12, fontWeight: 500, border: `1px solid ${danger ? "#fecaca" : "#cbd5e1"}`, background: primary ? "#1a9b8c" : danger ? "#fef2f2" : "#fff", color: primary ? "#fff" : danger ? "#991b1b" : "#475569", borderRadius: 4, cursor: "pointer", opacity: 0.98 }}>{label}</button>
            );
            return (
              <>
                <Btn label="Unapply" onClick={requireSel(onUnapply, "Unapply")} />
                <Btn label="Void" danger onClick={requireSel(onVoid, "Void")} />
                <Btn label="View Detail" primary onClick={requireSel(onViewDetail, "View Detail")} />
                <Btn label="Claim Status" primary onClick={requireSel(onClaimStatus, "Claim Status")} />
                <Btn label="Refund" onClick={requireSel(onRefund, "Refund")} />
                <Btn label="Reversal" onClick={requireSel(onReversal, "Reversal")} />
                <Btn label="Edit Visit" onClick={requireSel(onEditVisit, "Edit Visit")} />
                <Btn label="Reassign" onClick={requireSel(onReassign, "Reassign")} />
                <Btn label="Collections" onClick={requireSel(onCollections, "Collections")} />
                <div style={{ flex: "1 1 auto" }} />
                {/* Filtered totals */}
                <div style={{ display: "flex", gap: 18, alignItems: "center", padding: "0 12px", fontSize: 11 }}>
                  <strong style={{ color: "#475569" }}>Filtered Totals</strong>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#64748b", fontSize: 10 }}>Patient Bal</div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{money(totals.patBal)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#64748b", fontSize: 10 }}>Insurance Bal</div>
                    <div style={{ fontWeight: 700, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{money(totals.insBal)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: "#64748b", fontSize: 10 }}>Total Bal</div>
                    <div style={{ fontWeight: 700, color: totals.totalBal > 0 ? "#dc2626" : "#15803d", fontVariantNumeric: "tabular-nums" }}>{money(totals.totalBal)}</div>
                  </div>
                </div>
                <Btn label="Pmt Plan" onClick={requireSel(onPmtPlan, "Pmt Plan")} />
                <Btn label="Overview" onClick={requireSel(onOverview, "Overview")} />
                <Btn label="Print" onClick={requireSel(onPrint, "Print")} />
              </>
            );
          })()}
        </div>
      </div>

      {/* Claim Detail popup — opens when user clicks a Claim Number in the grid */}
      {claimDetail && (
        <ClaimDetailPopup claim={claimDetail} onClose={() => setClaimDetail(null)} onPrint={onPrint} />
      )}
    </>
  );
}

/* ─── CLAIM DETAIL POPUP ───
   Detailed single-claim view modeled on the reference screenshot: header row
   with Provider / Posted By / Batch # / Episode, procedure & charge block,
   referral / facility / asst-prov panel, DOS / Age Date / Entry Date,
   POS / TOS, ICD diagnosis grid, financial-class + bill-insurance + accept-
   assign, payment-source rows, action buttons across the bottom. All data
   comes from the existing claim object in the patient's claim list. */
function ClaimDetailPopup({ claim, onClose, onPrint }) {
  if (!claim) return null;
  const money = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const raw = claim.raw || {};

  // Mini field row helper for compact key-value pairs
  const F = ({ label, children, width }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: width || "auto" }}>
      <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 80 }}>{label}</label>
      <div style={{ fontSize: 12, color: "#0f172a", background: "#fff", border: "1px solid #cbd5e1", borderRadius: 3, padding: "2px 6px", minWidth: 90, flex: "0 0 auto" }}>{children || " "}</div>
    </div>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1100 }} />
      <div style={{ position: "fixed", top: "4vh", left: "50%", transform: "translateX(-50%)", width: "min(900px, 96vw)", maxHeight: "92vh", background: "#f0f4f8", borderRadius: 6, boxShadow: "0 24px 64px rgba(0,0,0,.35)", display: "flex", flexDirection: "column", zIndex: 1200, overflow: "hidden", fontFamily: "'Segoe UI', Arial, sans-serif", border: "1px solid #94a3b8" }}>
        <div style={{ flex: "1 1 auto", overflow: "auto", padding: 14 }}>

          {/* Top row: Provider / Posted By / Batch / Episode + Referral / Auth / Facility */}
          <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12, marginBottom: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: 8, background: "#e2e8f0", borderRadius: 4 }}>
              <F label="Provider:">{claim.provider || "-"}</F>
              <F label="Posted By:">{claim.submittedBy || "-"}</F>
              <F label="Batch #:">{raw.BatchID || raw.batchID || "-"}</F>
              <F label="Episode:">{raw.Episode || raw.episode || "STANDARD"}</F>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: 8, background: "#e2e8f0", borderRadius: 4 }}>
              <F label="Referral:">{raw.Referral || raw.ReferringProvider || "-"}</F>
              <F label="Auth #:">{raw.AuthorizationNumber || raw.AuthNumber || "-"}</F>
              <F label="Facility:">{claim.location || "-"}</F>
              <F label="Reference ID:">{claim.payerRefPri || "-"}</F>
            </div>
          </div>

          {/* Procedure + amount block */}
          <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12, marginBottom: 10 }}>
            <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4 }}>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 6, marginBottom: 6 }}>
                <label style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>Procedure:</label>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, padding: "2px 6px", background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 3, fontFamily: "monospace" }}>{claim.cpt || "-"}</div>
                  <div style={{ fontSize: 12, color: "#0f172a", flex: "1 1 auto", padding: "2px 6px", background: "#fafbfc", border: "1px solid #cbd5e1", borderRadius: 3 }}>{raw.CPTDescription || raw.ProcedureDescription || claim.cpt}</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 6, marginBottom: 6 }}>
                <label style={{ fontSize: 11, color: "#475569", fontWeight: 600 }}>Modifiers:</label>
                <div style={{ display: "flex", gap: 4 }}>
                  {[1, 2, 3, 4].map(i => <div key={i} style={{ flex: "0 0 60px", height: 22, border: "1px solid #cbd5e1", borderRadius: 3, background: "#fff", fontSize: 11, padding: "2px 4px", fontFamily: "monospace" }}>{raw[`Modifier${i}`] || ""}</div>)}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
                <F label="Charge:">{money(claim.billed)}</F>
                <F label="Closed:">{claim.isClosed ? "Y" : "N"}</F>
                <F label="Pmt Plan:">{raw.PmtPlan || "N"}</F>
                <F label="Allowed:">{money(raw.AllowedAmount || 0)}</F>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginTop: 6 }}>
                <F label="Expected:">{money(raw.ExpectedAmount || 0)}</F>
                <F label="Units:">{claim.units || 1}</F>
                <F label="NDC Qty:">{raw.NDCQty || ""}</F>
                <F label="NDC Price:">{money(raw.NDCPrice || 0)}</F>
              </div>
            </div>

            {/* Amount panel — Patient / Insurance / Total */}
            <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4 }}>
              <div style={{ display: "grid", gridTemplateColumns: "60px 80px 80px 80px", gap: 6, fontSize: 11, fontWeight: 700, color: "#475569", marginBottom: 6, paddingBottom: 4, borderBottom: "1px solid #e2e8f0" }}>
                <div></div>
                <div style={{ textAlign: "right" }}>Patient:</div>
                <div style={{ textAlign: "right" }}>Insurance:</div>
                <div style={{ textAlign: "right" }}>Total:</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "60px 80px 80px 80px", gap: 6, fontSize: 11, marginBottom: 4 }}>
                <div style={{ color: "#475569", fontWeight: 600 }}>Portion:</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(claim.patRes + claim.copay)}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(claim.billed - (claim.patRes + claim.copay))}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{money(claim.billed)}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "60px 80px 80px 80px", gap: 6, fontSize: 11, marginBottom: 4 }}>
                <div style={{ color: "#475569", fontWeight: 600 }}>Paid:</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(0)}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(claim.paid + claim.secPaid)}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{money(claim.paid + claim.secPaid)}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "60px 80px 80px 80px", gap: 6, fontSize: 11, marginBottom: 4 }}>
                <div style={{ color: "#475569", fontWeight: 600 }}>W/O:</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(0)}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(claim.writeOff + claim.adminWriteOff)}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{money(claim.writeOff + claim.adminWriteOff)}</div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "60px 80px 80px 80px", gap: 6, fontSize: 11, marginBottom: 4, paddingTop: 4, borderTop: "1px solid #e2e8f0" }}>
                <div style={{ color: "#475569", fontWeight: 700 }}>Balance:</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{money(claim.patRes + claim.copay)}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>{money(claim.balance - (claim.patRes + claim.copay))}</div>
                <div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 700, color: claim.balance > 0 ? "#dc2626" : "#15803d" }}>{money(claim.balance)}</div>
              </div>
            </div>
          </div>

          {/* DOS / Dates / POS / TOS row */}
          <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12, marginBottom: 10 }}>
            <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <F label="DOS:">{fmtDate(claim.dos)}</F>
                <span style={{ fontSize: 11, color: "#475569" }}>To</span>
                <F label="">{fmtDate(claim.dos)}</F>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 6 }}>
                <F label="Age Date:">{fmtDate(raw.AgeDate || claim.submittedOn)}</F>
                <F label="Entry Date:">{fmtDate(raw.EntryDate || claim.dos)}</F>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <F label="POS:">{raw.POS || raw.PlaceOfService || "11 - OFFICE"}</F>
                <F label="TOS:">{raw.TOS || raw.TypeOfService || "MEDICAL CARE"}</F>
              </div>
            </div>
            <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4 }}>
              <F label="Resp Party:">{raw.RespParty || claim.patient}</F>
              <div style={{ height: 6 }} />
              <F label="Ins Order:">{raw.InsOrder || "1"}</F>
            </div>
          </div>

          {/* ICD Diagnosis Codes */}
          <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#475569" }}>Carrier Required: ICD-9 or ICD-10</span>
            </div>
            <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>ICD-10 Diagnosis Codes</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, marginBottom: 8 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ height: 24, border: "1px solid #cbd5e1", borderRadius: 3, background: "#fafbfc", padding: "2px 6px", fontSize: 11, fontFamily: "monospace" }}>
                  {raw[`ICD10_${i}`] || raw[`DxCode${i}`] || (i === 1 ? claim.diagnosisCodes : "") || ""}
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#475569", marginBottom: 4 }}>ICD-9 Diagnosis Codes</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{ height: 24, border: "1px solid #cbd5e1", borderRadius: 3, background: "#fafbfc", padding: "2px 6px", fontSize: 11, fontFamily: "monospace" }}>
                  {raw[`ICD9_${i}`] || ""}
                </div>
              ))}
            </div>
          </div>

          {/* Visit / Line notes + Financial class */}
          <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12, marginBottom: 10 }}>
            <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 80 }}>Visit Note:</label>
                <input type="text" value={raw.VisitNote || ""} readOnly style={{ flex: "1 1 auto", fontSize: 11, border: "1px solid #cbd5e1", borderRadius: 3, padding: "2px 6px" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label style={{ fontSize: 11, color: "#475569", fontWeight: 600, minWidth: 80 }}>Line Note:</label>
                <input type="text" value={claim.notes || raw.LineNote || ""} readOnly style={{ flex: "1 1 auto", fontSize: 11, border: "1px solid #cbd5e1", borderRadius: 3, padding: "2px 6px" }} />
              </div>
            </div>
            <div style={{ padding: 8, background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4 }}>
              <F label="Financial Class:">{raw.FinancialClass || "MC"}</F>
              <div style={{ height: 6 }} />
              <F label="Bill Insurance:">{claim.payer ? "Y" : "N"}</F>
              <div style={{ height: 6 }} />
              <F label="Accept Assign:">{raw.AcceptAssign || "Y"}</F>
            </div>
          </div>

          {/* Payment source table */}
          <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12, marginBottom: 10 }}>
            <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#cbd5e1" }}>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Source</th>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Date</th>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Check #</th>
                    <th style={{ padding: "4px 6px", textAlign: "right", fontWeight: 700 }}>Amount</th>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>ICN</th>
                  </tr>
                </thead>
                <tbody>
                  {claim.paid > 0 && (
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "3px 6px", color: "#15803d", fontWeight: 600 }}>Primary</td>
                      <td style={{ padding: "3px 6px" }}>{fmtDate(claim.checkDatePri)}</td>
                      <td style={{ padding: "3px 6px", fontFamily: "monospace" }}>{claim.checkNumPri || "-"}</td>
                      <td style={{ padding: "3px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(claim.paid)}</td>
                      <td style={{ padding: "3px 6px", fontFamily: "monospace", fontSize: 10 }}>{claim.payerRefPri || ""}</td>
                    </tr>
                  )}
                  {claim.secPaid > 0 && (
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "3px 6px", color: "#15803d", fontWeight: 600 }}>Secondary</td>
                      <td style={{ padding: "3px 6px" }}>{fmtDate(claim.checkDateSec)}</td>
                      <td style={{ padding: "3px 6px", fontFamily: "monospace" }}>{claim.checkNumSec || "-"}</td>
                      <td style={{ padding: "3px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(claim.secPaid)}</td>
                      <td style={{ padding: "3px 6px", fontFamily: "monospace", fontSize: 10 }}>{claim.payerRefSec || ""}</td>
                    </tr>
                  )}
                  {claim.paid === 0 && claim.secPaid === 0 && (
                    <tr><td colSpan={5} style={{ padding: "8px 6px", textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No payments recorded</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div style={{ background: "#fff", border: "1px solid #cbd5e1", borderRadius: 4, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: "#cbd5e1" }}>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Ins Billed</th>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Date</th>
                    <th style={{ padding: "4px 6px", textAlign: "left", fontWeight: 700 }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {claim.payer && (
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "3px 6px" }}>{claim.payer}</td>
                      <td style={{ padding: "3px 6px" }}>{fmtDate(claim.submittedOn)}</td>
                      <td style={{ padding: "3px 6px" }}>Primary</td>
                    </tr>
                  )}
                  {claim.secPayer && (
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <td style={{ padding: "3px 6px" }}>{claim.secPayer}</td>
                      <td style={{ padding: "3px 6px" }}>{fmtDate(claim.secSubmitDate)}</td>
                      <td style={{ padding: "3px 6px" }}>Secondary</td>
                    </tr>
                  )}
                  {!claim.payer && !claim.secPayer && (
                    <tr><td colSpan={3} style={{ padding: "8px 6px", textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No insurance billed</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer action buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: "#e2e8f0", borderTop: "1px solid #cbd5e1", justifyContent: "center" }}>
          {["Attach. (0)", "Demand Claim", "Visit Log", "Charge Log", "Extra Info", "Rebill", "Save"].map(label => (
            <button key={label} onClick={() => alert(`${label} — coming soon`)}
              style={{ padding: "5px 14px", fontSize: 12, fontWeight: 500, border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", borderRadius: 4, cursor: "pointer" }}>
              {label}
            </button>
          ))}
          {onPrint && (
            <button onClick={() => onPrint(claim)}
              style={{ padding: "5px 14px", fontSize: 12, fontWeight: 500, border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", borderRadius: 4, cursor: "pointer" }}>
              Print
            </button>
          )}
          <button onClick={onClose}
            style={{ padding: "5px 14px", fontSize: 12, fontWeight: 700, border: "1px solid #94a3b8", background: "#1a9b8c", color: "#fff", borderRadius: 4, cursor: "pointer" }}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function Patient360Modal_LEGACY_UNUSED({ claim, allClaims, adjustmentCodes, onClose }) {
  // Gather every claim row for this patient (match by patientId, fallback to
  // patient name if patientId is empty).
  const patientClaims = useMemo(() => {
    if (!claim) return [];
    return allClaims.filter((c) => {
      if (claim.patientId && c.patientId) return c.patientId === claim.patientId;
      return c.patient === claim.patient;
    });
  }, [claim, allClaims]);

  // Group by Date of Service so we have a clean visit timeline.
  const visits = useMemo(() => {
    const map = new Map();
    patientClaims.forEach((c) => {
      const key = c.dos || "Unknown";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    // Sort visits by date descending (most recent first).
    return Array.from(map.entries()).sort((a, b) => {
      const da = new Date(a[0]).getTime() || 0;
      const db = new Date(b[0]).getTime() || 0;
      return db - da;
    });
  }, [patientClaims]);

  // Aggregate all clinical notes and any medication / diagnosis info across
  // the patient's claims so we can show a dedicated "Notes & Clinical
  // Details" section. Each entry keeps the source DOS + claim ID so users
  // can trace which visit it came from.
  const clinicalEntries = useMemo(() => {
    const entries = [];
    patientClaims.forEach((c) => {
      const base = { dos: c.dos, claimId: c.id, cpt: c.cpt };
      if (c.notes && String(c.notes).trim()) {
        entries.push({ ...base, type: "Note", text: String(c.notes) });
      }
      if (c.appointmentNotes && String(c.appointmentNotes).trim()) {
        entries.push({ ...base, type: "Session Note", text: String(c.appointmentNotes) });
      }
      if (c.remarks && String(c.remarks).trim()) {
        entries.push({ ...base, type: "Remarks", text: String(c.remarks) });
      }
      if (c.denialReason && String(c.denialReason).trim()) {
        entries.push({ ...base, type: "Denial Reason", text: String(c.denialReason), tone: "danger" });
      }
      if (c.medications && String(c.medications).trim()) {
        entries.push({ ...base, type: "Medications", text: String(c.medications) });
      }
    });
    return entries;
  }, [patientClaims]);

  // Collect every unique diagnosis code seen across all visits.
  const diagnoses = useMemo(() => {
    const seen = new Map();
    patientClaims.forEach((c) => {
      const code = c.diagnosisCodes || (c.raw && (c.raw.DiagnosisCodes || c.raw.ICDCodes || c.raw.ICD)) || "";
      const desc = c.diagnosisDesc || (c.raw && (c.raw.DiagnosisDescription || c.raw.DxDescription)) || "";
      if (code && !seen.has(code)) seen.set(code, desc);
    });
    return Array.from(seen.entries());
  }, [patientClaims]);

  // Aggregate totals across all visits.
  const totals = useMemo(() => {
    return patientClaims.reduce(
      (acc, c) => {
        acc.billed += Number(c.billed || 0);
        acc.paid += Number(c.paid || 0);
        acc.secPaid += Number(c.secPaid || 0);
        acc.copay += Number(c.copay || 0);
        acc.balance += Number(c.balance || 0);
        acc.writeOff += Number(c.writeOff || 0);
        acc.adminWriteOff += Number(c.adminWriteOff || 0);
        acc.deductible += Number(c.deductible || 0);
        acc.patRes += Number(c.patRes || 0);
        return acc;
      },
      { billed: 0, paid: 0, secPaid: 0, copay: 0, balance: 0, writeOff: 0, adminWriteOff: 0, deductible: 0, patRes: 0 }
    );
  }, [patientClaims]);

  // Collect unique insurance pairs (primary + secondary).
  const insuranceList = useMemo(() => {
    const seen = new Set();
    const list = [];
    patientClaims.forEach((c) => {
      if (c.payer) {
        const key = `pri-${c.payer}-${c.policyNumber}`;
        if (!seen.has(key)) {
          seen.add(key);
          list.push({ type: "Primary", payer: c.payer, payerId: c.payerId, policy: c.policyNumber });
        }
      }
      if (c.secPayer) {
        const key = `sec-${c.secPayer}-${c.secPolicyNumber}`;
        if (!seen.has(key)) {
          seen.add(key);
          list.push({ type: "Secondary", payer: c.secPayer, payerId: c.secPayerId, policy: c.secPolicyNumber });
        }
      }
    });
    return list;
  }, [patientClaims]);

  // Status badge colors
  const statusColor = (s) => {
    const m = {
      Paid: { bg: "#dcfce7", fg: "#166534" },
      Posted: { bg: "#dbeafe", fg: "#1e40af" },
      Sent: { bg: "#e0f2fe", fg: "#075985" },
      Submitted: { bg: "#dbeafe", fg: "#1e40af" },
      Forwarded: { bg: "#f3e8ff", fg: "#6b21a8" },
      Received: { bg: "#cffafe", fg: "#155e75" },
      Pending: { bg: "#fef3c7", fg: "#92400e" },
      Denied: { bg: "#fee2e2", fg: "#991b1b" },
      Rejected: { bg: "#ffedd5", fg: "#9a3412" },
    };
    return m[s] || { bg: "#f1f5f9", fg: "#475569" };
  };

  const money = (n) => `$${(Number(n || 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <>
      <div className="cs-drawer-backdrop" onClick={onClose} style={{ zIndex: 900 }} />
      <div style={{ position: "fixed", top: "3vh", left: "50%", transform: "translateX(-50%)", width: "min(1100px, 95vw)", maxHeight: "94vh", background: "#fff", borderRadius: 12, boxShadow: "0 24px 64px rgba(0,0,0,.25)", display: "flex", flexDirection: "column", zIndex: 1000, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #e2e8f0", background: "linear-gradient(135deg, #0e6b60 0%, #1a9b8c 100%)", color: "#fff" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", opacity: .85 }}>Patient 360° View</div>
            <h2 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700 }}>{claim.patient}</h2>
            <div style={{ display: "flex", gap: 18, marginTop: 6, fontSize: 12, opacity: .9 }}>
              {claim.mrn && <span><strong>MRN:</strong> {claim.mrn}</span>}
              {claim.dob && <span><strong>DOB:</strong> {fmtDate(claim.dob)}</span>}
              {claim.patientId && <span><strong>Patient ID:</strong> {claim.patientId}</span>}
              <span><strong>Total Visits:</strong> {visits.length}</span>
              <span><strong>Total Claims:</strong> {patientClaims.length}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", color: "#fff", border: "1px solid rgba(255,255,255,.3)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 14 }}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: "1 1 auto", overflowY: "auto", padding: "16px 24px", background: "#f8fafc" }}>
          {/* Financial summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Total Billed", value: totals.billed, color: "#0f172a" },
              { label: "Primary Paid", value: totals.paid, color: "#15803d" },
              { label: "Secondary Paid", value: totals.secPaid, color: "#15803d" },
              { label: "Copay", value: totals.copay, color: "#0e6b60" },
              { label: "Deductible", value: totals.deductible, color: "#0e6b60" },
              { label: "Patient Resp.", value: totals.patRes, color: "#0e6b60" },
              { label: "Write-Off", value: totals.writeOff, color: "#7c2d12" },
              { label: "Admin W/O", value: totals.adminWriteOff, color: "#7c2d12" },
              { label: "Outstanding", value: totals.balance, color: totals.balance > 0 ? "#dc2626" : "#15803d" },
            ].map((s) => (
              <div key={s.label} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em" }}>{s.label}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: s.color, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{money(s.value)}</div>
              </div>
            ))}
          </div>

          {/* Insurance section */}
          {insuranceList.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>
                <FontAwesomeIcon icon={faShieldHalved} />&nbsp; Insurance Coverage
              </div>
              <div style={{ padding: "10px 14px" }}>
                {insuranceList.map((ins, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: i < insuranceList.length - 1 ? "1px solid #f1f5f9" : "none", fontSize: 13 }}>
                    <span style={{ display: "inline-block", padding: "2px 10px", borderRadius: 999, background: ins.type === "Primary" ? "#dbeafe" : "#fef3c7", color: ins.type === "Primary" ? "#1e40af" : "#92400e", fontSize: 11, fontWeight: 700 }}>{ins.type}</span>
                    <strong style={{ flex: "0 0 200px" }}>{ins.payer}</strong>
                    {ins.payerId && <span style={{ color: "#94a3b8", fontSize: 12 }}>Payer ID: {ins.payerId}</span>}
                    {ins.policy && <span style={{ color: "#94a3b8", fontSize: 12, marginLeft: "auto" }}>Policy #: {ins.policy}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Diagnoses (ICD codes) */}
          {diagnoses.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>
                <FontAwesomeIcon icon={faNotesMedical} />&nbsp; Diagnoses ({diagnoses.length})
              </div>
              <div style={{ padding: "10px 14px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                {diagnoses.map(([code, desc]) => (
                  <div key={code} style={{ display: "inline-flex", flexDirection: "column", padding: "6px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, minWidth: 120 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#1e40af", fontFamily: "monospace" }}>{code}</span>
                    {desc && <span style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>{desc}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes & Clinical Details — dedicated prominent section */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", background: "#fef9c3", fontSize: 12, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: ".06em", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span><FontAwesomeIcon icon={faNotesMedical} />&nbsp; Notes &amp; Clinical Details</span>
              <span style={{ fontSize: 10, color: "#a16207" }}>{clinicalEntries.length} {clinicalEntries.length === 1 ? "entry" : "entries"}</span>
            </div>
            {clinicalEntries.length === 0 && (
              <div style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontStyle: "italic", fontSize: 12 }}>
                No notes, remarks, or clinical details recorded for this patient in the current date range.
              </div>
            )}
            {clinicalEntries.length > 0 && (
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                {clinicalEntries.map((e, i) => {
                  const tone = e.tone === "danger"
                    ? { tag: "#fee2e2", tagText: "#991b1b", bar: "#dc2626" }
                    : e.type === "Medications"
                      ? { tag: "#dcfce7", tagText: "#166534", bar: "#15803d" }
                      : e.type === "Session Note"
                        ? { tag: "#e0e7ff", tagText: "#3730a3", bar: "#4f46e5" }
                        : { tag: "#fef3c7", tagText: "#92400e", bar: "#e8812a" };
                  return (
                    <div key={i} style={{ padding: "10px 14px", borderBottom: i < clinicalEntries.length - 1 ? "1px solid #f1f5f9" : "none", borderLeft: `3px solid ${tone.bar}`, display: "flex", gap: 12 }}>
                      <div style={{ flexShrink: 0, minWidth: 140 }}>
                        <div style={{ display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: tone.tag, color: tone.tagText }}>{e.type}</div>
                        <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{fmtDate(e.dos)}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>Claim {e.claimId}{e.cpt ? ` · ${e.cpt}` : ""}</div>
                      </div>
                      <div style={{ flex: "1 1 auto", fontSize: 13, color: "#0f172a", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{e.text}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Visit timeline */}
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 12, fontWeight: 700, color: "#0e6b60", textTransform: "uppercase", letterSpacing: ".06em" }}>
              <FontAwesomeIcon icon={faClockRotateLeft} />&nbsp; Visit Timeline ({visits.length} visits)
            </div>
            {visits.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>
                No visits found for this patient in the current date range.
              </div>
            )}
            {visits.map(([dos, visitClaims], vi) => {
              const vBilled = visitClaims.reduce((s, c) => s + Number(c.billed || 0), 0);
              const vPaid = visitClaims.reduce((s, c) => s + Number(c.paid || 0) + Number(c.secPaid || 0), 0);
              const vBal = visitClaims.reduce((s, c) => s + Number(c.balance || 0), 0);
              return (
                <div key={dos + vi} style={{ padding: "12px 14px", borderBottom: vi < visits.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  {/* Visit header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: 8, background: "#e6f6f4", color: "#0e6b60", fontWeight: 700, flexShrink: 0 }}>
                      <div style={{ fontSize: 10, opacity: .8 }}>{(dos && new Date(dos).getFullYear()) || ""}</div>
                      <div style={{ fontSize: 14, lineHeight: 1 }}>{(dos && new Date(dos).toLocaleDateString("en-US", { month: "short", day: "numeric" })) || dos}</div>
                    </div>
                    <div style={{ flex: "1 1 auto" }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{fmtDate(dos)}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        {visitClaims.length} service line{visitClaims.length !== 1 ? "s" : ""}
                        {visitClaims[0]?.provider && ` · Provider: ${visitClaims[0].provider}`}
                        {visitClaims[0]?.location && ` · Location: ${visitClaims[0].location}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "#94a3b8", fontSize: 9, textTransform: "uppercase" }}>Billed</div>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>{money(vBilled)}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "#94a3b8", fontSize: 9, textTransform: "uppercase" }}>Paid</div>
                        <div style={{ fontWeight: 700, color: "#15803d" }}>{money(vPaid)}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ color: "#94a3b8", fontSize: 9, textTransform: "uppercase" }}>Balance</div>
                        <div style={{ fontWeight: 700, color: vBal > 0 ? "#dc2626" : "#15803d" }}>{money(vBal)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Service lines table */}
                  <div style={{ background: "#f8fafc", borderRadius: 6, padding: "4px 8px", fontSize: 11 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #e2e8f0", color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em" }}>
                          <th style={{ textAlign: "left", padding: "4px 6px" }}>Claim #</th>
                          <th style={{ textAlign: "left", padding: "4px 6px" }}>CPT</th>
                          <th style={{ textAlign: "left", padding: "4px 6px" }}>Status</th>
                          <th style={{ textAlign: "right", padding: "4px 6px" }}>Charge</th>
                          <th style={{ textAlign: "right", padding: "4px 6px" }}>Paid (Pri)</th>
                          <th style={{ textAlign: "right", padding: "4px 6px" }}>Paid (Sec)</th>
                          <th style={{ textAlign: "right", padding: "4px 6px" }}>Copay</th>
                          <th style={{ textAlign: "right", padding: "4px 6px" }}>Balance</th>
                          <th style={{ textAlign: "left", padding: "4px 6px" }}>Notes / Denial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visitClaims.map((c, ci) => {
                          const sc = statusColor(c.status);
                          return (
                            <tr key={c.rowKey || ci} style={{ borderBottom: ci < visitClaims.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                              <td style={{ padding: "4px 6px", fontFamily: "monospace", fontSize: 11, color: "#0e6b60" }}>{c.id}</td>
                              <td style={{ padding: "4px 6px", fontFamily: "monospace", fontSize: 11 }}>{c.cpt || "-"}</td>
                              <td style={{ padding: "4px 6px" }}>
                                <span style={{ display: "inline-block", padding: "1px 6px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.fg }}>{c.status}</span>
                              </td>
                              <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(c.billed)}</td>
                              <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: c.paid > 0 ? "#15803d" : "#94a3b8" }}>{money(c.paid)}</td>
                              <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: c.secPaid > 0 ? "#15803d" : "#94a3b8" }}>{money(c.secPaid)}</td>
                              <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(c.copay)}</td>
                              <td style={{ padding: "4px 6px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600, color: c.balance > 0 ? "#dc2626" : "#15803d" }}>{money(c.balance)}</td>
                              <td style={{ padding: "4px 6px", fontSize: 10, color: "#475569", maxWidth: 200 }}>
                                {c.denialCode && <div><strong style={{ color: "#dc2626" }}>{c.denialCode}</strong></div>}
                                {c.denialReason && <div style={{ color: "#dc2626" }}>{c.denialReason}</div>}
                                {c.remarks && <div>{c.remarks}</div>}
                                {c.notes && <div style={{ fontStyle: "italic" }}>📝 {c.notes}</div>}
                                {!c.denialCode && !c.denialReason && !c.remarks && !c.notes && <span style={{ color: "#cbd5e1" }}>—</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 24px", borderTop: "1px solid #e2e8f0", background: "#f8fafc", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "#64748b" }}>
          <div>
            Data reflects current date filter. To see older history, widen the date range and reopen Patient 360.
          </div>
          <button onClick={onClose} className="cs-btn cs-btn-sm cs-btn-primary">
            Close
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── DETAIL DRAWER ─── */
function DetailDrawer({ claim: c, tab, onTabChange, onClose, onToast, onSaveNotes, onOpenAttachments, onResubmit, onOpenEob, onCheckEligibility, onPrintClaim, onVerify, onApplyCopay, onRegeneratePdf, onOpenCptDocument, onViewSubmitHistory, onLoadHistory, historyEvents, historySubmitHx, historyLoading, regenLoadingKey }) {
  // Auto-load history data when the History tab becomes active.
  useEffect(() => {
    if (tab === "history" && onLoadHistory && c?.id) {
      onLoadHistory(c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, c?.id]);
  const notesRef = useRef(null);
  const timeline = [
    { label: "Claim Submitted", date: fmtDate(c.submittedOn), done: true, color: "#1d4ed8" },
    { label: "Received by Payer", date: fmtDate(c.submittedOn), done: true, color: "#0e7490" },
    { label: "Under Review", date: !["Submitted", "Sent"].includes(c.status) ? fmtDate(c.updatedOn) : "-", done: !["Submitted", "Sent"].includes(c.status), color: "#c2410c" },
    { label: "Adjudicated", date: ["Paid", "Posted", "Denied", "Rejected"].includes(c.status) ? fmtDate(c.updatedOn) : "-", done: ["Paid", "Posted", "Denied", "Rejected"].includes(c.status), color: (c.status === "Denied" || c.status === "Rejected") ? "#dc2626" : "#15803d" },
  ];
  return (
    <>
      <div className="cs-drawer-backdrop" onClick={onClose} />
      <div className="cs-drawer">
        <div className="cs-drawer-head">
          <div>
            <div className="cs-drawer-title">Claim Details</div>
            <div className="cs-drawer-id">{c.id}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <StatusBadge status={c.status} />
              <span className={`cs-bhsud ${c.conType.toLowerCase()}`}>{c.conType}</span>
            </div>
          </div>
          <button className="cs-drawer-close" onClick={onClose}><FontAwesomeIcon icon={faXmark} /></button>
        </div>
        <div className="cs-drawer-tabs">
          {[["overview", "Overview"], ["financials", "Financials"], ["notes", "Notes & Workflow"], ["history", "History"]].map(([k, l]) => (
            <button key={k} className={`cs-drawer-tab${tab === k ? " active" : ""}`} onClick={() => onTabChange(k)}>{l}</button>
          ))}
        </div>
        <div className="cs-drawer-body">
          {(c.status === "Denied" || c.status === "Rejected") && c.denialReason && (
            <div className="cs-denial-banner">
              <span className="cs-denial-icon">⚠</span>
              <div>
                <div className="cs-denial-title">{c.status === "Denied" ? "Denial" : "Rejection"} Reason</div>
                <div className="cs-denial-body">{c.denialReason}{c.denialCode && <span style={{ marginLeft: 6, fontFamily: "monospace", fontSize: 11 }}>({c.denialCode})</span>}</div>
              </div>
            </div>
          )}
          {tab === "overview" && (<>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Patient</div>
              <div className="cs-kv-row"><span className="cs-kv-label">Name</span><span className="cs-kv-value">{c.patient}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">MRN</span><span className="cs-kv-value mono">{c.mrn || "-"}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">DOB</span><span className="cs-kv-value">{formatDOSDate(c.dob)}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Claim Type</span><span className="cs-kv-value"><span className={`cs-bhsud ${c.conType.toLowerCase()}`}>{c.conType}</span></span></div>
            </div>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Service</div>
              <div className="cs-kv-row"><span className="cs-kv-label">Date of Service</span><span className="cs-kv-value">{formatDOSDate(c.dos)}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Provider</span><span className="cs-kv-value">{c.provider || "-"}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Location</span><span className="cs-kv-value">{c.location || "-"}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">CPT</span><span className="cs-kv-value mono">{c.cpt || "-"}</span></div>
            </div>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Payer</div>
              <div className="cs-kv-row"><span className="cs-kv-label">Primary</span><span className="cs-kv-value">{c.payer || "-"}</span></div>
              {c.secPayer && <div className="cs-kv-row"><span className="cs-kv-label">Secondary</span><span className="cs-kv-value">{c.secPayer}</span></div>}
              <div className="cs-kv-row"><span className="cs-kv-label">Submitted</span><span className="cs-kv-value">{formatDOSDate(c.submittedOn)}</span></div>
              {c.eob && <div className="cs-kv-row"><span className="cs-kv-label">EOB Ref</span><span className="cs-kv-value mono">{c.eob}</span></div>}
              {c.payerRefPri && <div className="cs-kv-row"><span className="cs-kv-label">Payer Ref #</span><span className="cs-kv-value mono">{c.payerRefPri}</span></div>}
            </div>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Timeline</div>
              <div className="cs-timeline">
                {timeline.map((t, i) => (
                  <div key={i} className="cs-tl-item">
                    <div className="cs-tl-track">
                      <div className={`cs-tl-dot${t.done ? "" : " pending"}`} style={t.done ? { background: t.color, color: "#fff" } : {}}>
                        {t.done && <FontAwesomeIcon icon={faCheck} style={{ fontSize: 7 }} />}
                      </div>
                      {i < timeline.length - 1 && <div className="cs-tl-line" style={{ background: t.done ? `${t.color}44` : "#e2e8f0" }} />}
                    </div>
                    <div className="cs-tl-content">
                      <div className={`cs-tl-label${t.done ? "" : " muted"}`}>{t.label}</div>
                      <div className="cs-tl-date">{t.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>)}
          {tab === "financials" && (<>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Financial Breakdown</div>
              <div className="cs-fin-grid">
                <div className="cs-fin-row"><span className="cs-fin-label">Amount Billed</span><span className="cs-fin-value">${c.billed.toFixed(2)}</span></div>
                <div className="cs-fin-row"><span className="cs-fin-label">Contractual Adj</span><span className="cs-fin-value">{c.contractual > 0 ? `−$${c.contractual.toFixed(2)}` : "-"}</span></div>
                <div className="cs-fin-row"><span className="cs-fin-label">Deductible</span><span className="cs-fin-value">{c.deductible > 0 ? `$${c.deductible.toFixed(2)}` : "-"}</span></div>
                <div className="cs-fin-row"><span className="cs-fin-label">Insurance Paid (Pri)</span><span className={`cs-fin-value${c.paid > 0 ? " green" : ""}`}>{c.paid > 0 ? `$${c.paid.toFixed(2)}` : "-"}</span></div>
                {c.secPaid > 0 && <div className="cs-fin-row"><span className="cs-fin-label">Insurance Paid (Sec)</span><span className="cs-fin-value green">${c.secPaid.toFixed(2)}</span></div>}
                <div className="cs-fin-row"><span className="cs-fin-label">Patient Copay</span><span className="cs-fin-value">{c.copay > 0 ? `$${c.copay.toFixed(2)}` : "-"}</span></div>
                {c.writeOff > 0 && <div className="cs-fin-row"><span className="cs-fin-label">Write-Off</span><span className="cs-fin-value">${c.writeOff.toFixed(2)}</span></div>}
                <div className="cs-fin-row"><span className="cs-fin-label" style={{ fontWeight: 700, color: "#1e293b" }}>Balance Due</span><span className={`cs-fin-value${c.balance > 0 ? " red" : " green"}`} style={{ fontWeight: 700 }}>{c.balance > 0 ? `$${c.balance.toFixed(2)}` : "Paid in Full"}</span></div>
              </div>
            </div>
            {c.checkNumPri && (
              <div className="cs-drawer-section">
                <div className="cs-drawer-section-title">Payment Info</div>
                <div className="cs-kv-row"><span className="cs-kv-label">Cheque # (Pri)</span><span className="cs-kv-value mono">{c.checkNumPri}</span></div>
                <div className="cs-kv-row"><span className="cs-kv-label">Cheque Date</span><span className="cs-kv-value">{fmtDate(c.checkDatePri)}</span></div>
                {c.checkNumSec && <>
                  <div className="cs-kv-row"><span className="cs-kv-label">Cheque # (Sec)</span><span className="cs-kv-value mono">{c.checkNumSec}</span></div>
                  <div className="cs-kv-row"><span className="cs-kv-label">Cheque Date (Sec)</span><span className="cs-kv-value">{fmtDate(c.checkDateSec)}</span></div>
                </>}
              </div>
            )}
          </>)}
          {tab === "history" && (<>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Submit History</div>
              {historyLoading && <div style={{ padding: "8px 0", fontSize: 12, color: "#64748b" }}><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 6 }} />Loading…</div>}
              {!historyLoading && (!historySubmitHx || historySubmitHx.length === 0) && (
                <div style={{ padding: "8px 0", fontSize: 12, color: "#94a3b8" }}>No submission history yet for this claim.</div>
              )}
              {!historyLoading && historySubmitHx && historySubmitHx.length > 0 && (
                <table className="hv-table hv-table--striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Submit Date</th>
                      <th>Clearing House</th>
                      <th>Status</th>
                      <th>Reference #</th>
                      <th>Submitted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historySubmitHx.map((item, index) => (
                      <tr key={`hx-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.SubmitDate ? fmtDate(parseApiDate(item.SubmitDate)) : "-"}</td>
                        <td>{item.ClearingHouseName ?? item.ClearingHouse ?? "-"}</td>
                        <td>{item.SubmitStatus ?? item.Status ?? "-"}</td>
                        <td>{item.ReferenceNumber ?? item.ClaimRefNumber ?? "-"}</td>
                        <td>{item.SubmittedBy ?? item.SubmitterName ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Claim Events</div>
              {historyLoading && <div style={{ padding: "8px 0", fontSize: 12, color: "#64748b" }}><FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 6 }} />Loading…</div>}
              {!historyLoading && (!historyEvents || historyEvents.length === 0) && (
                <div style={{ padding: "8px 0", fontSize: 12, color: "#94a3b8" }}>No events recorded for this claim.</div>
              )}
              {!historyLoading && historyEvents && historyEvents.length > 0 && (
                <div style={{ display: "grid", gap: 8 }}>
                  {historyEvents.map((item, index) => (
                    <div key={`ev-${index}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc" }}>
                      <div style={{ width: 26, height: 26, borderRadius: 999, display: "grid", placeItems: "center", background: "#1a9b8c", color: "#fff", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                        {index + 1}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{item.DataCurStatus ?? item.Status ?? "Status update"}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>
                          {item.EventDateTime ? fmtDate(parseApiDate(item.EventDateTime)) : "-"}
                          {item.UserFName ? ` • ${item.UserFName}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>)}
          {tab === "notes" && (<>
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Workflow</div>
              <div className="cs-kv-row"><span className="cs-kv-label">Submitted By</span><span className="cs-kv-value">{c.submittedBy || "-"}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Submit Date</span><span className="cs-kv-value">{fmtDate(c.submittedOn)}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Updated By</span><span className="cs-kv-value">{c.updatedBy || "-"}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Updated On</span><span className="cs-kv-value">{fmtDate(c.updatedOn)}</span></div>
              <div className="cs-kv-row"><span className="cs-kv-label">Verified By</span><span className="cs-kv-value">{c.verifiedBy || "-"}</span></div>
              {c.attachments > 0 && <div className="cs-kv-row"><span className="cs-kv-label">Attachments</span><span className="cs-kv-value" style={{ cursor: "pointer", color: "#1a9b8c" }} onClick={() => onOpenAttachments(c)}><FontAwesomeIcon icon={faPaperclip} style={{ marginRight: 4 }} />{c.attachments} file{c.attachments > 1 ? "s" : ""}</span></div>}
            </div>
            {c.remarks && (
              <div className="cs-drawer-section">
                <div className="cs-drawer-section-title">Remarks</div>
                <p style={{ margin: 0, fontSize: 13, color: "#475569", lineHeight: 1.6 }}>{c.remarks}</p>
              </div>
            )}
            <div className="cs-drawer-section">
              <div className="cs-drawer-section-title">Notes</div>
              <textarea ref={notesRef} defaultValue={c.notes} placeholder="Add notes…" rows={4}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#374151", background: "#f8fafc", outline: "none", resize: "vertical", fontFamily: "inherit" }}
                onFocus={e => { e.currentTarget.style.borderColor = "#1a9b8c"; }}
                onBlur={e => { e.currentTarget.style.borderColor = "#e2e8f0"; }} />
              <button style={{ marginTop: 6, padding: "6px 14px", background: "#1a9b8c", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                onClick={() => onSaveNotes(c, notesRef.current?.value ?? "")}>
                Save Notes
              </button>
            </div>
          </>)}
          <div className="cs-drawer-actions">
            {!c.isVerified && onVerify && (
              <button className="cs-drawer-action-btn" style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac" }} onClick={() => onVerify(c)}>
                <FontAwesomeIcon icon={faCheck} /> Verify
              </button>
            )}
            {(c.status === "Denied" || c.status === "Rejected") && (
              <button className="cs-drawer-action-btn" style={{ background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" }} onClick={() => onToast("Appeal initiated for " + c.id)}>
                ↺ Appeal
              </button>
            )}
            {(c.status === "Denied" || c.status === "Rejected" || c.status === "Submitted" || c.status === "Sent") && (
              <button className="cs-drawer-action-btn" style={{ background: "#e6f6f4", color: "#0e6b60", border: "1px solid #c8e5e0" }} onClick={() => onResubmit(c)}>
                <FontAwesomeIcon icon={faPaperPlane} /> Resubmit
              </button>
            )}
            {onApplyCopay && (
              <button className="cs-drawer-action-btn" style={{ background: "#fef9c3", color: "#a16207", border: "1px solid #fde047" }} onClick={() => onApplyCopay(c)}>
                <FontAwesomeIcon icon={faSackDollar} /> Apply Copay
              </button>
            )}
            {c.eob && (
              <button className="cs-drawer-action-btn" style={{ background: "#dbeafe", color: "#1d4ed8", border: "1px solid #93c5fd" }} onClick={() => onOpenEob(c)}>
                ↓ Download EOB
              </button>
            )}
            {onRegeneratePdf && (
              <button className="cs-drawer-action-btn" style={{ background: "#ede9fe", color: "#6d28d9", border: "1px solid #c4b5fd" }} disabled={regenLoadingKey === c.rowKey} onClick={() => onRegeneratePdf(c)}>
                <FontAwesomeIcon icon={regenLoadingKey === c.rowKey ? faSpinner : faRotate} spin={regenLoadingKey === c.rowKey} /> Regenerate CMS1500
              </button>
            )}
            {onOpenCptDocument && (
              <button className="cs-drawer-action-btn" style={{ background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }} onClick={() => onOpenCptDocument(c)}>
                <FontAwesomeIcon icon={faFile} /> CPT Document
              </button>
            )}
            {onViewSubmitHistory && (
              <button className="cs-drawer-action-btn" style={{ background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }} onClick={() => onViewSubmitHistory(c)}>
                <FontAwesomeIcon icon={faList} /> Submit History
              </button>
            )}
            <button className="cs-drawer-action-btn" style={{ background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }} onClick={() => onCheckEligibility(c)}>
              <FontAwesomeIcon icon={faShieldHalved} /> Eligibility
            </button>
            <button className="cs-drawer-action-btn" style={{ background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }} onClick={() => onPrintClaim(c)}>
              <FontAwesomeIcon icon={faPrint} /> Print
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
